// ============================================================================
// Moteur de raisonnement de la feuille de brouillon.
// Aucune IA, aucune liste d'étapes : pour chaque ligne écrite par l'élève,
//   1. VRAIE ?      calcul avec les valeurs du modèle de l'exercice
//   2. JUSTIFIÉE ?  elle découle en un pas de ce qui est déjà établi
//                   (hypothèses + lignes validées) : remplacer, calculer,
//                   développer, même opération des deux côtés, racine,
//                   un des cas, ou un OUTIL (= une SKILLCARD)
//   3. UTILE ?      elle apporte une relation nouvelle ou plus simple
// Les erreurs sont reconnues par les pièges des outils, des règles de calcul
// mal appliquées (distributivité, puissances…) et des « réparations »
// génériques (signe, opération inverse, carré = double).
// Le modèle (grandeurs, valeurs, outils en jeu) est produit une fois, hors
// ligne. Pendant le jeu, tout est déterministe et local.
//
// Porté du prototype `reasoner.js` (artifact « La feuille de brouillon », v19)
// sans changement de comportement : les tests de scénarios en sont la garantie.
// ============================================================================

import {
  addCongFact,
  congEstablished,
  congGoalReached,
  congIsTrue,
  congJudge,
  congPathLines,
  congStatedResidues,
  congWouldReach,
  createCongState,
  type CongState,
} from './congruence';
import {
  commitComplexLine,
  complexEstablished,
  complexGoalReached,
  complexIsTrue,
  complexJudge,
  complexPathLines,
  complexStatedValues,
  complexWouldReach,
  createComplexState,
  type ComplexState,
} from './complex';
import {
  addGeoLine,
  createGeoState,
  geoEstablished,
  geoGoalReached,
  geoIsGoal,
  geoJudge,
  geoPathLines,
  type GeoState,
} from './geometry';
import {
  commitKernelLine,
  createKernelSheetState,
  kernelAnswerContains,
  kernelEquivalentToAnswer,
  kernelGoalReached,
  kernelHasLine,
  kernelJudge,
  kernelNextMove,
  kernelSignTableSkeleton,
  kernelSolvePath,
  kernelStatedValues,
  type KernelSheetState,
  type KernelVerdict,
  type SignTableSkeleton,
} from './kernel-sheet';
import {
  kgeoCommit,
  createKGeoState,
  kgeoEstablished,
  kgeoGoalReached,
  kgeoHolds,
  kgeoIsAnswer,
  kgeoJudge,
  kgeoNextMove,
  kgeoSolvePath,
  kgeoStatedValues,
  type KGeoState,
  type KGeoVerdict,
} from './kernel-geometry';
import {
  createKRecState,
  krecCommit,
  krecEstablished,
  krecGoalReached,
  krecHolds,
  krecIsAnswer,
  krecJudge,
  krecNextMove,
  krecSolvePath,
  type KRecState,
  type KRecVerdict,
} from './kernel-recurrence';
import {
  createKProgState,
  kprogCommit,
  kprogEstablished,
  kprogGoalReached,
  kprogHolds,
  kprogIsAnswer,
  kprogJudge,
  kprogNextMove,
  kprogSolvePath,
  type KProgState,
  type KProgVerdict,
} from './kernel-python';
import {
  createKMatState,
  kmatCommit,
  kmatEstablished,
  kmatGoalReached,
  kmatHolds,
  kmatIsAnswer,
  kmatJudge,
  kmatNextMove,
  kmatSolvePath,
  type KMatState,
  type KMatVerdict,
} from './kernel-matrix';
import {
  createKArithState,
  karithCommit,
  karithEstablished,
  karithGoalReached,
  karithHolds,
  karithIsAnswer,
  karithJudge,
  karithNextMove,
  karithSolvePath,
  type KArithState,
  type KArithVerdict,
} from './kernel-arith';
import {
  createKLimState,
  klimCommit,
  klimEstablished,
  klimGoalReached,
  klimHolds,
  klimIsAnswer,
  klimJudge,
  klimNextMove,
  klimSolvePath,
  type KLimState,
  type KLimVerdict,
} from './kernel-limit';
import {
  createKIntState,
  kintCommit,
  kintEstablished,
  kintGoalReached,
  kintHolds,
  kintIsAnswer,
  kintJudge,
  kintNextMove,
  kintSolvePath,
  type KIntState,
  type KIntVerdict,
} from './kernel-integral';
import {
  createKOdeState,
  kodeAnswerWritten,
  kodeCommit,
  kodeEstablished,
  kodeGoalReached,
  kodeHolds,
  kodeIsAnswer,
  kodeJudge,
  kodeNextMove,
  kodeSolvePath,
  type KOdeState,
  type KOdeVerdict,
} from './kernel-ode';
import {
  createKPartState,
  kpartCommit,
  kpartEstablished,
  kpartGoalReached,
  kpartHolds,
  kpartIsAnswer,
  kpartJudge,
  kpartNextMove,
  kpartSolvePath,
  type KPartState,
  type KPartVerdict,
} from './kernel-partial';
import {
  createKRiemState,
  kriemCommit,
  kriemEstablished,
  kriemGoalReached,
  kriemHolds,
  kriemIsAnswer,
  kriemJudge,
  kriemNextMove,
  kriemSolvePath,
  type KRiemState,
  type KRiemVerdict,
} from './kernel-riemann';
import {
  createKClaimsState,
  kclaimsCommit,
  kclaimsDecisions,
  kclaimsEstablished,
  kclaimsGoalReached,
  kclaimsHolds,
  kclaimsJudge,
  kclaimsNextMove,
  kclaimsSolvePath,
  type KClaimsState,
  type KClaimsVerdict,
} from './kernel-claims';
import {
  createKLcState,
  klcCommit,
  klcDecisions,
  klcEstablished,
  klcGoalReached,
  klcHolds,
  klcJudge,
  klcNextMove,
  klcSolvePath,
  type KLcState,
  type KLcVerdict,
} from './kernel-limit-claims';
import {
  createKVarState,
  kvarCommit,
  kvarDecisions,
  kvarEstablished,
  kvarGoalReached,
  kvarHolds,
  kvarJudge,
  kvarNextMove,
  kvarSolvePath,
  kvarTableSkeleton,
  type KVarState,
  type KVarVerdict,
} from './kernel-variations';
import {
  createKTabState,
  ktabCommit,
  ktabDecisions,
  ktabEstablished,
  ktabGoalReached,
  ktabHolds,
  ktabJudge,
  ktabNextMove,
  ktabSkeleton,
  ktabSolvePath,
  type GridSkeleton,
  type KTabState,
  type KTabVerdict,
} from './kernel-tables';
import {
  createKAnaState,
  kanaCommit,
  kanaEstablished,
  kanaGoalReached,
  kanaHolds,
  kanaIsAnswer,
  kanaJudge,
  kanaNextMove,
  kanaSolvePath,
  type KAnaState,
  type KAnaVerdict,
} from './kernel-analysis';
import {
  createKForState,
  kforCommit,
  kforEstablished,
  kforGoalReached,
  kforHolds,
  kforIsAnswer,
  kforJudge,
  kforNextMove,
  kforSolvePath,
  kforStatedValues,
  type KForState,
  type KForVerdict,
} from './kernel-formula';
import {
  addBoundsLine,
  boundsEstablished,
  boundsGoalReached,
  boundsIsGoal,
  boundsJudge,
  boundsPathLines,
  createBoundsState,
} from './bounds';
import {
  addUnitsLine,
  createUnitsState,
  unitsEstablished,
  unitsGoalReached,
  unitsIsAnswer,
  unitsIsTrue,
  unitsJudge,
  unitsPathLines,
  unitsStatedValues,
  type UnitsState,
} from './units';
import {
  checkSyntax,
  formatNumber,
  formVariants,
  isConst,
  isNum,
  isVar,
  lex,
  normalize,
  normalizeChips,
  type Chip,
} from './lexer';
import type {
  BaissePred,
  CheckSpec,
  FormulePred,
  Pred,
  PythagorePred,
  RecurrencePred,
  ScratchModel,
  SecondDegrePred,
} from './model';
import { kernelSpecialPlugin, type KernelSpecialPlugin } from './model';
import { hasExactValues, materializeModel } from './samples';
import {
  SEP,
  addM,
  bin,
  canon,
  close,
  cmpHolds,
  decimalPlaces,
  roundTo,
  truncTo,
  combine,
  dv,
  eq2,
  eqEquiv,
  eqM,
  ev,
  factorOK,
  hasUnknown,
  hasUnknownLine,
  identEq,
  identical,
  isBin,
  kids,
  lineAtoms,
  lineEquiv,
  lineSyms,
  lineText,
  mapKids,
  matchEq,
  matchLine,
  nary,
  neg,
  num,
  numVal,
  numericIn,
  ops,
  parens,
  parseLine,
  parseText,
  pw,
  replaceSym,
  isExactDecimal,
  isFuncNode,
  subst,
  substLine,
  sym,
  symsOf,
  asFraction,
  textOf,
  topFactors,
  un,
  type Env,
  type Eq,
  type Lenient,
  type Line,
  type MatchCost,
  type Node,
} from './tree';

// ── Types de l'état et des verdicts ─────────────────────────────────────────

export type FactSource = 'given' | 'hyp' | 'line';
export interface Fact {
  line: Line;
  atoms: string[];
  source: FactSource;
  /** égalité de l'énoncé affichée en tête de feuille */
  shown?: boolean;
  /** résultat d'une question précédente, admis */
  admitted?: boolean;
  /** ligne-obligation d'un outil (initialisation d'une récurrence) */
  obligation?: boolean;
}
interface Cache {
  sig: string;
  keys?: string[];
  pending?: Record<string, 1>;
}
export interface ReasonerState {
  model: ScratchModel;
  /** grandeurs dont la valeur est connue à ce stade */
  K: Env;
  facts: Fact[];
  stated: { hyp?: boolean; verif?: boolean; checks?: string[] };
  _cache?: Cache | null;
  /** nombres complexes (moteur v11) : tout se joue dans ce mode */
  cplx?: ComplexState;
  /** question de congruences (moteur v3) : tout se joue dans ce mode */
  cong?: CongState;
  /** question de grandeurs et d'unités (moteur v4) : tout se joue dans ce mode */
  units?: UnitsState;
  /** question de raisonnement géométrique (moteur v4) */
  geo?: GeoState;
  bnd?: ReturnType<typeof createBoundsState>;
  /** feuille jugée par le noyau (moteur v18) : tout se joue dans ce mode */
  kern?: KernelSheetState;
  /** démonstration de géométrie jugée par le noyau (moteur v25) */
  kgeo?: KGeoState;
  /** hérédité d'une récurrence jugée par le noyau (moteur v26) */
  krec?: KRecState;
  /** trace d'un programme Python jugée par le noyau (moteur v28) */
  kprog?: KProgState;
  /** calcul matriciel jugé par le noyau (moteur v30) */
  kmat?: KMatState;
  /** arithmétique : Euclide, Bézout (moteur v35) */
  karith?: KArithState;
  /** limites (moteur v54) */
  klim?: KLimState;
  /** limites affirmées à trancher (lot 6.62, moteur v57) */
  klc?: KLcState;
  /** intégrales (moteur v55) */
  kint?: KIntState;
  /** équations différentielles (lot 6.67, moteur v62) */
  kode?: KOdeState;
  /** dérivées partielles (moteur v64) */
  kpart?: KPartState;
  /** sommes de Riemann (lot 6.64, moteur v59) */
  kriem?: KRiemState;
  /** affirmations à trancher (moteur v36) */
  kclaims?: KClaimsState;
  /** étude de fonction : tableau de variations (lot 6.20, moteur v42 étendu) */
  kvar?: KVarState;
  /** tableau à remplir : statistiques, proportionnalité (lot 6.21, moteur v42 étendu) */
  ktab?: KTabState;
  /** dérivée ou primitive jugée par le noyau (moteur v31) */
  kana?: KAnaState;
  /** feuille à formules jugée par le noyau (moteur v32) */
  kfor?: KForState;
  /**
   * mode spécial passé au noyau (moteur v34) : la lecture et le calcul restent ceux du
   * mode (`cong`, `cplx`, `units`, `bnd`), avec les règles du noyau (D7).
   */
  kspecial?: KernelSpecialPlugin;
}

export interface RuleConclusion {
  line: Line;
  label: string;
  hora: number;
  why: string;
  hint?: string;
  caseHora?: number;
  caseLabel?: string;
  /** réécriture d'un membre (factoriser…) : utile même si la relation n'est pas nouvelle */
  transform?: boolean;
  /** ne sert qu'au solveur : pas encore utilisable par l'élève (Δ pas calculé…) */
  needsProof?: boolean;
  obligation?: boolean;
  skill?: string;
  card?: string;
}
export interface Trap {
  line: Line;
  title: string;
  why: string;
  ident?: boolean;
}
type CandKind =
  | 'fact'
  | 'case'
  | 'transpo'
  | 'bothsides'
  | 'racine'
  | 'subst'
  | 'rule'
  | 'ident'
  | 'trans'
  | 'comb';
interface Definition {
  s: string;
  expr: Node;
  gi: number;
  source: FactSource;
}
interface Candidate {
  line: Line;
  kind: CandKind;
  src?: number;
  desc?: string;
  deep?: boolean;
  rc?: RuleConclusion;
  isCase?: boolean;
  def?: Definition;
  ord?: number;
  from?: Node;
  to?: Node;
  via?: Node;
  /** identité remarquable appliquée d'un coup (reconnue) : son nom */
  identity?: string;
}
interface Justification {
  cand: Candidate;
  m: MatchCost;
}
export interface RuleInfo {
  id: string;
  card: string;
  label: string;
  hora: number;
  why: string;
  obligation?: boolean;
}
export type VerdictKind =
  | 'empty'
  | 'syntax'
  | 'trap'
  | 'building'
  | 'open'
  | 'ready'
  | 'pending'
  | 'step'
  | 'false'
  | 'unjustified'
  | 'useless'
  | 'dup'
  | 'given';
export interface Verdict {
  kind: VerdictKind;
  atoms: string[];
  title?: string;
  message?: string;
  /** index du premier bout fautif (lignes rouges) */
  badChip?: number;
  /** la ligne est complète (syntaxe), mais peut encore continuer */
  complete?: boolean;
  trap?: Trap;
  rule?: RuleInfo;
  line?: Line;
  hora?: number;
  /** la ligne donne la réponse à la question */
  final?: boolean;
  hyp?: boolean;
  verif?: boolean;
  /** maillons d'une chaîne a = b = c, validés un par un */
  links?: Line[];
  /** noyau (moteur v18) : passage que le noyau ne sait pas encore vérifier */
  unknown?: boolean;
  /** noyau (moteur v18) : ce que la ligne apporte à la feuille */
  kern?: KernelVerdict['kern'];
  /** noyau (D7) : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  /** moteur v42 : la case fausse d'un tableau de signes (ligne -1 : la ligne des x) */
  cell?: { row: number; col?: number };
}
export interface Move {
  line: Line;
  kind: string;
  label: string;
  text?: string;
  why?: string;
  rc?: RuleConclusion;
  /** noyau (moteur v20) : la ligne telle que le noyau l'écrit (« 2√3 », pas « 2 × √3 ») */
  display?: string;
  /** moteur v42 : le pas est un tableau de signes à dresser */
  table?: boolean;
}
interface Skill {
  card: string;
  setup?: (st: ReasonerState, p: Pred) => void;
  conclusions: (st: ReasonerState, all?: boolean) => RuleConclusion[];
  traps?: (st: ReasonerState) => Trap[];
  unjustified?: (st: ReasonerState, E: Line) => { title: string; message: string } | null;
  next?: (st: ReasonerState) => Move | null;
}

// ── caches attachés au modèle (sans le modifier) ───────────────────────────
const TRAPS_CACHE = new WeakMap<ScratchModel, Trap[]>();
const ANSWERS_CACHE = new WeakMap<ScratchModel, Line[]>();

/* ================= outils = SKILLCARDS =================
   Chaque outil sait quand il s'applique, ce qu'il permet d'écrire,
   et les erreurs typiques de ceux qui l'utilisent. */
function L(text: string): Line {
  return parseText(text);
}
function preds<P extends Pred>(st: ReasonerState, skill: P['skill']): P[] {
  return (st.model.preds || []).filter((p): p is P => p.skill === skill);
}

export const SKILLS: Record<string, Skill> = {
  pythagore: {
    card: 'Théorème de Pythagore',
    conclusions(st) {
      return preds<PythagorePred>(st, 'pythagore').map((p) => ({
        line: L(p.hyp + '² = ' + p.sides[0] + '² + ' + p.sides[1] + '²'),
        label: 'Pythagore',
        hora: 5,
        why:
          'Le triangle est rectangle en ' +
          p.right +
          ', donc [' +
          p.hyp +
          '] est l’hypoténuse : ' +
          p.hyp +
          '² = ' +
          p.sides[0] +
          '² + ' +
          p.sides[1] +
          '².',
        hint:
          'Le triangle est rectangle en ' +
          p.right +
          ' : quel théorème relie ses trois côtés ? Commence la ligne par ' +
          p.hyp +
          '².',
      }));
    },
    traps(st) {
      const out: Trap[] = [];
      preds<PythagorePred>(st, 'pythagore').forEach((p) => {
        const h = p.hyp;
        const a = p.sides[0];
        const b = p.sides[1];
        const hypo =
          'L’angle droit est en ' +
          p.right +
          ' : l’hypoténuse est le côté opposé, [' +
          h +
          ']. C’est ' +
          h +
          '² qui est seul d’un côté.';
        const plus =
          'Pour obtenir le carré de l’hypoténuse, on ADDITIONNE les carrés des deux autres côtés.';
        out.push({
          line: L(h + ' = ' + a + ' + ' + b),
          title: 'Additionner les longueurs',
          why:
            'Pythagore relie les CARRÉS des longueurs : ' +
            h +
            '² = ' +
            a +
            '² + ' +
            b +
            '². En additionnant les longueurs, on tombe sur une réponse piège.',
        });
        out.push({
          line: L(h + '² = ' + a + ' + ' + b),
          title: 'Il manque les carrés',
          why: 'Ce sont les carrés des côtés qui s’additionnent : ' + a + '² + ' + b + '².',
        });
        out.push({
          line: L(a + '² = ' + h + '² + ' + b + '²'),
          title: 'Mauvaise hypoténuse',
          why: hypo,
        });
        out.push({
          line: L(b + '² = ' + h + '² + ' + a + '²'),
          title: 'Mauvaise hypoténuse',
          why: hypo,
        });
        out.push({
          line: L(h + '² = ' + b + '² − ' + a + '²'),
          title: 'Soustraire au lieu d’additionner',
          why: plus,
        });
        out.push({
          line: L(h + '² = ' + a + '² − ' + b + '²'),
          title: 'Soustraire au lieu d’additionner',
          why: plus,
        });
      });
      return out;
    },
  },

  baisse: {
    card: 'Appliquer un pourcentage',
    conclusions(st) {
      const out: RuleConclusion[] = [];
      preds<BaissePred>(st, 'baisse').forEach((p) => {
        out.push({
          line: L(p.remise + ' = ' + p.taux + ' % × ' + p.base),
          label: 'Pourcentage',
          hora: 4,
          why:
            'Baisser de ' +
            p.taux +
            ' %, c’est retirer ' +
            p.taux +
            ' % du prix : la remise vaut ' +
            p.taux +
            ' % de ' +
            p.base +
            '.',
          hint: 'Commence par la remise : combien font ' + p.taux + ' % de ' + p.base + ' ?',
        });
        out.push({
          line: L(p.nouveau + ' = ' + p.base + ' − ' + p.remise),
          label: 'Retirer la remise',
          hora: 3,
          why: 'Le nouveau prix, c’est le prix de départ moins la remise.',
          hint: 'Le nouveau prix, c’est le prix de départ moins la remise.',
        });
        out.push({
          line: L(p.nouveau + ' = ' + p.base + ' − ' + p.taux + ' % × ' + p.base),
          label: 'Pourcentage',
          hora: 4,
          why: 'On retire du prix ' + p.taux + ' % de ce prix.',
          hint: 'Le nouveau prix, c’est le prix de départ moins ' + p.taux + ' % de ce prix.',
        });
        out.push({
          line: L(p.nouveau + ' = ' + p.base + ' × (1 − ' + p.taux + ' %)'),
          label: 'Coefficient multiplicateur',
          hora: 5,
          why:
            'Baisser de ' +
            p.taux +
            ' %, c’est garder ' +
            (100 - p.taux) +
            ' % du prix : on multiplie par 1 − ' +
            p.taux +
            ' %.',
          hint: 'Baisser de ' + p.taux + ' %, c’est multiplier le prix par un nombre. Lequel ?',
        });
      });
      return out;
    },
    traps(st) {
      const out: Trap[] = [];
      preds<BaissePred>(st, 'baisse').forEach((p) => {
        const why1 =
          p.taux +
          ' % de ' +
          p.base +
          ' ne font pas ' +
          p.taux +
          ' : la remise se calcule sur le prix (' +
          p.taux +
          ' % × ' +
          p.base +
          ').';
        out.push({
          line: L(p.nouveau + ' = ' + p.base + ' − ' + p.taux),
          title: '−' + p.taux + ' % n’est pas −' + p.taux,
          why: why1,
        });
        out.push({
          line: L(p.remise + ' = ' + p.taux),
          title: 'La remise n’est pas ' + p.taux,
          why: why1,
        });
        out.push({
          line: L(p.nouveau + ' = ' + p.taux + ' % × ' + p.base),
          title: 'Ça, c’est la remise',
          why:
            p.taux +
            ' % × ' +
            p.base +
            ' est le montant de la réduction, pas le nouveau prix. Il faut encore le retirer du prix de départ.',
        });
        out.push({
          line: L(p.nouveau + ' = ' + p.remise),
          title: 'Ça, c’est la remise',
          why: 'La remise est ce qu’on enlève. Le nouveau prix, c’est ' + p.base + ' − remise.',
        });
        out.push({
          line: L(p.nouveau + ' = ' + p.base + ' − ' + p.taux + ' %'),
          title:
            'Retirer ' + formatNumber(p.taux / 100) + ', ce n’est pas retirer ' + p.taux + ' %',
          why:
            p.taux +
            ' % s’applique au prix : ' +
            p.taux +
            ' % × ' +
            p.base +
            ', puis on le retire.',
        });
      });
      return out;
    },
  },

  produitNul: {
    card: 'Équation produit nul',
    conclusions(st) {
      const out: RuleConclusion[] = [];
      st.facts.forEach((f) => {
        if (f.line.dis.length !== 1) return;
        const pr = productZero(f.line.dis[0]!, st.K);
        if (!pr) return;
        if (pr.length === 1) {
          out.push({
            line: { dis: [{ sides: [pr[0]!, num(0)] }] },
            label: 'Carré nul',
            hora: 5,
            why: 'Un carré est nul si et seulement si le nombre élevé au carré est nul : u² = 0 donne u = 0.',
            hint: 'Un carré est nul quand le nombre au carré est nul : écris « (ce qui est au carré) = 0 ».',
          });
          return;
        }
        out.push({
          line: { dis: pr.map((fc) => ({ sides: [fc, num(0)] })) },
          label: 'Produit nul',
          hora: 6,
          caseHora: 3,
          caseLabel: 'Produit nul (un cas)',
          why: 'Un produit est nul si et seulement si l’un de ses facteurs est nul.',
          hint: 'Un produit est nul quand l’un de ses facteurs est nul : écris « facteur = 0 ou facteur = 0 ».',
        });
      });
      return out;
    },
    traps(st) {
      const out: Trap[] = [];
      st.facts.forEach((f) => {
        if (f.line.dis.length !== 1) return;
        const pr = productZero(f.line.dis[0]!, st.K);
        if (!pr || pr.length !== 2) return;
        out.push({
          line: eq2(pr[0]!, pr[1]!),
          title: 'Chaque facteur se compare à 0',
          why: 'Le produit vaut 0 : c’est CHAQUE facteur qu’on égale à 0, pas les facteurs entre eux.',
        });
      });
      return out;
    },
  },

  // k·a + k·b = k(a + b) : le facteur commun mis en évidence
  facteurCommun: {
    card: 'Factoriser avec un facteur commun',
    conclusions(st) {
      const out: RuleConclusion[] = [];
      // équation à résoudre (pas une expression à factoriser) : la ligne la plus récente
      // d'abord (x² − 7x = 0 plutôt que x² − 6x − x = 0)
      const facts = st.model.answers ? st.facts.slice() : st.facts.slice().reverse();
      facts.forEach((f) => {
        if (f.line.dis.length !== 1 || f.line.dis[0]!.sides.length !== 2) return;
        const d = f.line.dis[0]!;
        [0, 1].forEach((si) => {
          const s = d.sides[si]!;
          if (s.k !== 'add' || s.c!.length < 2 || !hasUnknown(s, st.K)) return;
          // x² − 6x = x : on ne factorise pas un côté d'une équation qui a des x des deux côtés
          // x² − 6x = x : on ne factorise pas un côté d'une équation qui a des x des deux
          // côtés (mais « A = 4x² − 8x » est une définition : on factorise)
          const oth = d.sides[1 - si]!;
          const definition = oth.k === 's' && !symsOf(s)[oth.n!];
          if (!definition && hasUnknown(oth, st.K)) return;
          const terms = s.c!.map((t) => {
            const sg = signSplit(t);
            // (x + 1)² = (x + 1) × (x + 1) : le facteur commun peut être dans un carré
            const fs = (sg.t.k === 'mul' ? sg.t.c! : [sg.t]).flatMap((g) =>
              g.k === 'sq' && g.a!.k === 'add' && hasUnknown(g.a!, st.K) ? [g.a!, g.a!] : [g],
            );
            return { neg: sg.neg, f: fs };
          });
          terms[0]!.f.forEach((cf) => {
            if (!hasUnknown(cf, st.K) || out.length) return;
            const key = canon(cf);
            if (!terms.every((t) => t.f.some((g) => canon(g) === key))) return;
            const rest = terms.map((t) => {
              let done = false;
              const r = t.f.filter((g) => {
                if (!done && canon(g) === key) {
                  done = true;
                  return false;
                }
                return true;
              });
              const m = r.length ? nary('mul', r) : num(1);
              return t.neg ? neg(m) : m;
            });
            out.push({
              line: { dis: [eqOf(si, nary('mul', [cf, nary('add', rest)]), d.sides[1 - si]!)] },
              label: 'Facteur commun',
              hora: 5,
              transform: true,
              why:
                'On repère le facteur commun ' +
                textOf(cf) +
                ' dans chaque terme et on le met en évidence : k·a − k·b = k(a − b).',
              hint:
                'Un même facteur apparaît dans chaque terme. Lequel ? Écris ' +
                d.sides[si === 0 ? 1 : 0]!.n +
                ' = (ce facteur)(…).',
            });
          });
          if (out.length) return;
          // facteur numérique ou monôme : 3x + 12 = 3 × x + 3 × 4 = 3(x + 4) ; x² + 3x = x(x + 3)
          const mf = monomialFactor(s, st.K);
          if (!mf) return;
          const other = d.sides[1 - si]!;
          const negLead = (monoMulti(s.c![0]!, st.K)?.c ?? 0) < 0;
          // −2x − 6 : pas de ligne « 2 × x … » ambiguë, on met directement −2 (ou 2) en facteur
          if (!negLead)
            out.push({
              line: { dis: [eqOf(si, nary('add', mf.shown), other)] },
              label: 'Faire apparaître le facteur commun',
              hora: 2,
              transform: true,
              why: 'On écrit chaque terme comme un produit par ' + textOf(mf.k) + '.',
              hint:
                'Quel nombre (ou quelle lettre) divise chaque terme ? Écris chaque terme sous la forme ' +
                textOf(mf.k) +
                ' × (…).',
            });
          // premier terme négatif : on peut aussi mettre −k en facteur (−2x − 6 = −2(x + 3))
          if (negLead) {
            const kn = neg(mf.k);
            out.push({
              line: {
                dis: [
                  eqOf(
                    si,
                    nary('mul', [
                      kn,
                      nary(
                        'add',
                        mf.rest.map((r) => neg(r)),
                      ),
                    ]),
                    other,
                  ),
                ],
              },
              label: 'Facteur commun',
              hora: 5,
              transform: true,
              why:
                textOf(kn) +
                ' divise chaque terme : on le met en évidence, et chaque signe change dans la parenthèse.',
              hint:
                'Mets ' +
                textOf(kn) +
                ' en facteur : chaque terme est divisé par ' +
                textOf(kn) +
                '.',
            });
          }
          out.push({
            line: { dis: [eqOf(si, nary('mul', [mf.k, nary('add', mf.rest)]), other)] },
            label: 'Facteur commun',
            hora: 5,
            transform: true,
            why:
              textOf(mf.k) +
              ' divise chaque terme : on le met en évidence, k × a + k × b = k(a + b).',
            hint:
              'Un même facteur divise chaque terme. Lequel ? Écris ' +
              (other.k === 's' ? other.n : '…') +
              ' = ' +
              textOf(mf.k) +
              '(…).',
          });
        });
      });
      return out;
    },
    traps(st) {
      const out: Trap[] = [];
      st.facts.forEach((f) => {
        if (f.source !== 'given' || f.line.dis.length !== 1 || f.line.dis[0]!.sides.length !== 2)
          return;
        const d = f.line.dis[0]!;
        [0, 1].forEach((si) => {
          const s = d.sides[si]!;
          if (s.k !== 'add' || !hasUnknown(s, st.K)) return;
          const mf = monomialFactor(s, st.K);
          if (!mf) return;
          // un terme recopié sans être divisé par le facteur : 3x + 12 = 3(x + 12)
          s.c!.forEach((t, i) => {
            if (canon(t) === canon(mf.rest[i]!)) return;
            const rest = mf.rest.map((r, j) => (j === i ? t : r));
            out.push({
              line: { dis: [eqOf(si, nary('mul', [mf.k, nary('add', rest)]), d.sides[1 - si]!)] },
              title: 'Chaque terme est divisé',
              why:
                'Quand on met ' +
                textOf(mf.k) +
                ' en facteur, il faut le retirer de CHAQUE terme : ' +
                textOf(signSplit(t).t) +
                ' = ' +
                textOf(mf.k) +
                ' × ' +
                textOf(signSplit(mf.rest[i]!).t) +
                ', il reste donc ' +
                textOf(mf.rest[i]!) +
                '.',
            });
          });
        });
      });
      return out;
    },
  },

  // ax² + bx + c = 0 : discriminant, puis formules des racines si Δ > 0
  secondDegre: {
    card: 'Équation du second degré',
    conclusions(st, all) {
      const out: RuleConclusion[] = [];
      preds<SecondDegrePred>(st, 'secondDegre').forEach((p) => {
        out.push({
          line: L(p.delta + ' = ' + p.b + '² − 4 × ' + p.a + ' × ' + p.c),
          label: 'Discriminant',
          hora: 5,
          why: 'Pour ax² + bx + c = 0, on calcule le discriminant : Δ = b² − 4ac.',
          hint: 'Commence par le discriminant : Δ = b² − 4ac.',
        });
        const D = st.K[p.delta];
        const ok = D !== undefined && D > 0;
        // Δ = 0 (moteur v12) : une seule solution, x₀ = −b ÷ (2a)
        const zero = D !== undefined && Math.abs(D) < 1e-12;
        if (p.double && all && D === undefined)
          // pour relier x₀ à Δ (le corrigé calcule Δ d'abord) : vraie quand Δ = 0
          out.push({
            line: L(p.double + ' = (−' + p.b + ' + √' + p.delta + ') ÷ (2 × ' + p.a + ')'),
            label: 'Solution double',
            hora: 5,
            needsProof: true,
            why: 'Δ = 0, donc une seule solution : ' + p.double + ' = −b ÷ (2a).',
            hint: 'Calcule d’abord Δ.',
          });
        if (p.double && (zero || (all && D !== undefined))) {
          out.push({
            line: L(p.double + ' = −' + p.b + ' ÷ (2 × ' + p.a + ')'),
            label: 'Solution double',
            hora: 5,
            needsProof: !zero,
            why: 'Δ = 0, donc une seule solution : ' + p.double + ' = −b ÷ (2a).',
            hint: 'Δ est nul : il y a une seule solution. Écris ' + p.double + ' = −b ÷ (2a).',
          });
        }
        if (ok || all) {
          const r1 = p.roots[0];
          const r2 = p.roots[1];
          (
            [
              [r1, '−'],
              [r2, '+'],
              [r1, '+'],
              [r2, '−'],
            ] as const
          ).forEach((rs) => {
            out.push({
              line: L(
                rs[0] + ' = (−' + p.b + ' ' + rs[1] + ' √' + p.delta + ') ÷ (2 × ' + p.a + ')',
              ),
              label: 'Formule des racines',
              hora: 5,
              needsProof: !ok,
              why:
                'Δ > 0, donc deux solutions : ' +
                r1 +
                ' = (−b − √Δ) ÷ (2a) et ' +
                r2 +
                ' = (−b + √Δ) ÷ (2a).',
              hint:
                'Δ est positif : il y a deux solutions. Écris ' +
                rs[0] +
                ' = (−b ' +
                rs[1] +
                ' √Δ) ÷ (2a).',
            });
          });
        }
      });
      return out;
    },
    traps(st) {
      const out: Trap[] = [];
      const f = formatNumber;
      preds<SecondDegrePred>(st, 'secondDegre').forEach((p) => {
        const a = st.K[p.a]!;
        const b = st.K[p.b]!;
        const c = st.K[p.c]!;
        out.push({
          line: L(p.delta + ' = ' + p.b + '² + 4 × ' + p.a + ' × ' + p.c),
          title: 'Le signe dans Δ',
          why:
            'Δ = b² − 4ac. Ici, −4 × a × c = −4 × ' +
            f(a) +
            ' × (' +
            f(c) +
            ') = ' +
            f(-4 * a * c) +
            '.',
        });
        p.roots.forEach((r) => {
          ['−', '+'].forEach((sg) => {
            out.push({
              line: L(r + ' = (' + p.b + ' ' + sg + ' √' + p.delta + ') ÷ (2 × ' + p.a + ')'),
              title: 'Le signe de −b',
              why: 'La formule commence par −b. Ici b = ' + f(b) + ', donc −b = ' + f(-b) + '.',
            });
            out.push({
              line: L(r + ' = (−' + p.b + ' ' + sg + ' √' + p.delta + ') ÷ 2'),
              title: 'Diviser par 2a',
              why: 'Le dénominateur est 2a = 2 × ' + f(a) + ' = ' + f(2 * a) + ', pas 2.',
            });
            out.push({
              line: L(r + ' = (−' + p.b + ' ' + sg + ' √' + p.delta + ') ÷ 2 × ' + p.a),
              title: 'Des parenthèses autour de 2a',
              why: 'On divise par le produit 2a : écris ÷ (2a). Sans parenthèses, on divise par 2 puis on multiplie par a.',
            });
            out.push({
              line: L(r + ' = (−' + p.b + ' ' + sg + ' ' + p.delta + ') ÷ (2 × ' + p.a + ')'),
              title: 'La racine de Δ',
              why: 'Dans la formule, c’est √Δ qui apparaît, pas Δ.',
            });
          });
        });
      });
      return out;
    },
    unjustified(st, E) {
      let res: { title: string; message: string } | null = null;
      preds<SecondDegrePred>(st, 'secondDegre').forEach((p) => {
        if (
          res ||
          st.K[p.delta] !== undefined ||
          E.dis.length !== 1 ||
          E.dis[0]!.sides.length !== 2
        )
          return;
        if (
          E.dis[0]!.sides.some(
            (s) => s.k === 's' && (p.roots.indexOf(s.n!) >= 0 || s.n === p.double),
          )
        ) {
          res = {
            title: 'Calcule d’abord Δ',
            message:
              'C’est le signe du discriminant qui dit combien il y a de solutions. Calcule Δ avant d’écrire ' +
              p.roots.join(' ou ') +
              '.',
          };
        }
      });
      return res;
    },
  },

  // a² − b² = (a − b)(a + b) : d'abord faire apparaître les deux carrés, puis factoriser
  differenceCarres: {
    card: 'Identité remarquable a² − b²',
    conclusions(st) {
      const out: RuleConclusion[] = [];
      diffSquares(st).forEach((ds) => {
        if (ds.explicit) {
          out.push({
            line: {
              dis: [
                eqOf(
                  ds.si,
                  nary('mul', [nary('add', [ds.a, neg(ds.b)]), nary('add', [ds.a, ds.b])]),
                  ds.other,
                ),
              ],
            },
            label: 'Identité a² − b²',
            hora: 5,
            transform: true,
            why:
              'a² − b² = (a − b)(a + b), avec a = ' +
              textOf(ds.a) +
              ' et b = ' +
              textOf(ds.b) +
              '.',
            hint: 'Tu as une différence de deux carrés, a² − b². Applique a² − b² = (a − b)(a + b).',
          });
        } else {
          const parts: string[] = [];
          if (!ds.pExplicit) parts.push(textOf(ds.pTerm) + ' = ' + textOf(un('sq', ds.a)));
          if (!ds.qExplicit) parts.push(textOf(ds.qTerm) + ' = ' + textOf(un('sq', ds.b)));
          out.push({
            line: {
              dis: [eqOf(ds.si, nary('add', [un('sq', ds.a), neg(un('sq', ds.b))]), ds.other)],
            },
            label: 'Reconnaître a² − b²',
            hora: 3,
            transform: true,
            why: parts.join(' et ') + ' : on fait apparaître une différence de deux carrés.',
            hint:
              'Fais apparaître deux carrés : ' +
              textOf(ds.qTerm) +
              ', c’est le carré de quel nombre ?',
          });
        }
      });
      return out;
    },
    unjustified(st, E) {
      if (!factoredWithoutHint(st, E) || !diffSquares(st).length) return null;
      return {
        title: 'D’où vient ce produit ?',
        message:
          'Factoriser de tête, c’est risqué. Fais d’abord apparaître deux carrés : écris ' +
          st.model.goal[0] +
          ' = (…)² − (…)². Ensuite, je valide chaque transformation.',
      };
    },
    traps(st) {
      const out: Trap[] = [];
      diffSquares(st).forEach((ds) => {
        out.push({
          line: { dis: [eqOf(ds.si, un('sq', nary('add', [ds.a, neg(ds.b)])), ds.other)] },
          title: 'Ce n’est pas (a − b)²',
          why: '(a − b)² = a² − 2ab + b². Ici, c’est une DIFFÉRENCE de deux carrés : a² − b² = (a − b)(a + b).',
        });
        out.push({
          line: { dis: [eqOf(ds.si, un('sq', nary('add', [ds.a, ds.b])), ds.other)] },
          title: 'Ce n’est pas (a + b)²',
          why: '(a + b)² = a² + 2ab + b². Ici, c’est une DIFFÉRENCE de deux carrés : a² − b² = (a − b)(a + b).',
        });
        if (!ds.qExplicit) {
          out.push({
            line: {
              dis: [
                eqOf(
                  ds.si,
                  nary('mul', [nary('add', [ds.a, neg(ds.qTerm)]), nary('add', [ds.a, ds.qTerm])]),
                  ds.other,
                ),
              ],
            },
            title: 'b, pas b²',
            why:
              textOf(ds.qTerm) +
              ' = ' +
              textOf(un('sq', ds.b)) +
              ' : dans (a − b)(a + b), on met b = ' +
              textOf(ds.b) +
              ', pas ' +
              textOf(ds.qTerm) +
              '.',
          });
        }
      });
      return out;
    },
  },

  // a² + 2ab + b² = (a + b)² et a² − 2ab + b² = (a − b)² : le carré parfait reconnu puis factorisé
  carreParfait: {
    card: 'Identités remarquables (a + b)² et (a − b)²',
    conclusions(st) {
      const out: RuleConclusion[] = [];
      perfectSquares(st).forEach((ps) => {
        const name = ps.minus ? '(a − b)²' : '(a + b)²';
        const form = ps.minus ? 'a² − 2ab + b² = (a − b)²' : 'a² + 2ab + b² = (a + b)²';
        if (ps.explicit) {
          out.push({
            line: {
              dis: [
                eqOf(ps.si, un('sq', nary('add', [ps.a, ps.minus ? neg(ps.b) : ps.b])), ps.other),
              ],
            },
            label: 'Identité ' + name,
            hora: 5,
            transform: true,
            why: form + ', avec a = ' + textOf(ps.a) + ' et b = ' + textOf(ps.b) + '.',
            hint:
              'Tu reconnais ' +
              (ps.minus ? 'a² − 2ab + b²' : 'a² + 2ab + b²') +
              '. Écris-le sous la forme ' +
              name +
              '.',
          });
        } else {
          const middle = nary('mul', [num(2), ps.a, ps.b]);
          out.push({
            line: {
              dis: [
                eqOf(
                  ps.si,
                  nary('add', [un('sq', ps.a), ps.minus ? neg(middle) : middle, un('sq', ps.b)]),
                  ps.other,
                ),
              ],
            },
            label: 'Reconnaître ' + name,
            hora: 3,
            transform: true,
            why:
              'On fait apparaître ' +
              (ps.minus ? 'a² − 2ab + b²' : 'a² + 2ab + b²') +
              ', avec a = ' +
              textOf(ps.a) +
              ' et b = ' +
              textOf(ps.b) +
              '.',
            hint:
              'Trois termes, dont deux carrés : ' +
              textOf(ps.pTerm) +
              ' et ' +
              textOf(ps.qTerm) +
              '. Écris-les a² et b², et le terme du milieu 2 × a × b.',
          });
        }
      });
      return out;
    },
    unjustified(st, E) {
      if (!factoredWithoutHint(st, E) || !perfectSquares(st).length) return null;
      return {
        title: 'D’où vient ce carré ?',
        message:
          'Factoriser de tête, c’est risqué. Fais d’abord apparaître a² + 2ab + b² (ou a² − 2ab + b²) : les deux carrés et le double produit 2 × a × b. Ensuite, je valide chaque transformation.',
      };
    },
    traps(st) {
      const out: Trap[] = [];
      perfectSquares(st).forEach((ps) => {
        out.push({
          line: {
            dis: [
              eqOf(ps.si, un('sq', nary('add', [ps.a, ps.minus ? ps.b : neg(ps.b)])), ps.other),
            ],
          },
          title: ps.minus ? 'Le signe dans la parenthèse' : 'Ce n’est pas (a − b)²',
          why: ps.minus
            ? 'Le double produit est précédé d’un « − » : c’est (a − b)² = a² − 2ab + b², pas (a + b)².'
            : 'Le double produit est précédé d’un « + » : c’est (a + b)² = a² + 2ab + b², pas (a − b)².',
        });
        out.push({
          line: {
            dis: [
              eqOf(
                ps.si,
                nary('mul', [nary('add', [ps.a, neg(ps.b)]), nary('add', [ps.a, ps.b])]),
                ps.other,
              ),
            ],
          },
          title: 'Ce n’est pas a² − b²',
          why: '(a − b)(a + b) = a² − b² : il n’y a pas de double produit. Ici, il y en a un : c’est un carré parfait.',
        });
      });
      return out;
    },
  },

  // une formule de la SKILLCARD, donnée sous forme de données (périmètre, aire, prix au mètre…)
  formule: {
    card: 'Formule',
    conclusions(st) {
      return preds<FormulePred>(st, 'formule').map((p) => ({
        line: L(p.line),
        label: p.label || 'Formule',
        hora: p.hora || 3,
        why: p.why || 'On applique la formule : ' + p.line + '.',
        hint: p.hint || 'Utilise la formule : ' + p.line + '.',
      }));
    },
  },

  // P(n) : la formule. On vérifie P(0), on suppose P(n), on démontre P(n+1) ;
  // alors seulement la formule s'applique à n'importe quel rang.
  recurrence: {
    card: 'Raisonnement par récurrence',
    setup(st, p) {
      addFact(st, L((p as RecurrencePred).hyp), 'hyp');
    },
    conclusions(st, all) {
      const out: RuleConclusion[] = [];
      preds<RecurrencePred>(st, 'recurrence').forEach((p) => {
        out.push({
          line: L(p.init),
          label: 'Initialisation',
          hora: 3,
          obligation: true,
          why: 'On vérifie que la formule est vraie au premier rang, n = 0.',
          hint:
            'Commence par l’initialisation : la formule est-elle vraie pour n = 0 ? Compare ' +
            p.init.split('=')[0]!.trim() +
            ' et la formule au rang 0.',
        });
        const proven = recurrenceProven(st, p);
        if (proven || all) {
          (p.inst || []).forEach((s) => {
            out.push({
              line: L(s),
              label: 'Conclusion de la récurrence',
              hora: 5,
              needsProof: !proven,
              why: 'Initialisation et hérédité sont démontrées : la formule est vraie pour tout n, donc aussi pour ce rang.',
              hint: 'L’initialisation et l’hérédité sont faites : la formule est vraie pour tout n. Applique-la au rang demandé.',
            });
          });
        }
      });
      return out;
    },
    next(st) {
      const p = preds<RecurrencePred>(st, 'recurrence')[0];
      if (!p || st.stated.hyp || !established(st, L(p.init))) return null;
      return {
        line: L(p.hyp),
        kind: 'hyp',
        label: 'Hypothèse de récurrence',
        text: 'Hérédité : suppose la formule vraie à un rang n. Écris cette hypothèse.',
        why: 'Hérédité : on suppose la formule vraie à un rang n, pour la démontrer au rang n + 1.',
      };
    },
    traps(st) {
      const out: Trap[] = [];
      preds<RecurrencePred>(st, 'recurrence').forEach((p) => {
        const hyp = L(p.hyp).dis[0]!;
        const nxt = L(p.next).dis[0]!;
        const s = hyp.sides[0]!.k === 's' ? hyp.sides[0]! : hyp.sides[1]!;
        const wrong = hyp.sides[0]!.k === 's' ? nxt.sides[1]! : nxt.sides[0]!;
        st.facts.forEach((f) => {
          if (f.source === 'hyp' || f.line.dis.length !== 1) return;
          const d = f.line.dis[0]!;
          if (!symsOf({ k: 'add', c: d.sides })[s.n!]) return;
          out.push({
            line: {
              dis: [{ sides: d.sides.map((x) => (x.k === 's' ? x : replaceSym(x, s.n!, wrong))) }],
            },
            title: 'Hypothèse au mauvais rang',
            why:
              'Tu remplaces ' +
              s.n +
              ' : c’est l’hypothèse au rang n qui sert, ' +
              p.hyp +
              '. La formule au rang n + 1, c’est ce que tu cherches à démontrer.',
          });
        });
      });
      return out;
    },
    unjustified(st, E) {
      let res: { title: string; message: string } | null = null;
      preds<RecurrencePred>(st, 'recurrence').forEach((p) => {
        if (res || E.dis.length !== 1) return;
        if (matchLine(E, L(p.next), st.K) && !established(st, L(p.next))) {
          res = {
            title: 'C’est le but de l’hérédité',
            message:
              'Cette égalité, c’est la formule au rang n + 1 : c’est ce que tu dois démontrer, pas un point de départ. Pars de la définition de la suite et utilise l’hypothèse de récurrence.',
          };
          return;
        }
        const proven = recurrenceProven(st, p);
        (p.inst || []).forEach((s) => {
          if (!res && !proven && matchLine(E, L(s), st.K)) {
            res = {
              title: 'Pas encore démontré',
              message:
                'Tu appliques la formule, mais elle n’est encore qu’une conjecture. Démontre-la d’abord : initialisation (n = 0), puis hérédité (de n à n + 1).',
            };
          }
        });
      });
      return res;
    },
  },
};

// un carré : u² écrit tel quel, ou un nombre / un monôme qui en est un (25 = 5², 9x² = (3x)²)
function sqRoot(t: Node): { r: Node; explicit: boolean } | null {
  if (t.k === 'sq') return { r: t.a!, explicit: true };
  if (t.k === 'n' && t.v! > 0 && Number.isInteger(Math.sqrt(t.v!)))
    return { r: num(Math.sqrt(t.v!)), explicit: false };
  if (
    t.k === 'mul' &&
    t.c!.length === 2 &&
    t.c![0]!.k === 'n' &&
    t.c![1]!.k === 'sq' &&
    Number.isInteger(Math.sqrt(t.c![0]!.v!))
  ) {
    return { r: nary('mul', [num(Math.sqrt(t.c![0]!.v!)), t.c![1]!.a!]), explicit: false };
  }
  return null;
}
interface DiffSquares {
  si: number;
  other: Node;
  a: Node;
  b: Node;
  explicit: boolean;
  pExplicit: boolean;
  qExplicit: boolean;
  pTerm: Node;
  qTerm: Node;
}
function diffSquares(st: ReasonerState): DiffSquares[] {
  const out: DiffSquares[] = [];
  st.facts.forEach((f) => {
    if (f.line.dis.length !== 1 || f.line.dis[0]!.sides.length !== 2) return;
    const d = f.line.dis[0]!;
    [0, 1].forEach((si) => {
      const s = d.sides[si]!;
      if (!hasUnknown(s, st.K)) return;
      const ds = diffSquaresOf(s);
      if (ds) out.push({ si, other: d.sides[1 - si]!, ...ds });
    });
  });
  return out;
}
// p − q avec p et q deux carrés (écrits u² ou reconnaissables : 25, 9x²)
function diffSquaresOf(s: Node): Omit<DiffSquares, 'si' | 'other'> | null {
  if (s.k !== 'add' || s.c!.length !== 2) return null;
  const pos = s.c!.filter((t) => !signSplit(t).neg);
  const negs = s.c!.filter((t) => signSplit(t).neg);
  if (pos.length !== 1 || negs.length !== 1) return null;
  const q = signSplit(negs[0]!).t;
  const rp = sqRoot(pos[0]!);
  const rq = sqRoot(q);
  if (!rp || !rq) return null;
  return {
    a: rp.r,
    b: rq.r,
    explicit: rp.explicit && rq.explicit,
    pExplicit: rp.explicit,
    qExplicit: rq.explicit,
    pTerm: pos[0]!,
    qTerm: q,
  };
}
// une forme factorisée (produit ou carré) écrite sans étape, quand le modèle ne donne pas son propre conseil
function factoredWithoutHint(st: ReasonerState, E: Line): boolean {
  if (
    st.model.aim !== 'factor' ||
    st.model.factorHint ||
    E.dis.length !== 1 ||
    E.dis[0]!.sides.length !== 2
  )
    return false;
  return E.dis[0]!.sides.some(
    (s) => topFactors(s, st.K).length >= 2 || (s.k === 'sq' && s.a!.k === 'add'),
  );
}
interface PerfectSquare {
  si: number;
  other: Node;
  a: Node;
  b: Node;
  minus: boolean;
  /** les deux carrés sont déjà écrits a² et b² */
  explicit: boolean;
  pTerm: Node;
  qTerm: Node;
}
// trois termes : deux carrés (positifs) et un double produit ± 2ab, vérifié par le calcul
function perfectSquares(st: ReasonerState): PerfectSquare[] {
  const out: PerfectSquare[] = [];
  st.facts.forEach((f) => {
    if (f.line.dis.length !== 1 || f.line.dis[0]!.sides.length !== 2) return;
    const d = f.line.dis[0]!;
    [0, 1].forEach((si) => {
      const s = d.sides[si]!;
      if (!hasUnknown(s, st.K)) return;
      const ps = perfectSquareOf(s, st.K);
      if (ps) out.push({ si, other: d.sides[1 - si]!, ...ps });
    });
  });
  return out;
}
function perfectSquareOf(s: Node, K: Env): Omit<PerfectSquare, 'si' | 'other'> | null {
  if (s.k !== 'add' || s.c!.length !== 3) return null;
  for (let i = 0; i < 3; i++)
    for (let j = i + 1; j < 3; j++) {
      const k = 3 - i - j;
      const P = s.c![i]!;
      const Q = s.c![j]!;
      if (signSplit(P).neg || signSplit(Q).neg) continue;
      const rp = sqRoot(P);
      const rq = sqRoot(Q);
      if (!rp || !rq) continue;
      const two = nary('mul', [num(2), rp.r, rq.r]);
      const m = s.c![k]!;
      const minus = identical(m, neg(two), K);
      if (!minus && !identical(m, two, K)) continue;
      return { a: rp.r, b: rq.r, minus, explicit: rp.explicit && rq.explicit, pTerm: P, qTerm: Q };
    }
  return null;
}
function recurrenceProven(st: ReasonerState, p: RecurrencePred): boolean {
  return established(st, L(p.init)) && established(st, L(p.next));
}
function productZero(eq: Eq, K: Env): Node[] | null {
  if (eq.sides.length !== 2) return null;
  for (let i = 0; i < 2; i++) {
    const z = eq.sides[i]!;
    const p = eq.sides[1 - i]!;
    // un carré nul : u² = 0 donne u = 0
    if (z.k === 'n' && z.v === 0 && p.k === 'sq' && hasUnknown(p.a!, K) && p.a!.k === 'add')
      return [p.a!];
    if (
      z.k === 'n' &&
      z.v === 0 &&
      p.k === 'mul' &&
      p.c!.filter((c) => hasUnknown(c, K)).length >= 2
    ) {
      return p.c!.filter((c) => hasUnknown(c, K));
    }
  }
  return null;
}
function ruleConclusions(st: ReasonerState, all?: boolean): RuleConclusion[] {
  const out: RuleConclusion[] = [];
  const used: Record<string, 1> = {};
  (st.model.preds || []).forEach((p) => {
    used[p.skill] = 1;
  });
  if (st.facts.some((f) => f.line.dis.length === 1 && productZero(f.line.dis[0]!, st.K)))
    used.produitNul = 1;
  Object.keys(SKILLS).forEach((id) => {
    if (!used[id]) return;
    SKILLS[id]!.conclusions(st, all).forEach((c) => {
      c.skill = id;
      c.card = SKILLS[id]!.card;
      out.push(c);
    });
  });
  return out;
}
// une ligne déjà obtenue par l'élève (pas une simple hypothèse)
function established(st: ReasonerState, line: Line): boolean {
  const K = st.K;
  const unk = hasUnknownLine(line, K);
  return st.facts.some((f) => {
    if (f.source !== 'line') return false;
    return !!matchLine(f.line, line, K) || (unk && lineEquiv(f.line, line, K));
  });
}

/* ================= mouvements génériques ================= */
function trivSimp(t: Node): Node {
  if (t.c) {
    const c = t.c
      .map(trivSimp)
      .filter(
        (x) =>
          !(t.k === 'add' && x.k === 'n' && x.v === 0) &&
          !(t.k === 'mul' && x.k === 'n' && x.v === 1),
      );
    if (!c.length) return num(t.k === 'add' ? 0 : 1);
    return nary(t.k, c);
  }
  return mapKids(t, trivSimp);
}
function eqOf(si: number, side: Node, other: Node): Eq {
  return { sides: si === 0 ? [side, other] : [other, side] };
}
function verb(t: Node): string {
  if (t.k === 'n')
    return t.v! > 0
      ? 'Retire ' + formatNumber(t.v!) + ' des deux côtés.'
      : 'Ajoute ' + formatNumber(-t.v!) + ' aux deux côtés.';
  return 'Retire ' + textOf(t) + ' des deux côtés.';
}
interface Transposition {
  eq: Eq;
  kind: 'transpo' | 'bothsides';
  desc: string;
  /** terme en x déplacé : la ligne se mesure une fois les termes semblables regroupés */
  unknownMove?: boolean;
}
// même opération des deux côtés : on ne déplace que des nombres
function transpose(eq: Eq, K: Env): Transposition[] {
  const out: Transposition[] = [];
  if (eq.sides.length !== 2) return out;
  [0, 1].forEach((si) => {
    const side = eq.sides[si]!;
    const other = eq.sides[1 - si]!;
    if (!hasUnknown(side, K)) return;
    if (side.k === 'add') {
      side.c!.forEach((t, i) => {
        if (!numericIn(t, K)) return;
        const rest = nary(
          'add',
          side.c!.filter((_, j) => j !== i),
        );
        const moved = neg(t);
        out.push({
          eq: eqOf(si, rest, nary('add', [other, moved])),
          kind: 'transpo',
          desc: verb(t),
        });
        out.push({
          eq: eqOf(si, nary('add', [side, moved]), nary('add', [other, moved])),
          kind: 'bothsides',
          desc: verb(t),
        });
      });
    }
    if (side.k === 'mul') {
      side.c!.forEach((fct, i) => {
        if (!numericIn(fct, K)) return;
        const rest = nary(
          'mul',
          side.c!.filter((_, j) => j !== i),
        );
        const d = 'Divise les deux côtés par ' + textOf(fct) + '.';
        out.push({ eq: eqOf(si, rest, dv(other, fct)), kind: 'transpo', desc: d });
        out.push({ eq: eqOf(si, dv(side, fct), dv(other, fct)), kind: 'bothsides', desc: d });
      });
    }
    if (side.k === 'div' && numericIn(side.b!, K)) {
      out.push({
        eq: eqOf(si, side.a!, nary('mul', [other, side.b!])),
        kind: 'transpo',
        desc: 'Multiplie les deux côtés par ' + textOf(side.b!) + '.',
      });
    }
    if (side.k === 'neg')
      out.push({
        eq: eqOf(si, side.a!, neg(other)),
        kind: 'transpo',
        desc: 'Change les signes des deux côtés.',
      });
    // système (2x + y = 3) : l'inconnue de coefficient 1 seule d'un côté → y = 3 − 2x
    if (side.k === 'add' && !hasUnknown(other, K)) {
      const unkNames = Object.keys(symsOf(side)).filter((x) => K[x] === undefined);
      if (unkNames.length === 2)
        side.c!.forEach((t, i) => {
          if (t.k !== 's' || K[t.n!] !== undefined) return;
          const rest = side.c!.filter((_, j) => j !== i);
          out.push({
            eq: eqOf(si, t, nary('add', [other].concat(rest.map(neg)))),
            kind: 'transpo',
            desc: 'Isole ' + t.n + ' : les autres termes passent de l’autre côté.',
          });
        });
    }
    // l'inconnue des deux côtés (2x − 1 = x + 4) : ses termes passent à gauche
    if (hasUnknown(other, K)) {
      const terms = other.k === 'add' ? other.c! : [other];
      const unk = (n: Node) => Object.keys(symsOf(n)).filter((x) => K[x] === undefined);
      const left = unk(side);
      terms.forEach((t, i) => {
        // seulement l'inconnue déjà à gauche (pas « A = x² − 14x + 49 »)
        const u = unk(t);
        if (!u.length || !u.every((x) => left.includes(x))) return;
        const rest = terms.filter((_, j) => j !== i);
        const lhs = nary('add', (side.k === 'add' ? side.c! : [side]).concat([neg(t)]));
        const moved = eqOf(si, lhs, rest.length ? nary('add', rest) : num(0));
        out.push({ eq: moved, kind: 'transpo', desc: verb(t), unknownMove: true });
        // la même ligne, termes semblables regroupés (2x − 1 = x + 4 → x − 1 = 4)
        out.push({
          eq: { sides: moved.sides.map((x) => trivSimp(collectDeep(x, [], K))) },
          kind: 'transpo',
          desc: verb(t).replace(/\.$/, ', puis regroupe les x.'),
          unknownMove: true,
        });
      });
    }
  });
  const more: Transposition[] = [];
  out.forEach((o) => {
    const s = { sides: o.eq.sides.map(trivSimp) };
    if (lineText({ dis: [s] }) !== lineText({ dis: [o.eq] }))
      more.push({ eq: s, kind: o.kind, desc: o.desc });
    // le coefficient calculé dans la foulée : x = 0,3y ÷ 0,2 → x = 1,5y
    if (hasUnknown(o.eq.sides[0]!, K) && hasUnknown(o.eq.sides[1]!, K)) {
      const did: unknown[] = [];
      const c = { sides: s.sides.map((x) => evalInner(x, {}, did)) };
      if (did.length && lineText({ dis: [c] }) !== lineText({ dis: [s] }))
        more.push({ eq: c, kind: o.kind, desc: o.desc });
    }
  });
  return out.concat(more);
}
function isPositiveSym(st: ReasonerState, n: string): boolean {
  const s = st.model.symbols[n];
  return !!s && s.kind === 'length';
}
function racine(eq: Eq, st: ReasonerState): Eq[] {
  const out: Eq[] = [];
  if (eq.sides.length !== 2) return out;
  [0, 1].forEach((si) => {
    const s = eq.sides[si]!;
    if (
      s.k === 'sq' &&
      s.a!.k === 's' &&
      st.K[s.a!.n!] === undefined &&
      isPositiveSym(st, s.a!.n!)
    ) {
      out.push({ sides: [s.a!, un('sqrt', eq.sides[1 - si]!)] });
    }
  });
  return out;
}
// x² = 49 (x n'est pas une longueur) : x = √49 ou x = −√49
function racine2(eq: Eq, st: ReasonerState): Line[] {
  const out: Line[] = [];
  if (eq.sides.length !== 2) return out;
  [0, 1].forEach((si) => {
    const s = eq.sides[si]!;
    const o = eq.sides[1 - si]!;
    if (s.k !== 'sq') return;
    // x² = 49, ou (x − 1)² = 49 : le carré d'une expression qui contient l'inconnue
    if (s.a!.k === 's') {
      if (st.K[s.a!.n!] !== undefined || isPositiveSym(st, s.a!.n!)) return;
    } else if (!hasUnknown(s.a!, st.K)) return;
    if (hasUnknown(o, st.K)) return;
    const v = numVal(o, st.K);
    if (v === null || !(v > 0)) return;
    const r = un('sqrt', o);
    out.push({ dis: [{ sides: [s.a!, r] }, { sides: [s.a!, neg(r)] }] });
  });
  return out;
}
// les lignes « s = expression » permettent de remplacer s ailleurs
function definitions(st: ReasonerState): Definition[] {
  const out: Definition[] = [];
  st.facts.forEach((g, gi) => {
    if (g.line.dis.length !== 1) return;
    const d = g.line.dis[0]!;
    if (d.sides.length !== 2) return;
    [0, 1].forEach((si) => {
      const s = d.sides[si]!;
      const e = d.sides[1 - si]!;
      if (s.k !== 's' || st.K[s.n!] !== undefined || e.k === 's' || symsOf(e)[s.n!]) return;
      if (!hasUnknown(e, st.K)) return;
      out.push({ s: s.n!, expr: e, gi, source: g.source });
    });
  });
  return out;
}
function substCandidates(st: ReasonerState, defs: Definition[]): Candidate[] {
  const out: Candidate[] = [];
  st.facts.forEach((f, fi) => {
    if (f.line.dis.length !== 1) return;
    const d = f.line.dis[0]!;
    if (d.sides.length !== 2) return;
    defs.forEach((df) => {
      if (df.gi === fi) return;
      let hit = false;
      const sides = d.sides.map((x) => {
        if (!symsOf(x)[df.s]) return x;
        hit = true;
        return replaceSym(x, df.s, df.expr);
      });
      if (hit) out.push({ line: { dis: [{ sides }] }, kind: 'subst', def: df, src: fi });
    });
  });
  return out;
}

/* ================= candidats : tout ce qu'on peut écrire en un pas ================= */
export function candidates(st: ReasonerState, deep?: boolean, all?: boolean): Candidate[] {
  const K = st.K;
  const out: Candidate[] = [];
  st.facts.forEach((f, fi) => {
    out.push({ line: f.line, kind: 'fact', src: fi });
    const dis = f.line.dis;
    if (dis.length > 1) dis.forEach((d) => out.push({ line: { dis: [d] }, kind: 'case', src: fi }));
    const per = dis.map((d) => transpose(d, K));
    per.forEach((list) => {
      list.forEach((t) => {
        out.push({
          line: { dis: [t.eq] },
          kind: dis.length > 1 ? 'transpo' : t.kind,
          desc: t.desc,
          src: fi,
        });
        if (deep)
          transpose(t.eq, K).forEach((t2) =>
            out.push({
              line: { dis: [t2.eq] },
              kind: 'transpo',
              desc: t2.desc,
              src: fi,
              deep: true,
            }),
          );
        // x à gauche, nombres à droite en une ligne : 2x − 1 = x + 4 → 2x − x = 4 + 1
        else if (t.unknownMove && dis.length === 1)
          transpose(t.eq, K)
            .filter((t2) => t2.kind === 'transpo' && !t2.unknownMove)
            .forEach((t2) =>
              out.push({ line: { dis: [t2.eq] }, kind: 'transpo', desc: t2.desc, src: fi }),
            );
      });
    });
    if (dis.length > 1) {
      const options = dis.map((d, i) =>
        [d].concat(per[i]!.filter((t) => t.kind === 'transpo').map((t) => t.eq)),
      );
      let combos: Eq[][] = [[]];
      options.forEach((opts) => {
        const nx: Eq[][] = [];
        combos.forEach((c) => opts.forEach((o) => nx.push(c.concat([o]))));
        combos = nx.slice(0, 60);
      });
      combos.forEach((c) => {
        if (c.every((x, i) => x === dis[i])) return;
        out.push({ line: { dis: c }, kind: 'transpo', src: fi });
      });
    }
    // chasser les dénominateurs : x ÷ 3 + 2 = x ÷ 2 → 2x + 12 = 3x
    if (dis.length === 1) {
      const lcd = commonDenominator(dis[0]!, K);
      if (lcd)
        out.push({
          line: { dis: [{ sides: dis[0]!.sides.map((x) => scaleSide(x, lcd)) }] },
          kind: 'bothsides',
          desc: 'multiplie les deux membres par ' + lcd + ', le dénominateur commun.',
          src: fi,
        });
    }
    dis.forEach((d) => {
      racine(d, st).forEach((r) => out.push({ line: { dis: [r] }, kind: 'racine', src: fi }));
      if (dis.length === 1)
        racine2(d, st).forEach((r) => out.push({ line: r, kind: 'racine', src: fi }));
    });
  });
  substCandidates(st, definitions(st)).forEach((c) => out.push(c));
  ruleConclusions(st, all).forEach((rc) => {
    out.push({ line: rc.line, kind: 'rule', rc });
    if (rc.line.dis.length > 1) {
      rc.line.dis.forEach((d) => {
        out.push({ line: { dis: [d] }, kind: 'rule', rc, isCase: true });
        if (deep)
          transpose(d, K).forEach((t) =>
            out.push({ line: { dis: [t.eq] }, kind: 'rule', rc, isCase: true, deep: true }),
          );
      });
    } else if (deep) {
      transpose(rc.line.dis[0]!, K).forEach((t) =>
        out.push({ line: { dis: [t.eq] }, kind: 'rule', rc, deep: true }),
      );
    }
  });
  return out;
}

/* ================= erreurs ================= */
function modelTraps(st: ReasonerState): Trap[] {
  let t = TRAPS_CACHE.get(st.model);
  if (!t) {
    t = (st.model.traps || []).map((x) => ({ line: L(x.line), title: x.title, why: x.why }));
    TRAPS_CACHE.set(st.model, t);
  }
  return t;
}
function trapLines(st: ReasonerState): Trap[] {
  const out: Trap[] = [];
  const used: Record<string, 1> = {};
  (st.model.preds || []).forEach((p) => {
    used[p.skill] = 1;
  });
  used.produitNul = 1;
  Object.keys(SKILLS).forEach((id) => {
    const tr = SKILLS[id]!.traps;
    if (used[id] && tr) tr(st).forEach((t) => out.push(t));
  });
  modelTraps(st).forEach((t) => out.push(t));
  // la racine oubliée : X² = … donne X = … sans racine
  st.facts.forEach((f) => {
    f.line.dis.forEach((d) => {
      if (d.sides.length !== 2) return;
      [0, 1].forEach((si) => {
        const s = d.sides[si]!;
        if (s.k === 'sq' && s.a!.k === 's' && st.K[s.a!.n!] === undefined) {
          out.push({
            line: eq2(s.a!, d.sides[1 - si]!),
            title: 'La racine oubliée',
            why:
              'C’est ' +
              s.a!.n +
              '² qui vaut ça. Pour trouver ' +
              s.a!.n +
              ', il faut prendre la racine carrée.',
          });
        }
      });
    });
  });
  return out;
}
interface MisRewrite {
  to: Node;
  title: string;
  why: string;
}
// règles de calcul mal appliquées, à partir de ce qui est établi
function misRewrites(t: Node): MisRewrite[] {
  const out: MisRewrite[] = [];
  function walk(node: Node, rebuild: (x: Node) => Node): void {
    if (node.k === 'sq' && node.a!.k === 'add' && node.a!.c!.length === 2) {
      out.push({
        to: rebuild(
          nary(
            'add',
            node.a!.c!.map((u) => un('sq', signSplit(u).t)),
          ),
        ),
        title: 'Il manque le double produit',
        why: '(a + b)² = a² + 2ab + b² : le carré d’une somme n’est pas la somme des carrés.',
      });
      const sp = signSplit(node.a!.c![0]!);
      const sq = signSplit(node.a!.c![1]!);
      if (sp.neg !== sq.neg)
        out.push({
          to: rebuild(
            nary('add', [un('sq', sp.t), fold(nary('mul', [num(2), sp.t, sq.t])), un('sq', sq.t)]),
          ),
          title: 'Le signe du double produit',
          why: '(a − b)² = a² − 2ab + b² : le double produit garde le signe « − ».',
        });
    }
    if (node.k === 'neg' && node.a!.k === 'add' && node.a!.c!.length >= 2) {
      out.push({
        to: rebuild(nary('add', [neg(node.a!.c![0]!)].concat(node.a!.c!.slice(1)))),
        title: 'Le « − » devant la parenthèse',
        why:
          'Un « − » devant une parenthèse change le signe de CHAQUE terme : ' +
          textOf(node) +
          ' = ' +
          textOf(nary('add', node.a!.c!.map(neg))) +
          '.',
      });
    }
    if (
      node.k === 'mul' &&
      node.c!.length === 2 &&
      node.c!.every((c) => c.k === 'add' && c.c!.length === 2)
    ) {
      const p = node.c![0]!.c!;
      const q = node.c![1]!.c!;
      out.push({
        to: rebuild(nary('add', [prodSigned([], p[0]!, q[0]!), prodSigned([], p[1]!, q[1]!)])),
        title: 'Les produits croisés oubliés',
        why:
          'Chaque terme de la 1re parenthèse multiplie chaque terme de la 2de : ' +
          textOf(node) +
          ' donne 4 produits, pas 2.',
      });
    }
    if (node.k === 'mul') {
      node.c!.forEach((c, i) => {
        if (c.k !== 'add' || c.c!.length < 2) return;
        const others = node.c!.filter((_, j) => j !== i);
        if (!others.every((o) => o.k === 'n')) return;
        const first = fold(nary('mul', others.concat([c.c![0]!])));
        out.push({
          to: rebuild(nary('add', [first].concat(c.c!.slice(1)))),
          title: 'Distribuer à chaque terme',
          why:
            'Le facteur multiplie TOUS les termes de la parenthèse : ' +
            textOf(node) +
            ' = ' +
            textOf(expand(node)) +
            '.',
        });
      });
      node.c!.forEach((c, i) => {
        if (c.k !== 'n') return;
        node.c!.forEach((p, j) => {
          if (p.k !== 'pow' || p.a!.k !== 'n' || p.a!.v !== c.v) return;
          const rest = node.c!.filter((_, q) => q !== i && q !== j);
          const wrong = pw(num(c.v! * c.v!), p.b!);
          out.push({
            to: rebuild(rest.length ? nary('mul', rest.concat([wrong])) : wrong),
            title: 'Multiplier des puissances',
            why:
              textOf(nary('mul', [c, p])) +
              ' = ' +
              textOf(pw(num(c.v!), fold(nary('add', [p.b!, num(1)])))) +
              ' : on ajoute 1 à l’exposant. Ce n’est pas ' +
              textOf(wrong) +
              '.',
          });
        });
      });
    }
    if (node.c)
      node.c.forEach((c, i) =>
        walk(c, (x) => {
          const cc = node.c!.slice();
          cc[i] = x;
          return rebuild(nary(node.k, cc));
        }),
      );
    else if (isBin(node)) {
      walk(node.a!, (x) => rebuild(bin(node.k, x, node.b!)));
      walk(node.b!, (x) => rebuild(bin(node.k, node.a!, x)));
    } else if (node.a) walk(node.a, (x) => rebuild(node.k === 'neg' ? neg(x) : un(node.k, x)));
  }
  walk(t, (x) => x);
  return out;
}
function wrongRewrites(st: ReasonerState): Trap[] {
  const out: Trap[] = [];
  const sources = st.facts
    .map((f) => f.line)
    .concat(substCandidates(st, definitions(st)).map((c) => c.line))
    .concat(ruleConclusions(st).map((r) => r.line));
  sources.slice().forEach((line) => improveChain(line, st, sources, 2));
  sources.forEach((line) => {
    if (line.dis.length !== 1 || line.dis[0]!.sides.length !== 2) return;
    const d = line.dis[0]!;
    [0, 1].forEach((si) => {
      misRewrites(d.sides[si]!).forEach((w) => {
        out.push({
          line: { dis: [eqOf(si, w.to, d.sides[1 - si]!)] },
          title: w.title,
          why: w.why,
          ident: true,
        });
      });
    });
  });
  return out;
}
interface Mutation {
  line: Line;
  title: string;
  why?: string;
  sign?: boolean;
  square?: boolean;
}
// erreurs génériques : on « répare » la ligne de l'élève ; si la réparation est justifiée, on sait quelle erreur il a faite
function mutations(E: Line): Mutation[] {
  const out: Mutation[] = [];
  function walk(t: Node, rebuild: (x: Node) => Line): void {
    if (t.k === 'n' && t.v !== 0) {
      out.push({ line: rebuild(num(-t.v!)), title: 'Attention au signe', sign: true });
    }
    if (
      t.k === 'add' &&
      t.c!.length === 2 &&
      ((t.c![1]!.k === 'n' && t.c![1]!.v! < 0) || t.c![1]!.k === 'neg')
    ) {
      const dvd = t.c![1]!.k === 'n' ? num(-t.c![1]!.v!) : t.c![1]!.a!;
      out.push({
        line: rebuild(dv(t.c![0]!, dvd)),
        title: 'Diviser, pas soustraire',
        why: 'Le nombre multiplie l’inconnue : pour l’enlever, on divise les deux côtés. On ne le soustrait pas.',
      });
    }
    if (t.k === 'div') {
      out.push({
        line: rebuild(dv(t.b!, t.a!)),
        title: 'Dans le bon sens',
        why: 'On divise ce qui est de l’autre côté par le nombre qui multiplie l’inconnue, pas l’inverse.',
      });
    }
    if (t.k === 'sq') {
      out.push({
        line: rebuild(nary('mul', [t.a!, num(2)])),
        title: 'Le carré n’est pas le double',
        why: 'Un carré, c’est le nombre multiplié par lui-même : 6² = 6 × 6 = 36, pas 6 × 2.',
        square: true,
      });
    }
    if (t.c)
      t.c.forEach((c, i) =>
        walk(c, (x) => {
          const cc = t.c!.slice();
          cc[i] = x;
          return rebuild(nary(t.k, cc));
        }),
      );
    else if (isBin(t)) {
      if (t.k === 'pow') return;
      walk(t.a!, (x) => rebuild(bin(t.k, x, t.b!)));
      walk(t.b!, (x) => rebuild(bin(t.k, t.a!, x)));
    } else if (t.a) walk(t.a, (x) => rebuild(t.k === 'neg' ? neg(x) : un(t.k, x)));
  }
  E.dis.forEach((d, di) => {
    d.sides.forEach((s, si) => {
      walk(s, (x) => ({
        dis: E.dis.map((dd, j) =>
          j !== di ? dd : { sides: dd.sides.map((ss, k) => (k === si ? x : ss)) },
        ),
      }));
    });
  });
  return out;
}

/* ================= état ================= */
const MATERIALIZED = new WeakMap<ScratchModel, ScratchModel>();
// `samples` → `envs` une seule fois par modèle (cf. samples.ts)
function playable(model: ScratchModel): ScratchModel {
  if ((!model.samples || model.envs) && !hasExactValues(model)) return model;
  let m = MATERIALIZED.get(model);
  if (!m) {
    m = materializeModel(model);
    MATERIALIZED.set(model, m);
  }
  return m;
}

export function createState(input: ScratchModel): ReasonerState {
  const model = playable(input);
  const st: ReasonerState = { model, K: {}, facts: [], stated: {} };
  if (model.kernel?.plugin === 'geometry') {
    st.kgeo = createKGeoState(model);
    return st;
  }
  if (model.kernel?.induction) {
    st.krec = createKRecState(model);
    return st;
  }
  if (model.kernel?.plugin === 'python') {
    st.kprog = createKProgState(model);
    return st;
  }
  if (model.kernel?.plugin === 'matrix') {
    st.kmat = createKMatState(model);
    return st;
  }
  if (model.kernel?.plugin === 'arith') {
    st.karith = createKArithState(model);
    return st;
  }
  if (model.kernel?.plugin === 'limit') {
    st.klim = createKLimState(model);
    return st;
  }
  if (model.kernel?.plugin === 'limclaims') {
    st.klc = createKLcState(model);
    return st;
  }
  if (model.kernel?.plugin === 'integral') {
    st.kint = createKIntState(model);
    return st;
  }
  if (model.kernel?.plugin === 'ode') {
    st.kode = createKOdeState(model);
    return st;
  }
  if (model.kernel?.plugin === 'partial') {
    st.kpart = createKPartState(model);
    return st;
  }
  if (model.kernel?.plugin === 'riemann') {
    st.kriem = createKRiemState(model);
    return st;
  }
  if (model.kernel?.plugin === 'claims') {
    st.kclaims = createKClaimsState(model);
    return st;
  }
  if (model.kernel?.plugin === 'variations') {
    st.kvar = createKVarState(model);
    return st;
  }
  if (model.kernel?.plugin === 'tables') {
    st.ktab = createKTabState(model);
    return st;
  }
  if (model.kernel?.plugin === 'analysis') {
    st.kana = createKAnaState(model);
    return st;
  }
  if (model.kernel?.plugin === 'formula') {
    st.kfor = createKForState(model);
    return st;
  }
  // modes spéciaux passés au noyau (moteur v34) : l'état du mode, juge du noyau
  const special = kernelSpecialPlugin(model);
  if (special) st.kspecial = special;
  else if (model.kernel) {
    st.kern = createKernelSheetState(model);
    return st;
  }
  if (model.congruence) {
    st.cong = createCongState(model, model.congruence);
    return st;
  }
  if (model.complex) {
    st.cplx = createComplexState(model, model.complex);
    return st;
  }
  if (model.bounds) {
    st.bnd = createBoundsState(model.bounds);
    return st;
  }
  if (model.geometry) {
    st.geo = createGeoState(model.geometry, [...(model.givens ?? []), ...(model.admitted ?? [])]);
    return st;
  }
  if (model.units) {
    st.units = createUnitsState(model.units, [...(model.givens ?? []), ...(model.admitted ?? [])]);
    return st;
  }
  Object.keys(model.symbols).forEach((n) => {
    const s = model.symbols[n]!;
    if (s.given) {
      st.K[n] = s.value!;
      addFact(st, eq2(sym(n), num(s.value!)), 'given');
    }
  });
  (model.givens || []).forEach((g) => addFact(st, parseText(g), 'given', { shown: true }));
  (model.admitted || []).forEach((g) => addFact(st, parseText(g), 'given', { admitted: true }));
  (model.preds || []).forEach((p) => {
    const setup = SKILLS[p.skill]!.setup;
    if (setup) setup(st, p);
  });
  return st;
}
function addFact(st: ReasonerState, line: Line, source: FactSource, extra?: Partial<Fact>): void {
  st.facts.push(Object.assign({ line, atoms: normalize(lineAtoms(line)), source }, extra || {}));
  updateKnown(st);
}
function cloneState(st: ReasonerState): ReasonerState {
  return {
    model: st.model,
    K: Object.assign({}, st.K),
    facts: st.facts.slice(),
    stated: Object.assign({}, st.stated),
    _cache: null,
  };
}
// une grandeur à valeur unique devient connue dès qu'une ligne l'isole avec un calcul
function updateKnown(st: ReasonerState): void {
  let changed = true;
  while (changed) {
    changed = false;
    st.facts.forEach((f) => {
      if (f.line.dis.length !== 1) return;
      const d = f.line.dis[0]!;
      if (d.sides.length !== 2) return;
      [0, 1].forEach((si) => {
        const s = d.sides[si]!;
        const o = d.sides[1 - si]!;
        const spec = s.k === 's' ? st.model.symbols[s.n!] : undefined;
        if (spec && st.K[s.n!] === undefined && (spec.values || []).length === 1) {
          const v = numVal(o, st.K);
          if (v !== null) {
            st.K[s.n!] = v;
            changed = true;
          }
        }
      });
    });
  }
}
function oracleK(st: ReasonerState): Env {
  const K: Env = Object.assign({}, st.K);
  Object.keys(st.model.symbols).forEach((n) => {
    const s = st.model.symbols[n]!;
    if (K[n] === undefined && (s.values || []).length === 1) K[n] = s.values![0]!;
  });
  return K;
}
function assignments(names: string[], env: Record<string, number[] | undefined>): Env[] {
  let res: Env[] = [{}];
  names.forEach((n) => {
    const nx: Env[] = [];
    res.forEach((a) => {
      (env[n] || [NaN]).forEach((v) => {
        const b = Object.assign({}, a);
        b[n] = v;
        nx.push(b);
      });
    });
    res = nx;
  });
  return res;
}
function holds(d: Eq, a: Env): boolean {
  const v0 = ev(d.sides[0]!, a);
  return d.sides.every((s) => close(ev(s, a), v0));
}
/** vraie selon le modèle de l'exercice */
export function isTrue(line: Line, st: ReasonerState): boolean {
  if (st.kern) return kernelEquivalentToAnswer(st.kern, lineText(line));
  if (st.kgeo) return kgeoHolds(st.kgeo, lineText(line));
  if (st.krec) return krecHolds(st.krec, lineText(line));
  if (st.kprog) return kprogHolds(st.kprog, lineText(line));
  if (st.kmat) return kmatHolds(st.kmat, lineText(line));
  if (st.karith) return karithHolds(st.karith, lineText(line));
  if (st.klim) return klimHolds(st.klim, lineText(line));
  if (st.klc) return klcHolds(st.klc, lineText(line));
  if (st.kint) return kintHolds(st.kint, lineText(line));
  if (st.kode) return kodeHolds(st.kode, lineText(line));
  if (st.kpart) return kpartHolds(st.kpart, lineText(line));
  if (st.kriem) return kriemHolds(st.kriem, lineText(line));
  if (st.kclaims) return kclaimsHolds(st.kclaims, lineText(line));
  if (st.kvar) return kvarHolds(st.kvar, lineText(line));
  if (st.ktab) return ktabHolds(st.ktab, lineText(line));
  if (st.kana) return kanaHolds(st.kana, lineText(line));
  if (st.kfor) return kforHolds(st.kfor, lineText(line));
  if (st.cong) return congIsTrue(st.cong, line);
  if (st.cplx) return complexIsTrue(st.cplx, line);
  if (st.units) return unitsIsTrue(line);
  if (st.geo) return geoEstablished(st.geo, line);
  if (st.bnd) return boundsEstablished(st.bnd, line);
  const model = st.model;
  if (model.envs) {
    return model.envs.every((env) => line.dis.some((d) => holds(d, env)));
  }
  const env: Record<string, number[] | undefined> = {};
  Object.keys(model.symbols).forEach((n) => {
    const s = model.symbols[n]!;
    env[n] = s.given ? [s.value!] : s.values;
  });
  return line.dis.every((d) => {
    const names = Object.keys(symsOf({ k: 'add', c: d.sides }));
    if (d.cmp)
      return assignments(names, env).some((a) =>
        cmpHolds(d.cmp!, ev(d.sides[0]!, a), ev(d.sides[1]!, a)),
      );
    return assignments(names, env).some((a) => holds(d, a));
  });
}
/** une valeur écrite « finie » : un nombre, ou une fraction irréductible (−5/2, 1/4) */
/** Une opération « irrationnelle » (moteur v2) : fonction, racine, π, puissance. */
function hasIrrational(t: Node): boolean {
  if (isFuncNode(t) || t.k === 'sqrt' || t.k === 'pow') return true;
  if (t.k === 's') return isConst(t.n);
  return kids(t).some(hasIrrational);
}
/**
 * Valeur exacte écrite sans décimal (moteur v2) : « ln(5) ÷ 2 », « √2 ÷ 2 »,
 * « 2π » — une réponse finale quand elle n'a pas d'écriture décimale exacte et
 * que plus aucun calcul ne s'y fait.
 */
function closedFormValue(o: Node): number | null {
  if (!numericIn(o, {}) || !hasIrrational(o)) return null;
  const v = ev(o, {});
  if (!isFinite(v) || isExactDecimal(v)) return null;
  const did: unknown[] = [];
  evalInner(o, {}, did);
  return did.length ? null : v;
}
/** Formes exactes déjà écrites : « AB = √32 », « x = ln(5) ÷ 2 » (moteur v2). */
function closedForms(st: ReasonerState): Record<string, Node> {
  const out: Record<string, Node> = {};
  st.facts.forEach((f) => {
    if (f.line.dis.length !== 1) return;
    const d = f.line.dis[0]!;
    if (d.sides.length !== 2) return;
    [0, 1].forEach((si) => {
      const s = d.sides[si]!;
      const o = d.sides[1 - si]!;
      if (s.k === 's' && closedFormValue(o) !== null) out[s.n!] = o;
    });
  });
  return out;
}
function exactValue(o: Node): number | null {
  if (o.k === 'n') return o.v!;
  const closed = closedFormValue(o);
  if (closed !== null) return closed;
  const sg = signSplit(o);
  const t = sg.t;
  if (t.k !== 'div' || t.a!.k !== 'n' || t.b!.k !== 'n') return null;
  const a = t.a!.v!;
  const b = t.b!.v!;
  if (!Number.isInteger(a) || !Number.isInteger(b) || b <= 1 || a === 0) return null;
  if (gcd(Math.abs(a), b) !== 1) return null;
  return (sg.neg ? -1 : 1) * (a / b);
}
export function statedValues(st: ReasonerState): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  if (st.kgeo) {
    out[st.model.goal[0]!] = kgeoStatedValues(st.kgeo);
    return out;
  }
  if (
    st.krec ||
    st.kprog ||
    st.kmat ||
    st.kana ||
    st.karith ||
    st.klim ||
    st.klc ||
    st.kint ||
    st.kode ||
    st.kpart ||
    st.kriem ||
    st.kclaims ||
    st.kvar ||
    st.ktab
  )
    return out;
  if (st.kfor) return kforStatedValues(st.kfor);
  if (st.kern) {
    out[st.kern.x] = kernelStatedValues(st.kern);
    return out;
  }
  if (st.cong) {
    out[st.model.goal[0]!] = congStatedResidues(st.cong);
    return out;
  }
  if (st.cplx) {
    out[st.model.goal[0]!] = complexStatedValues(st.cplx);
    return out;
  }
  if (st.units) {
    out[st.model.goal[0]!] = unitsStatedValues(st.units);
    return out;
  }
  if (st.geo || st.bnd) return out;
  st.facts.forEach((f) => {
    if (f.source !== 'line') return;
    f.line.dis.forEach((d) => {
      if (d.sides.length !== 2) return;
      [0, 1].forEach((si) => {
        const s = d.sides[si]!;
        const o = d.sides[1 - si]!;
        const v = s.k === 's' ? exactValue(o) : null;
        if (v !== null) (out[s.n!] = out[s.n!] || []).push(v);
      });
    });
  });
  return out;
}
function answerLines(st: ReasonerState): Line[] {
  let a = ANSWERS_CACHE.get(st.model);
  if (!a) {
    a = (st.model.answers || []).map((x) => L(x));
    ANSWERS_CACHE.set(st.model, a);
  }
  return a;
}
/** un produit x × x écrit tel quel (une inconnue multipliée par elle-même) */
function selfProduct(t: Node): boolean {
  if (t.k === 'mul') {
    const syms = t.c!.filter((c) => c.k === 's').map((c) => c.n!);
    if (syms.some((n, i) => syms.indexOf(n) !== i)) return true;
  }
  return kids(t).some(selfProduct);
}
function isAnswerLine(st: ReasonerState, E: Line): boolean {
  // développer : x × x + x × 3 n'est pas encore la réponse x² + 3x (c'est l'étape de distributivité)
  if (st.model.aim === 'expand' && E.dis.some((d) => d.sides.some(selfProduct))) return false;
  return answerLines(st).some((a) => !!matchLine(E, a, st.K));
}
export function goalReached(st: ReasonerState): boolean {
  if (st.kfor) return kforGoalReached(st.kfor);
  if (!exactGoalReached(st) && !roundingReady(st)) return false;
  // réponse arrondie (moteur v10) : il reste à écrire « AC ≈ 8,4 »
  return !roundingPending(st);
}
function exactGoalReached(st: ReasonerState): boolean {
  if (st.kern) return kernelGoalReached(st.kern);
  if (st.kgeo) return kgeoGoalReached(st.kgeo);
  if (st.krec) return krecGoalReached(st.krec);
  if (st.kprog) return kprogGoalReached(st.kprog);
  if (st.kmat) return kmatGoalReached(st.kmat);
  if (st.karith) return karithGoalReached(st.karith);
  if (st.klim) return klimGoalReached(st.klim);
  if (st.klc) return klcGoalReached(st.klc);
  if (st.kint) return kintGoalReached(st.kint);
  if (st.kode) return kodeGoalReached(st.kode);
  if (st.kpart) return kpartGoalReached(st.kpart);
  if (st.kriem) return kriemGoalReached(st.kriem);
  if (st.kclaims) return kclaimsGoalReached(st.kclaims);
  if (st.kvar) return kvarGoalReached(st.kvar);
  if (st.ktab) return ktabGoalReached(st.ktab);
  if (st.kana) return kanaGoalReached(st.kana);
  if (st.cong) return congGoalReached(st.cong);
  if (st.cplx) return complexGoalReached(st.cplx);
  if (st.units) return unitsGoalReached(st.units);
  if (st.geo) return geoGoalReached(st.geo);
  if (st.bnd) return boundsGoalReached(st.bnd);
  if (st.model.answers)
    return st.facts.some((f) => f.source === 'line' && isAnswerLine(st, f.line));
  const sv = statedValues(st);
  if (st.model.goalValues) {
    return st.model.goalValues.every((v) =>
      st.model.goal.some((g) => (sv[g] || []).some((w) => close(v, w))),
    );
  }
  return st.model.goal.every((g) =>
    (st.model.symbols[g]!.values || []).every((v) => (sv[g] || []).some((w) => close(v, w))),
  );
}
/** −10 ÷ (−70), 10 ÷ 70 : fraction d'entiers encore simplifiable (gcd > 1 ou dénominateur négatif). */
function reducibleFraction(o: Node): boolean {
  if (o.k !== 'div' || o.a!.k !== 'n' || o.b!.k !== 'n') return false;
  const a = o.a!.v!;
  const b = o.b!.v!;
  // 2 ÷ 0,15 : fraction de décimaux, à écrire en fraction d'entiers (40/3)
  if (b !== 0 && (!Number.isInteger(a) || !Number.isInteger(b)))
    return !isExactDecimal(a / b) && asFraction(a / b) !== null;
  if (!Number.isInteger(a) || !Number.isInteger(b) || b === 0 || a === 0) return false;
  return b < 0 || gcd(Math.abs(a), Math.abs(b)) > 1;
}
/** « x = 10 ÷ 70 » : la grandeur cherchée vaut une fraction encore simplifiable. */
function isolatesGoalByFraction(E: Line, st: ReasonerState): boolean {
  return E.dis.every(
    (d) =>
      d.sides.length === 2 &&
      [0, 1].some(
        (si) =>
          d.sides[si]!.k === 's' &&
          st.model.goal.indexOf(d.sides[si]!.n!) >= 0 &&
          reducibleFraction(d.sides[1 - si]!),
      ),
  );
}
/**
 * La ligne isole la grandeur cherchée sur une expression numérique encore
 * jamais écrite, et le but n'est pas atteint : c'est un pas vers la réponse
 * (valeur fractionnaire ou exacte, que le moteur connaît déjà numériquement).
 */
function towardGoal(st: ReasonerState, E: Line): boolean {
  if (goalReached(st)) return false;
  const t = lineText(E);
  if (st.facts.some((f) => lineText(f.line) === t)) return false;
  if (isolatesGoal(E, st, false)) return true;
  // une grandeur intermédiaire (P(S̄) = 1 − 1/6) dont la valeur finale n'est pas encore écrite
  const stated = statedValues(st);
  return (
    E.dis.length === 1 &&
    E.dis[0]!.sides.length === 2 &&
    [0, 1].some((si) => {
      const s = E.dis[0]!.sides[si]!;
      const o = E.dis[0]!.sides[1 - si]!;
      return (
        s.k === 's' && numericIn(o, {}) && !stated[s.n!] && st.model.symbols[s.n!] !== undefined
      );
    })
  );
}
// la ligne donne une valeur à la grandeur cherchée
function isolatesGoal(E: Line, st: ReasonerState, literal: boolean): boolean {
  return E.dis.every((d) => {
    if (d.sides.length !== 2) return false;
    return [0, 1].some((si) => {
      const s = d.sides[si]!;
      const o = d.sides[1 - si]!;
      return (
        s.k === 's' &&
        st.model.goal.indexOf(s.n!) >= 0 &&
        (literal ? exactValue(o) !== null : numericIn(o, {}))
      );
    });
  });
}

/* ================= préfixes (pour ne rien juger trop tôt) ================= */
function key(atoms: string[]): string {
  return atoms.join(SEP) + SEP;
}
function givenK(st: ReasonerState): Env {
  const g: Env = {};
  Object.keys(st.model.symbols).forEach((n) => {
    const s = st.model.symbols[n]!;
    if (s.given) g[n] = s.value!;
  });
  return g;
}
function variantKeys(line: Line, gk: Env): string[] {
  const lines = [line];
  const s = substLine(line, gk);
  if (lineText(s) !== lineText(line)) lines.push(s);
  const keys: string[] = [];
  lines.forEach((l) => {
    formVariants(normalize(lineAtoms(l))).forEach((v) => keys.push(key(v)));
  });
  return keys;
}
function improveChain(line: Line, st: ReasonerState, out: Line[], depth?: number): void {
  let cur = line;
  for (let i = 0; i < (depth || 4); i++) {
    const m = improve(cur, st);
    if (!m) break;
    out.push(m.line);
    cur = m.line;
  }
}
function cached<T>(st: ReasonerState, name: 'keys' | 'pending', fn: () => T): T {
  const sig = st.facts.length + '|' + (st.stated.hyp ? 1 : 0);
  st._cache = st._cache && st._cache.sig === sig ? st._cache : { sig };
  const c = st._cache as unknown as Record<string, unknown>;
  if (!(name in c)) c[name] = fn();
  return c[name] as T;
}
function validKeys(st: ReasonerState): string[] {
  return cached(st, 'keys', () => computeKeys(st));
}
function computeKeys(st: ReasonerState): string[] {
  const gk = givenK(st);
  let keys: string[] = [];
  const lines: Line[] = [];
  candidates(st, false, true).forEach((c) => {
    lines.push(c.line);
    if (c.kind === 'subst' || c.kind === 'rule') improveChain(c.line, st, lines);
  });
  st.facts.forEach((f) => improveChain(f.line, st, lines));
  checkEntries(st).forEach((c) => lines.push(c.line));
  lines.forEach((l) => {
    keys = keys.concat(variantKeys(l, gk));
  });
  return keys;
}
function prefixInfo(A: string[], keys: string[]): { strict: boolean; len: number } {
  const kA = key(A);
  let best = 0;
  let strict = false;
  keys.forEach((k) => {
    if (k.indexOf(kA) === 0 && k !== kA) strict = true;
    const parts = k.split(SEP);
    let i = 0;
    while (i < A.length && parts[i] === A[i]) i++;
    if (i > best) best = i;
  });
  return { strict, len: best };
}
function trapPrefix(st: ReasonerState, A: string[]): Trap | null {
  const gk = givenK(st);
  const kA = key(A);
  const traps = trapLines(st);
  for (let i = 0; i < traps.length; i++) {
    const ks = variantKeys(traps[i]!.line, gk);
    for (let j = 0; j < ks.length; j++)
      if (ks[j]!.indexOf(kA) === 0 && ks[j] !== kA) return traps[i]!;
  }
  return null;
}

/* ================= justification ================= */
const ORDER: Record<CandKind, number> = {
  fact: 0,
  subst: 1,
  trans: 1,
  transpo: 2,
  bothsides: 2,
  racine: 3,
  case: 4,
  rule: 5,
  ident: 6,
  comb: 6,
};
function justify(st: ReasonerState, J: Line): Justification | null {
  const K = st.K;
  const list = candidates(st, false).filter((c) => !(c.rc && c.rc.needsProof));
  list.forEach((c, i) => {
    c.ord = i;
  });
  list.sort(
    (a, b) => ORDER[a.kind] - ORDER[b.kind] || (b.src || 0) - (a.src || 0) || a.ord! - b.ord!,
  );
  const finalLine = isolatesGoal(J, st, true);
  for (let i = 0; i < list.length; i++) {
    const c = list[i]!;
    if (c.line.dis.length !== J.dis.length) continue;
    const m = matchLine(J, c.line, K);
    // la valeur finale ne sort pas d'un outil d'un seul coup : on écrit d'abord le calcul
    if (m && finalLine && c.kind === 'rule' && m.ev > 0) continue;
    if (m && c.kind !== 'rule' && m.ev > 0) {
      // « 3 × 2x + 3 × 3 » se lit comme 6x + 9 pas encore calculé, mais c'est surtout la
      // transformation d'un outil (faire apparaître le facteur commun), écrite telle quelle
      for (let j = i + 1; j < list.length; j++) {
        const t = list[j]!;
        if (t.kind !== 'rule' || !t.rc?.transform || t.line.dis.length !== J.dis.length) continue;
        const mt = matchLine(J, t.line, K);
        if (mt && mt.ev === 0) return { cand: t, m: mt };
      }
    }
    if (m) return { cand: c, m };
  }
  // développer, réduire : un membre gardé, l'autre réécrit à l'identique
  if (J.dis.length === 1) {
    for (let f = st.facts.length - 1; f >= 0; f--) {
      const fl = st.facts[f]!.line;
      if (fl.dis.length !== 1) continue;
      // la réponse ne s'obtient pas d'un seul bond depuis l'énoncé : il faut au moins une étape,
      // sauf si ce bond est UNE identité remarquable, écrite telle quelle (la reconnaître, c'est le savoir-faire)
      if (st.facts[f]!.source === 'given' && isAnswerLine(st, J)) {
        const jump = identEq(J.dis[0]!, fl.dis[0]!, K, true);
        const reco = jump && identityJump(st, jump.from, jump.to);
        if (jump && reco)
          return {
            cand: { kind: 'ident', line: fl, src: f, from: jump.from, to: jump.to, identity: reco },
            m: { ev: 0, sub: 0 },
          };
        continue;
      }
      const id = identEq(J.dis[0]!, fl.dis[0]!, K);
      if (id)
        return {
          cand: { kind: 'ident', line: fl, src: f, from: id.from, to: id.to },
          m: { ev: 0, sub: 0 },
        };
    }
    if (hasUnknownLine(J, K)) {
      const tr = transitive(st, J.dis[0]!);
      if (tr) return tr;
      const bo = sameOpBoth(st, J.dis[0]!, 0);
      if (bo)
        return {
          cand: { kind: 'bothsides', line: st.facts[bo.src]!.line, src: bo.src, desc: bo.desc },
          m: { ev: 0, sub: 0 },
        };
      // calcul littéral : multiplier les deux membres par une lettre (moteur v16)
      const ls = st.model.envs && st.model.path?.length ? literalSameOp(st, J.dis[0]!) : null;
      if (ls)
        return {
          cand: { kind: 'bothsides', line: st.facts[ls.src]!.line, src: ls.src, desc: ls.desc },
          m: { ev: 0, sub: 0 },
        };
      // système : combinaison de deux équations écrites (moteur v12)
      const lc = linearCombination(st, J.dis[0]!);
      if (lc)
        return {
          cand: { kind: 'comb', line: st.facts[lc.src]!.line, src: lc.src, desc: lc.desc },
          m: { ev: 0, sub: 0 },
        };
    }
  }
  return null;
}
/** une équation à deux membres, avec des inconnues : sa fonction « gauche − droite » */
function eqResidual(eq: Eq): ((env: Env) => number) | null {
  if (eq.sides.length !== 2 || eq.cmp || eq.rels) return null;
  const [l, r] = eq.sides as [Node, Node];
  return (env) => ev(l, env) - ev(r, env);
}
/**
 * Calcul littéral (moteur v16) : gauche − droite de `eq` vaut celle d'une égalité écrite
 * multipliée par k × m, m un monôme d'au plus deux lettres (t, 1/t, t², t × u, t ÷ u) :
 * « on multiplie (ou divise) les deux membres par t ». Vérifié en valeurs, lettres libres.
 */
function literalSameOp(st: ReasonerState, eq: Eq): { src: number; desc: string } | null {
  const r = eqResidual(eq);
  if (!r || eq.sides.some((sd) => !Object.keys(symsOf(sd)).length && false)) return null;
  const names = new Set<string>();
  const facts: { f: (env: Env) => number; fi: number }[] = [];
  eq.sides.forEach((sd) => Object.keys(symsOf(sd)).forEach((n) => names.add(n)));
  st.facts.forEach((fa, fi) => {
    if (fa.line.dis.length !== 1 || fa.source === 'hyp') return;
    const f = eqResidual(fa.line.dis[0]!);
    if (!f) return;
    facts.push({ f, fi });
    fa.line.dis[0]!.sides.forEach((sd) => Object.keys(symsOf(sd)).forEach((n) => names.add(n)));
  });
  const ns = [...names];
  if (!ns.length || !facts.length) return null;
  const SEEDS = [1.37, 2.11, 0.73, 3.29, 1.83, 2.57, 0.91, 4.13];
  const pts = [0, 1, 2, 3, 4].map((k) => {
    const env: Env = {};
    ns.forEach((n, i) => (env[n] = SEEDS[(i + 3 * k) % SEEDS.length]! + 0.07 * k + 0.03 * i));
    return env;
  });
  const rv = pts.map(r);
  if (rv.some((v) => !isFinite(v) || Math.abs(v) < 1e-9)) return null;
  const monos: { m: (e: Env) => number; t: string }[] = [{ m: () => 1, t: '' }];
  ns.forEach((a) => {
    monos.push({ m: (e) => e[a]!, t: a });
    monos.push({ m: (e) => 1 / e[a]!, t: '1 ÷ ' + a });
    monos.push({ m: (e) => e[a]! * e[a]!, t: a + '²' });
    ns.forEach((b) => {
      if (b === a) return;
      monos.push({ m: (e) => e[a]! / e[b]!, t: a + ' ÷ ' + b });
      if (a < b) monos.push({ m: (e) => e[a]! * e[b]!, t: a + ' × ' + b });
    });
  });
  for (let i = facts.length - 1; i >= 0; i--) {
    const fv = pts.map(facts[i]!.f);
    if (fv.some((v) => !isFinite(v) || Math.abs(v) < 1e-9)) continue;
    for (const mo of monos) {
      const ks = pts.map((e, k) => rv[k]! / (fv[k]! * mo.m(e)));
      if (ks.every((k) => isFinite(k) && Math.abs(k - ks[0]!) < 1e-7 * Math.max(1, Math.abs(k)))) {
        const k = ks[0]!;
        if (!mo.t && Math.abs(Math.abs(k) - 1) < 1e-9)
          return { src: facts[i]!.fi, desc: 'passe des termes d’un membre à l’autre' };
        return {
          src: facts[i]!.fi,
          desc:
            'multiplie (ou divise) les deux membres par ' +
            (mo.t
              ? mo.t.startsWith('1 ÷ ')
                ? mo.t.slice(4)
                : mo.t
              : formatNumber(Math.abs(1 / k))),
        };
      }
    }
  }
  return null;
}
/** petit rationnel lisible (dénominateur ≤ 12), sinon null */
function smallRational(v: number): [number, number] | null {
  for (let q = 1; q <= 12; q++) {
    const p = Math.round(v * q);
    if (Math.abs(p) <= 144 && Math.abs(p / q - v) < 1e-9) return [p, q];
  }
  return null;
}
/**
 * Combinaison linéaire (moteur v12) : gauche − droite de `eq` vaut exactement
 * λ × (E₁) + μ × (E₂) pour deux équations écrites, λ et μ non nuls — « on
 * additionne les deux équations », « 3 × (E₁) − 2 × (E₂) ». Vérifié en valeurs.
 */
function linearCombination(st: ReasonerState, eq: Eq): { src: number; desc: string } | null {
  const K = st.K;
  const r = eqResidual(eq);
  // une inconnue isolée (« x = 1 ») ne s'obtient pas d'un bond depuis le système
  if (!r || isFinalLine({ dis: [eq] })) return null;
  const facts: { f: (env: Env) => number; fi: number }[] = [];
  st.facts.forEach((fa, fi) => {
    if (fa.line.dis.length !== 1 || fa.source === 'hyp') return;
    const d = fa.line.dis[0]!;
    if (Object.keys(lineSyms(fa.line)).filter((x) => K[x] === undefined).length < 2) return;
    const f = eqResidual(d);
    if (f) facts.push({ f, fi });
  });
  if (facts.length < 2) return null;
  const unk: Record<string, 1> = {};
  [eq, ...facts.map((x) => st.facts[x.fi]!.line.dis[0]!)].forEach((d) =>
    d.sides.forEach((sd) => symsOf(sd, unk)),
  );
  const names = Object.keys(unk).filter((x) => K[x] === undefined);
  if (names.length < 2) return null;
  const SEEDS = [
    [1.37, -2.11, 0.73, 3.29],
    [-0.83, 1.91, 2.47, -1.53],
    [2.21, 0.59, -1.17, 0.97],
    [-1.63, -0.71, 1.43, 2.83],
    [0.41, 2.69, -2.39, -0.37],
  ];
  const envs = SEEDS.map((sd) => {
    const env: Env = { ...K };
    names.forEach((n, i) => (env[n] = sd[i % sd.length]! + i * 0.17));
    return env;
  });
  // affine en les inconnues : f(milieu) = moyenne (sinon ce n'est pas un système linéaire)
  const mid = (i: number, j: number): Env => {
    const env: Env = { ...K };
    names.forEach((n) => (env[n] = (envs[i]![n]! + envs[j]![n]!) / 2));
    return env;
  };
  const affine = (f: (env: Env) => number) =>
    [
      [0, 1],
      [2, 3],
      [1, 4],
    ].every(([i, j]) => {
      const m = f(mid(i!, j!));
      const avg = (f(envs[i!]!) + f(envs[j!]!)) / 2;
      return isFinite(m) && Math.abs(m - avg) < 1e-7 * Math.max(1, Math.abs(m));
    });
  if (!affine(r)) return null;
  const lin = facts.filter((x) => affine(x.f));
  facts.length = 0;
  facts.push(...lin);
  if (facts.length < 2) return null;
  const rv = envs.map(r);
  if (rv.some((v) => !isFinite(v)) || rv.every((v) => Math.abs(v) < 1e-9)) return null;
  for (let i = 0; i < facts.length; i++) {
    for (let j = i + 1; j < facts.length; j++) {
      const a = envs.map(facts[i]!.f);
      const b = envs.map(facts[j]!.f);
      if (a.concat(b).some((v) => !isFinite(v))) continue;
      const det = a[0]! * b[1]! - a[1]! * b[0]!;
      if (Math.abs(det) < 1e-9) continue;
      const lam = (rv[0]! * b[1]! - rv[1]! * b[0]!) / det;
      const mu = (a[0]! * rv[1]! - a[1]! * rv[0]!) / det;
      if (Math.abs(lam) < 1e-9 || Math.abs(mu) < 1e-9) continue;
      const ok = rv.every(
        (v, k) => Math.abs(lam * a[k]! + mu * b[k]! - v) < 1e-7 * Math.max(1, Math.abs(v)),
      );
      if (!ok) continue;
      const L = smallRational(lam);
      const M = smallRational(mu);
      if (!L || !M) continue;
      const f = formatNumber;
      const desc =
        Math.abs(lam) === 1 && Math.abs(mu) === 1
          ? lam === mu
            ? 'additionne les deux équations'
            : 'soustrait les deux équations'
          : 'combine les deux équations (' + f(lam) + ' × la première, ' + f(mu) + ' × la seconde)';
      return { src: facts[j]!.fi, desc };
    }
  }
  return null;
}
// a = b et b = c donnent a = c
function transitive(st: ReasonerState, eq: Eq): Justification | null {
  const K = st.K;
  if (eq.sides.length !== 2) return null;
  const two: { d: Eq; fi: number }[] = [];
  st.facts.forEach((f, fi) => {
    if (f.line.dis.length === 1 && f.line.dis[0]!.sides.length === 2)
      two.push({ d: f.line.dis[0]!, fi });
  });
  for (let i = 0; i < two.length; i++) {
    for (let j = 0; j < two.length; j++) {
      if (i === j) continue;
      for (let si = 0; si < 2; si++) {
        for (let sj = 0; sj < 2; sj++) {
          const x = two[i]!.d.sides[si]!;
          if (canon(x) !== canon(two[j]!.d.sides[sj]!)) continue;
          const cand = { sides: [two[i]!.d.sides[1 - si]!, two[j]!.d.sides[1 - sj]!] };
          const m = matchEq(eq, cand, K);
          if (m)
            return { cand: { kind: 'trans', line: { dis: [cand] }, src: two[j]!.fi, via: x }, m };
        }
      }
    }
  }
  return null;
}
// a = b obtenu en faisant la même opération des deux côtés d'une ligne établie (× k, + t, ÷ k)
function sameOpBoth(
  st: ReasonerState,
  eq: Eq,
  depth: number,
): { src: number; desc: string } | null {
  const K = st.K;
  if (eq.sides.length !== 2) return null;
  const tries: { eq: Eq; desc: string }[] = [];
  [0, 1].forEach((si) => {
    const a = eq.sides[si]!;
    const b = eq.sides[1 - si]!;
    const vb = numVal(b, K);
    const fa = a.k === 'mul' ? a.c! : [a];
    const fb = b.k === 'mul' ? b.c! : [b];
    if (fa.length > 1) {
      fa.forEach((f, i) => {
        const ra = nary(
          'mul',
          fa.filter((_, q) => q !== i),
        );
        const d = 'Multiplie les deux côtés par ' + textOf(f) + '.';
        if (fb.length > 1) {
          fb.forEach((g, j) => {
            if (canon(f) === canon(g))
              tries.push({
                eq: eqOf(
                  si,
                  ra,
                  nary(
                    'mul',
                    fb.filter((_, q) => q !== j),
                  ),
                ),
                desc: d,
              });
          });
        }
        if (f.k === 'n' && f.v !== 0 && vb !== null && b.k !== 'mul')
          tries.push({ eq: eqOf(si, ra, num(vb / f.v!)), desc: d });
      });
    }
    const ta = a.k === 'add' ? a.c! : [a];
    const tb = b.k === 'add' ? b.c! : [b];
    if (ta.length > 1) {
      ta.forEach((t, i) => {
        const ra = nary(
          'add',
          ta.filter((_, q) => q !== i),
        );
        const sg = signSplit(t);
        const d =
          (sg.neg ? 'Retire ' : 'Ajoute ') +
          textOf(sg.t) +
          (sg.neg ? ' des deux côtés.' : ' aux deux côtés.');
        if (tb.length > 1) {
          tb.forEach((u, j) => {
            if (canon(t) === canon(u))
              tries.push({
                eq: eqOf(
                  si,
                  ra,
                  nary(
                    'add',
                    tb.filter((_, q) => q !== j),
                  ),
                ),
                desc: d,
              });
          });
        }
        if (t.k === 'n' && vb !== null && b.k !== 'add')
          tries.push({ eq: eqOf(si, ra, num(vb - t.v!)), desc: d });
      });
    }
    if (a.k === 'div' && b.k === 'div' && canon(a.b!) === canon(b.b!))
      tries.push({
        eq: eqOf(si, a.a!, b.a!),
        desc: 'Divise les deux côtés par ' + textOf(a.b!) + '.',
      });
  });
  for (let i = 0; i < tries.length; i++) {
    for (let f = st.facts.length - 1; f >= 0; f--) {
      const fl = st.facts[f]!.line;
      if (fl.dis.length === 1 && matchEq(tries[i]!.eq, fl.dis[0]!, K))
        return { src: f, desc: tries[i]!.desc };
    }
    if (depth < 1) {
      const r = sameOpBoth(st, tries[i]!.eq, depth + 1);
      if (r)
        return { src: r.src, desc: r.desc.replace(/\.$/, '') + ', puis ' + lower(tries[i]!.desc) };
    }
  }
  return null;
}
// même chose, en autorisant deux pas (pour reconnaître une erreur)
function justifiedDeep(st: ReasonerState, line: Line): { kind: CandKind; deep?: boolean } | null {
  const K = st.K;
  const deep = candidates(st, true);
  for (let q = 0; q < deep.length; q++) {
    if (deep[q]!.line.dis.length === line.dis.length && matchLine(line, deep[q]!.line, K))
      return deep[q]!;
  }
  if (line.dis.length > 1) {
    const ok = line.dis.every((d) =>
      deep.some((c) => c.line.dis.length === 1 && matchLine({ dis: [d] }, c.line, K)),
    );
    if (ok) return { kind: 'transpo' };
  }
  if (line.dis.length === 1) {
    const refs = st.facts
      .map((f) => f.line)
      .concat(substCandidates(st, definitions(st)).map((c) => c.line));
    for (let i = 0; i < refs.length; i++) {
      if (refs[i]!.dis.length === 1 && identEq(line.dis[0]!, refs[i]!.dis[0]!, K, true))
        return { kind: 'ident' };
    }
  }
  return null;
}
function calcError(st: ReasonerState, J: Line): NonNullable<Lenient['at']> | null {
  const K = st.K;
  const list = candidates(st, false).filter((c) => !(c.rc && c.rc.needsProof));
  for (let i = 0; i < list.length; i++) {
    if (list[i]!.line.dis.length !== J.dis.length) continue;
    const lo: Lenient = { mis: 0, max: 1, at: null };
    const m = matchLine(J, list[i]!.line, K, lo);
    if (m && lo.mis === 1 && lo.at) return lo.at;
  }
  return null;
}
function findTrap(st: ReasonerState, E: Line): { title: string; why?: string; line?: Line } | null {
  const K = st.K;
  const traps = trapLines(st);
  for (let i = 0; i < traps.length; i++) {
    const tr = traps[i]!;
    if (matchLine(E, tr.line, K)) return tr;
    if (
      E.dis.length === 1 &&
      tr.line.dis.length === 1 &&
      hasUnknownLine(E, K) &&
      identEq(E.dis[0]!, tr.line.dis[0]!, K, true)
    )
      return tr;
    if (E.dis.length > 1) {
      for (let j = 0; j < E.dis.length; j++) {
        if (matchLine({ dis: [E.dis[j]!] }, tr.line, K)) return tr;
      }
    }
  }
  if (E.dis.length === 1) {
    const wr = wrongRewrites(st);
    for (let w = 0; w < wr.length; w++) {
      if (identEq(E.dis[0]!, wr[w]!.line.dis[0]!, K, true)) return wr[w]!;
    }
  }
  const muts = mutations(E);
  for (let k = 0; k < muts.length; k++) {
    const mu = muts[k]!;
    if (mu.square) {
      if (!hasUnknownLine(mu.line, K) && isTrue(mu.line, st)) return mu;
      continue;
    }
    if (!isTrue(mu.line, st)) continue;
    const by = justifiedDeep(st, mu.line);
    if (!by) continue;
    if (mu.sign) {
      const moved =
        by.kind === 'transpo' || by.kind === 'bothsides' || (by.kind === 'rule' && by.deep);
      return {
        title: mu.title,
        why: moved
          ? 'Quand un nombre passe de l’autre côté du signe =, il change de signe : on fait la même opération des deux côtés.'
          : 'Un signe s’est inversé en route : reprends le signe de chaque terme, un par un.',
      };
    }
    return mu;
  }
  return null;
}

/* ================= utilité ================= */
function multiset(atoms: string[]): string {
  return atoms.slice().sort().join(SEP);
}
function covered(st: ReasonerState, d: Eq): boolean {
  const K = st.K;
  const m = eqM(d, K);
  return st.facts.some((f) => f.line.dis.some((d2) => eqEquiv(d, d2, K) && m >= eqM(d2, K)));
}
function negParens(t: Node): number {
  return (
    (t.k === 'neg' && t.a!.k === 'add' ? 1 : 0) + kids(t).reduce((s, c) => s + negParens(c), 0)
  );
}
// une réécriture avance si elle laisse moins de parenthèses à développer, puis moins de « −( ) », puis moins d'opérations
function progress(from: Node, to: Node): boolean {
  // un carré de binôme développé avance, même sous un coefficient : 2(x + 1)² → 2(x² + 2x + 1)
  if (binomialSquares(to) < binomialSquares(from) && parens(to) <= parens(from)) return true;
  // à opérations égales, regrouper les termes semblables avance : n + n + 1 → 2n + 1
  const a = [parens(from), negParens(from), ops(from), symLeaves(from)];
  const b = [parens(to), negParens(to), ops(to), symLeaves(to)];
  for (let i = 0; i < 4; i++) {
    if (b[i]! < a[i]!) return true;
    if (b[i]! > a[i]!) return false;
  }
  return false;
}
function symLeaves(t: Node): number {
  return t.k === 's' ? 1 : kids(t).reduce((s, c) => s + symLeaves(c), 0);
}
function isReduced(t: Node): boolean {
  const d: unknown[] = [];
  const K0: Env = {};
  powerCombine(collectDeep(monoNorm(evalInner(t, K0, d), d), d), d);
  return d.length === 0;
}
function termCount(t: Node): number {
  return (t.k === 'add' ? t.c!.length : 0) + kids(t).reduce((s, c) => s + termCount(c), 0);
}
// carrés de binômes (a ± b)² dans une expression
function binomialSquares(t: Node): number {
  return (
    (t.k === 'sq' && t.a!.k === 'add' && t.a!.c!.length === 2 ? 1 : 0) +
    kids(t).reduce((s, c) => s + binomialSquares(c), 0)
  );
}
function identLabel(from: Node, to: Node): string {
  const prod = to.k === 'mul' || (to.k === 'neg' && to.a!.k === 'mul');
  if (from.k === 'add' && prod) return 'Factoriser';
  if (binomialSquares(to) < binomialSquares(from) && parens(to) < parens(from))
    return 'Développer avec une identité';
  if (parens(to) < parens(from)) return isReduced(to) ? 'Développer et réduire' : 'Développer';
  if (negParens(to) < negParens(from))
    return isReduced(to) ? 'Réduire' : 'Supprimer les parenthèses';
  if (termCount(to) === termCount(from) && !isReduced(from)) return 'Calculer';
  return 'Réduire';
}
const IDENT_WHY: Record<string, string> = {
  Factoriser: 'On met le facteur commun en évidence.',
  'Développer avec une identité':
    'On développe avec une identité remarquable : (a + b)² = a² + 2ab + b² et (a − b)² = a² − 2ab + b².',
  Développer: 'On développe : chaque terme d’une parenthèse multiplie chaque terme de l’autre.',
  'Développer et réduire': 'On développe, puis on regroupe les termes semblables.',
  'Supprimer les parenthèses': 'Un « − » devant une parenthèse change le signe de chaque terme.',
  Réduire: 'On regroupe les termes semblables : l’expression garde la même valeur.',
  Calculer: 'On calcule chaque produit.',
};

/* ---- un seul outil de carte, appliqué d'un coup et sans réduire (décision du 09/10/2026) ----
   k(a + b) → ka + kb et (a + b)(c + d) → ac + ad + bc + bd (distributivité, chaque produit calculé) ;
   (a ± b)² → a² ± 2ab + b² et (a + b)(a − b) → a² − b² (développer, outil du calcul littéral) ;
   a² − b² → (a − b)(a + b) et a² ± 2ab + b² → (a ± b)² (factoriser, avec l'outil de la carte).
   Les carrés et le double produit peuvent être calculés (25 = 5², 2 × x × 5 = 10x) ; le reste
   de l'expression ne bouge pas : (2x + 1)² − 25 → (2x − 4)(2x + 6) n'est PAS un seul pas, ni
   (x − 4)(2x + 3) → 2x² − 5x − 12 (développer PUIS regrouper). */
const IDENTITY_WHY: Record<string, string> = {
  'Identité (a + b)²': 'Tu reconnais (a + b)² = a² + 2ab + b².',
  'Identité (a − b)²': 'Tu reconnais (a − b)² = a² − 2ab + b².',
  'Identité (a + b)(a − b)': 'Tu reconnais (a + b)(a − b) = a² − b².',
  'Identité a² − b²': 'Tu reconnais une différence de deux carrés : a² − b² = (a − b)(a + b).',
  Distributivité: 'Le facteur multiplie chaque terme de la parenthèse : k(a + b) = ka + kb.',
  'Double distributivité':
    'Chaque terme de la première parenthèse multiplie chaque terme de la seconde : (a + b)(c + d) = ac + ad + bc + bd.',
};
function termKey(t: Node): string {
  const sp = signSplit(t);
  return (sp.neg ? '−' : '+') + canon(sp.t);
}
function sameTerms(xs: Node[], ys: Node[]): boolean {
  if (xs.length !== ys.length) return false;
  const a = xs.map(termKey).sort();
  const b = ys.map(termKey).sort();
  return a.every((k, i) => k === b[i]);
}
function termsOf(t: Node): Node[] {
  return t.k === 'add' ? t.c! : [t];
}
/** (a − b)(a + b), dans un ordre quelconque, avec a et b donnés */
function isConjugateOf(t: Node, a: Node, b: Node): boolean {
  if (t.k !== 'mul' || t.c!.length !== 2) return false;
  const [f, g] = t.c! as [Node, Node];
  const minus = termsOf(a).concat([neg(b)]);
  const plus = termsOf(a).concat([b]);
  return (
    (sameTerms(termsOf(f), minus) && sameTerms(termsOf(g), plus)) ||
    (sameTerms(termsOf(f), plus) && sameTerms(termsOf(g), minus))
  );
}
/** un produit de deux binômes conjugués (u + v)(u − v) */
function isConjugateProduct(t: Node): boolean {
  if (t.k !== 'mul' || t.c!.length !== 2) return false;
  const [f, g] = t.c! as [Node, Node];
  if (f.k !== 'add' || g.k !== 'add' || f.c!.length !== 2 || g.c!.length !== 2) return false;
  for (let i = 0; i < 2; i++)
    for (let j = 0; j < 2; j++) {
      if (termKey(f.c![i]!) !== termKey(g.c![j]!)) continue;
      const v = f.c![1 - i]!;
      const w = g.c![1 - j]!;
      if (termKey(v) === termKey(neg(w))) return true;
    }
  return false;
}
function identityJump(st: ReasonerState, from: Node, to: Node): string | null {
  // distributivité (outil des cartes « Développer ») : chaque produit calculé, rien de regroupé
  if (to.k === 'add' && parens(to) === 0 && from.k === 'mul') {
    const adds = from.c!.filter((c) => c.k === 'add');
    const flat = from.c!.every((c) => c.k === 'add' || parens(c) === 0);
    if (flat && adds.length === 1 && to.c!.length === adds[0]!.c!.length) return 'Distributivité';
    if (
      flat &&
      adds.length === 2 &&
      from.c!.length === 2 &&
      to.c!.length === adds[0]!.c!.length * adds[1]!.c!.length
    )
      return 'Double distributivité';
  }
  // −(a − b) = −a + b : le signe moins devant la parenthèse multiplie chaque terme par −1
  if (
    to.k === 'add' &&
    parens(to) === 0 &&
    from.k === 'neg' &&
    from.a!.k === 'add' &&
    to.c!.length === from.a!.c!.length
  )
    return 'Distributivité';
  if (to.k === 'add' && parens(to) === 0) {
    if (from.k === 'sq' && from.a!.k === 'add' && from.a!.c!.length === 2 && to.c!.length === 3) {
      const [p, q] = from.a!.c! as [Node, Node];
      return signSplit(p).neg !== signSplit(q).neg ? 'Identité (a − b)²' : 'Identité (a + b)²';
    }
    if (isConjugateProduct(from) && to.c!.length === 2) return 'Identité (a + b)(a − b)';
  }
  const tool = (id: string): boolean => (st.model.preds || []).some((p) => p.skill === id);
  if (tool('differenceCarres')) {
    const ds = diffSquaresOf(from);
    if (ds && isConjugateOf(to, ds.a, ds.b)) return 'Identité a² − b²';
  }
  if (tool('carreParfait')) {
    const ps = perfectSquareOf(from, st.K);
    if (
      ps &&
      to.k === 'sq' &&
      to.a!.k === 'add' &&
      sameTerms(to.a!.c!, termsOf(ps.a).concat([ps.minus ? neg(ps.b) : ps.b]))
    )
      return ps.minus ? 'Identité (a − b)²' : 'Identité (a + b)²';
  }
  return null;
}
function ruleInfo(c: Candidate, m: MatchCost): RuleInfo {
  switch (c.kind) {
    case 'rule': {
      const rc = c.rc!;
      return {
        id: rc.skill!,
        card: rc.card!,
        obligation: !!rc.obligation && !c.isCase,
        label: c.isCase ? rc.caseLabel || rc.label : rc.label,
        hora: c.isCase ? rc.caseHora || 2 : rc.hora,
        why: rc.why + (m.sub ? ' On remplace par les valeurs de l’énoncé.' : ''),
      };
    }
    case 'subst': {
      const def = c.def!;
      if (def.source === 'hyp') {
        return {
          id: 'recurrence',
          card: 'Raisonnement par récurrence',
          label: 'Utiliser l’hypothèse',
          hora: 4,
          why:
            'On remplace ' +
            def.s +
            ' par ' +
            textOf(def.expr) +
            ' : c’est l’hypothèse de récurrence.',
        };
      }
      return {
        id: 'remplacer',
        card: 'Calcul',
        label: 'Remplacer',
        hora: 2,
        why: 'On remplace ' + def.s + ' par ' + textOf(def.expr) + '.',
      };
    }
    case 'ident': {
      if (c.identity) {
        const distrib = /distributivité/i.test(c.identity);
        return {
          id: distrib ? 'distributivite' : 'identite',
          card: distrib ? 'Développer' : 'Identités remarquables',
          label: c.identity,
          hora: distrib ? 4 : 5,
          why: IDENTITY_WHY[c.identity] || 'On reconnaît une identité remarquable.',
        };
      }
      const lbl = identLabel(c.from!, c.to!);
      return { id: 'calcul', card: 'Calcul littéral', label: lbl, hora: 2, why: IDENT_WHY[lbl]! };
    }
    case 'trans':
      return {
        id: 'remplacer',
        card: 'Calcul',
        label: 'Remplacer',
        hora: 2,
        why:
          'Les deux expressions sont égales à ' +
          textOf(c.via!) +
          ' : on remplace l’une par l’autre.',
      };
    case 'transpo':
      return {
        id: 'equation',
        card: 'Résoudre une équation',
        label: 'Même opération des deux côtés',
        hora: 4,
        why: c.desc ? onDit(c.desc) : 'On fait la même opération des deux côtés.',
      };
    case 'comb':
      return {
        id: 'equation',
        card: 'Résoudre un système',
        label: 'Combinaison des équations',
        hora: 4,
        why: c.desc ? onDit(c.desc) : 'On combine les deux équations.',
      };
    case 'bothsides':
      return {
        id: 'equation',
        card: 'Résoudre une équation',
        label: 'Même opération des deux côtés',
        hora: 2,
        why: c.desc ? onDit(c.desc) : 'On fait la même opération des deux côtés.',
      };
    case 'racine':
      if (c.line.dis.length === 2)
        return {
          id: 'racine',
          card: 'Racine carrée',
          label: 'Deux racines carrées',
          hora: 4,
          why: 'Un nombre et son opposé ont le même carré : x² = k (k > 0) donne x = √k ou x = −√k.',
        };
      return {
        id: 'racine',
        card: 'Racine carrée',
        label: 'Racine carrée',
        hora: 3,
        why: 'Une longueur est positive : on prend la racine carrée.',
      };
    case 'case':
      return {
        id: 'cas',
        card: 'Équation produit nul',
        label: 'Un des cas',
        hora: 2,
        why: 'L’un des facteurs est nul.',
      };
    default: {
      const l2 =
        m.sub && m.ev
          ? 'Remplacer et calculer'
          : m.sub
            ? 'Remplacer'
            : m.ev
              ? 'Calculer'
              : 'Réécrire';
      return {
        id: 'calcul',
        card: 'Calcul',
        label: l2,
        hora: 2,
        why: m.sub
          ? 'On remplace les grandeurs connues par leur valeur' +
            (m.ev ? ', puis on calcule.' : '.')
          : 'On calcule.',
      };
    }
  }
}
function usefulness(
  st: ReasonerState,
  E: Line,
  J: Line,
  A: string[],
  just: Justification,
): Verdict {
  const K = st.K;
  const ms = multiset(A);
  const info = ruleInfo(just.cand, just.m);
  const base: Verdict = { kind: 'step', atoms: A, rule: info, line: E };
  // mêmes symboles, mêmes côtés à l'ordre près : « 2x − x = 4 + 1 » n'est pas « 2x − 1 = x + 4 »
  const sides = (l: Line) =>
    l.dis
      .map((d) => d.sides.map(canon).sort().join('='))
      .sort()
      .join('|');
  const same = st.facts.filter(
    (f) => multiset(f.atoms) === ms && lineEquiv(f.line, J, K) && sides(f.line) === sides(J),
  )[0];
  if (same && same.source === 'hyp') {
    if (st.stated.hyp)
      return Object.assign(base, {
        kind: 'dup' as const,
        title: 'Déjà dans ton raisonnement',
        message: 'Tu as déjà posé cette hypothèse.',
      });
    return Object.assign(base, {
      kind: 'step' as const,
      hora: 2,
      final: false,
      hyp: true,
      rule: {
        id: 'recurrence',
        card: 'Raisonnement par récurrence',
        label: 'Hypothèse de récurrence',
        hora: 2,
        why: 'Hérédité : on suppose la formule vraie à un rang n, pour démontrer qu’elle l’est au rang n + 1.',
      },
    });
  }
  if (same) {
    const given = same.source === 'given';
    return Object.assign(base, {
      kind: given ? ('given' as const) : ('dup' as const),
      title: given ? 'C’est dans l’énoncé' : 'Déjà dans ton raisonnement',
      message: given
        ? 'Juste, mais l’énoncé te le donne déjà : ça ne t’apprend rien de nouveau.'
        : 'Tu as déjà écrit cette ligne. Passe à la suite.',
    });
  }
  if (info.obligation) {
    if (established(st, just.cand.line)) {
      return Object.assign(base, {
        kind: 'dup' as const,
        title: 'Déjà fait',
        message: 'L’initialisation est déjà dans ton raisonnement.',
      });
    }
    return Object.assign(base, { kind: 'step' as const, hora: info.hora, final: false });
  }
  const srcFact = just.cand.kind === 'fact' ? st.facts[just.cand.src!] : undefined;
  if (
    srcFact &&
    srcFact.shown &&
    just.m.sub > 0 &&
    !hasUnknownLine(J, K) &&
    goalReached(st) &&
    !st.stated.verif
  ) {
    return Object.assign(base, {
      kind: 'step' as const,
      hora: 1,
      final: false,
      verif: true,
      rule: {
        id: 'verif',
        card: 'Vérifier',
        label: 'Vérification',
        hora: 1,
        why: 'On remplace par la solution trouvée : l’égalité de l’énoncé est bien vraie.',
      },
    });
  }
  if (isAnswerLine(st, E))
    return Object.assign(base, { kind: 'step' as const, final: true, hora: info.hora + 2 });
  const aim = st.model.aim;
  const useless = (title?: string, message?: string): Verdict =>
    Object.assign(base, {
      kind: 'useless' as const,
      title: title || 'Juste, mais ça ne t’avance pas',
      message:
        message ||
        'Cette ligne est vraie et bien justifiée, mais tu as déjà la même information, écrite plus simplement. Cherche ce qui te rapproche de la question.',
    });
  if (just.cand.kind === 'ident') {
    if (aim === 'factor' && /^Développer/.test(info.label)) {
      return useless(
        'Juste, mais tu t’éloignes du but',
        'On te demande une forme factorisée, c’est-à-dire un produit. Développer, c’est faire le chemin inverse.',
      );
    }
    if (aim === 'expand' && info.label === 'Factoriser') {
      return useless(
        'Juste, mais tu t’éloignes du but',
        'On te demande de développer : une somme de termes, pas un produit.',
      );
    }
    if (
      !progress(just.cand.from!, just.cand.to!) &&
      !isAnswerLine(st, E) &&
      // vers la réponse (x = −10 ÷ (−70) → x = 1 ÷ 7) : avance tant que le but n'est pas atteint
      !towardGoal(st, E)
    )
      return useless();
  } else if (just.cand.kind !== 'bothsides' && !(just.cand.rc && just.cand.rc.transform)) {
    let useful = J.dis.some((d) => !covered(st, d));
    // Développer / factoriser (aim) : toutes les formes de l'expression sont équivalentes, et
    // développer allonge avant de réduire. Une ligne calculée depuis une forme avance si elle est
    // plus simple que CETTE forme (pas que la plus simple de toutes, souvent l'énoncé).
    if (!useful && aim && srcFact && J.dis.length === 1 && srcFact.line.dis.length === 1)
      useful = eqM(J.dis[0]!, K) < eqM(srcFact.line.dis[0]!, K);
    // (moteur v9) calculer dans la ligne qu'on vient d'écrire : après l'identité
    // « x² + 16 = x² + 4x + 2² », la ligne « x² + 16 = x² + 4x + 4 » avance (même si
    // l'énoncé « (x + 2)² » est plus court)
    if (
      !useful &&
      srcFact &&
      srcFact.source === 'line' &&
      srcFact === st.facts[st.facts.length - 1] &&
      J.dis.length === 1 &&
      srcFact.line.dis.length === 1 &&
      hasUnknownLine(J, K)
    )
      useful = eqM(J.dis[0]!, K) < eqM(srcFact.line.dis[0]!, K);
    // vers la réponse (I = F(1) − F(0) → I = 1/6 + 0 → I = 1/6) : avance tant que le but n'est pas atteint
    if (!useful && towardGoal(st, E)) useful = true;
    // moteur v16 : corrigé écrit à la main (isoler une variable) — une ligne justifiée avance
    if (!useful && st.model.path?.length && hasUnknownLine(J, K)) useful = true;
    // l'inconnue réunie d'un seul côté (2x − 1 = x + 4 → 2x − x = 4 + 1) : avance, même non réduite
    const from = just.cand.src !== undefined ? st.facts[just.cand.src] : undefined;
    if (
      !useful &&
      just.cand.kind === 'transpo' &&
      from &&
      J.dis.length === 1 &&
      from.line.dis.length === 1
    ) {
      const unkSides = (d: Eq) => d.sides.filter((x) => hasUnknown(x, K)).length;
      useful = unkSides(J.dis[0]!) < unkSides(from.line.dis[0]!);
      // système : isoler une inconnue (0,2x = 0,3y → x = 1,5y) pour la substituer ensuite
      const fd = from.line.dis[0]!;
      const lonely = (d: Eq) => d.sides.some((x) => x.k === 's' && K[x.n!] === undefined);
      const unk = Object.keys(symsOf({ k: 'add', c: fd.sides })).filter((x) => K[x] === undefined);
      if (!useful && unk.length === 2 && !lonely(fd) && lonely(J.dis[0]!)) useful = true;
    }
    if (!useful) return useless();
  }
  const fin = isolatesGoal(E, st, true) || isAnswerLine(st, E);
  return Object.assign(base, {
    kind: 'step' as const,
    final: fin,
    hora: info.hora + (fin ? 2 : 0),
  });
}
function pendingSet(st: ReasonerState): Record<string, 1> {
  return cached(st, 'pending', () => computePending(st));
}
function computePending(st: ReasonerState): Record<string, 1> {
  const set: Record<string, 1> = {};
  const OK = oracleK(st);
  function collect(t: Node): void {
    if (t.k !== 'n' && t.k !== 's' && !hasUnknown(t, OK)) {
      set[canon(t)] = 1;
      set[canon(subst(t, OK))] = 1;
    }
    kids(t).forEach(collect);
  }
  const lines = candidates(st, false, true).map((c) => c.line);
  st.facts.forEach((f) => improveChain(f.line, st, lines));
  lines.forEach((l) => l.dis.forEach((d) => d.sides.forEach(collect)));
  return set;
}
function calcVerdict(st: ReasonerState, E: Line, A: string[]): Verdict {
  const ms = multiset(A);
  const same = st.facts.filter((f) => multiset(f.atoms) === ms)[0];
  if (same) {
    const given = same.source === 'given';
    return {
      kind: given ? 'given' : 'dup',
      atoms: A,
      title: given ? 'C’est dans l’énoncé' : 'Déjà dans ton raisonnement',
      message: given ? 'Juste, mais l’énoncé te le donne déjà.' : 'Tu as déjà écrit ce calcul.',
    };
  }
  const sides: Node[] = [];
  E.dis.forEach((d) => d.sides.forEach((s) => sides.push(s)));
  const computed = sides.filter((s) => s.k !== 'n' && s.k !== 's');
  if (!computed.length) {
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais évident',
      message: 'C’est vrai, mais ça ne t’apprend rien : il n’y a rien à calculer.',
    };
  }
  const pend = pendingSet(st);
  const OK = oracleK(st);
  const hit = computed.some((s) => pend[canon(s)] || pend[canon(subst(s, OK))]);
  if (!hit) {
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais ça ne sert pas ici',
      message:
        'Ton calcul est juste, mais il n’apparaît nulle part dans le raisonnement de cet exercice.',
    };
  }
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: 1,
    final: false,
    rule: {
      id: 'calcul',
      card: 'Calcul',
      label: 'Calcul',
      hora: 1,
      why: 'Un calcul intermédiaire dont tu as besoin.',
    },
  };
}

/* ================= le juge ================= */
function chipAt(src: number[], i: number, n: number): number {
  return src[i] !== undefined ? src[i]! : n - 1;
}
function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}
// « Retire 5 des deux côtés. » → « On retire 5 des deux côtés. »
function onDit(s: string): string {
  return 'On ' + lower(s);
}

export interface JudgeOptions {
  /** l'élève demande la vérification : une ligne non justifiée ou fausse est jugée tout de suite */
  verify?: boolean;
}

/**
 * Modes spéciaux passés au noyau (moteur v34) : le juge du mode (unités, congruences,
 * complexes, encadrements) lit et calcule la ligne ; le noyau y applique ses règles. Une
 * ligne JUSTE que le mode ne sait pas justifier en un pas (« 5²⁰²⁶ ≡ 5 [11] » d'un bond)
 * reste sur la feuille, sans HORA, et « Tu tiens la réponse ! » si c'est la réponse (D7) ;
 * une ligne juste qui ne part pas de la question se garde aussi (« Calcul juste »).
 */
const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
/** atomes « lâches » : « 3¹⁰⁰ » et « 3 ^ 100 » s'écrivent pareil ; l'espace fine des unités tombe */
function looseTokens(atoms: string[], units = false): string[] {
  const out: string[] = [];
  for (const raw of atoms) {
    const a = raw.replace(/[\u2009\u202F\u00A0]/g, ' ').trim();
    if (!a) continue;
    const m = /^(.*?)([⁰¹²³⁴⁵⁶⁷⁸⁹]+)$/.exec(a);
    // une unité garde son exposant (« m³ »)
    if (m && !(units && /[a-zA-Zµ]$/.test(m[1]!))) {
      if (m[1]) out.push(m[1]);
      out.push('^', [...m[2]!].map((c) => String(SUP_DIGITS.indexOf(c))).join(''));
    } else out.push(...a.split(/\s+/));
  }
  return out;
}
/** les lignes connues d'un mode spécial (corrigé, réponse), en atomes lâches */
function specialKnownLines(st: ReasonerState): string[][] {
  const k = st.model.kernel;
  if (!k) return [];
  const texts = [...(k.path ?? []), k.answer];
  return texts.map((t) => {
    if (st.units) return looseTokens([t], true);
    try {
      return looseTokens(normalize(lex(t)));
    } catch {
      return [];
    }
  });
}

function specialKernelJudge(st: ReasonerState, chips: Chip[], opts?: JudgeOptions): Verdict {
  const v = specialKernelVerdict(st, chips, opts);
  // une ligne qui commence comme une ligne du corrigé (« 3² = 9 » avant « ≡ 1 [8] ») :
  // on attend la suite au lieu de la valider tout de suite
  if (!opts?.verify && v.kind !== 'empty') {
    const A = looseTokens(
      chips.flatMap((c) => c.atoms),
      !!st.units,
    );
    const prefix =
      A.length > 0 &&
      specialKnownLines(st).some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
    if (prefix) {
      if (v.kind === 'step') return { ...v, kind: 'ready' };
      if (v.kind !== 'ready')
        return { kind: 'building', atoms: v.atoms, complete: v.kind !== 'open' };
    }
  }
  return v;
}

function specialKernelVerdict(st: ReasonerState, chips: Chip[], opts?: JudgeOptions): Verdict {
  const full = { verify: true };
  const v = (
    st.cong
      ? congJudge(st.cong, st.model, chips, full)
      : st.cplx
        ? complexJudge(st.cplx, st.model, chips, full)
        : st.units
          ? unitsJudge(st.units, st.model.traps ?? [], chips, full)
          : boundsJudge(st.bnd!, st.model.traps ?? [], chips, full)
  ) as Verdict & { keep?: { line: Line; kind: 'jump' | 'aside' } };
  // en cours de frappe, une ligne non justifiée attend la vérification (comme avant)
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: v.atoms, complete: true };
  if (v.kind === 'unjustified' && v.keep) {
    const line = v.keep.line;
    if (v.keep.kind === 'aside')
      return {
        kind: 'useless',
        atoms: v.atoms,
        line,
        title: 'Calcul juste',
        message:
          'Ce calcul est juste, mais il ne part pas de la question. Garde-le s’il t’aide : il ne rapporte pas de HORA.',
      };
    const final = st.cong
      ? congWouldReach(st.cong, line)
      : st.cplx
        ? complexWouldReach(st.cplx, line)
        : !!st.bnd && !boundsGoalReached(st.bnd);
    return {
      kind: 'useless',
      atoms: v.atoms,
      line,
      jump: true,
      final,
      title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
      message: final
        ? 'C’est la bonne réponse. Détaille ton raisonnement, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une étape par ligne, pour gagner des HORA. Tu peux la garder sans HORA.',
      ...(st.bnd ? { bnd: { low: false, high: false, done: true } } : {}),
    } as Verdict;
  }
  return v;
}
/**
 * Tableau de signes (moteur v42) : la feuille le propose-t-elle ? Une inéquation du noyau
 * (méthode « factor »), sans système ni hypothèse sur l'inconnue.
 */
export function signTableOffered(model: ScratchModel): boolean {
  const k = model.kernel;
  // étude de fonction (lot 6.20) : le tableau de variations ; tableau à remplir (lot 6.21)
  if (k?.plugin === 'variations' || k?.plugin === 'tables') return true;
  if (!k || k.plugin !== 'algebra' || k.method !== 'factor' || k.system) return false;
  if (k.hypotheses?.length || k.domain) return false;
  return /[<>≤≥]/.test(k.start) || (k.path ?? []).some((t) => t.startsWith('⊞'));
}

/**
 * Tableau de signes (moteur v42) : les lignes et les valeurs que l'éditeur propose pour la
 * ligne étudiée ; null si la feuille n'en a pas (ou pas encore : rien à étudier).
 */
export function signTableSkeleton(st: ReasonerState): SignTableSkeleton | null {
  if (st.kvar) return kvarTableSkeleton(st.kvar);
  return st.kern ? kernelSignTableSkeleton(st.kern) : null;
}

/**
 * Tableau à remplir (lot 6.21) : les lignes, les cases données et à remplir que l'éditeur
 * propose ; null hors de ces feuilles.
 */
export function gridTableSkeleton(st: ReasonerState): GridSkeleton | null {
  return st.ktab ? ktabSkeleton(st.ktab) : null;
}

/** Juge la ligne en cours (des bouts). Pur : ne modifie pas l'état (sauf ses caches). */
export function judge(st: ReasonerState, chips: Chip[], opts?: JudgeOptions): Verdict {
  if (st.kern) return kernelJudge(st.kern, chips, opts) as Verdict;
  if (st.kgeo) return kgeoJudge(st.kgeo, chips, opts) as Verdict;
  if (st.krec) return krecJudge(st.krec, chips, opts) as Verdict;
  if (st.kprog) return kprogJudge(st.kprog, chips, opts) as unknown as Verdict;
  if (st.kmat) return kmatJudge(st.kmat, chips, opts) as unknown as Verdict;
  if (st.karith) return karithJudge(st.karith, chips, opts) as unknown as Verdict;
  if (st.klim) return klimJudge(st.klim, chips, opts) as unknown as Verdict;
  if (st.klc) return klcJudge(st.klc, chips, opts) as unknown as Verdict;
  if (st.kint) return kintJudge(st.kint, chips, opts) as unknown as Verdict;
  if (st.kode) return kodeJudge(st.kode, chips, opts) as unknown as Verdict;
  if (st.kpart) return kpartJudge(st.kpart, chips, opts) as unknown as Verdict;
  if (st.kriem) return kriemJudge(st.kriem, chips, opts) as unknown as Verdict;
  if (st.kclaims) return kclaimsJudge(st.kclaims, chips, opts) as unknown as Verdict;
  if (st.kvar) return kvarJudge(st.kvar, chips, opts) as unknown as Verdict;
  if (st.ktab) return ktabJudge(st.ktab, chips, opts) as unknown as Verdict;
  if (st.kana) return kanaJudge(st.kana, chips, opts) as unknown as Verdict;
  if (st.kfor) return kforJudge(st.kfor, chips, opts) as unknown as Verdict;
  if (st.kspecial) return specialKernelJudge(st, chips, opts);
  if (st.cong) return congJudge(st.cong, st.model, chips, opts) as Verdict;
  if (st.cplx) return complexJudge(st.cplx, st.model, chips, opts) as Verdict;
  if (st.units) return unitsJudge(st.units, st.model.traps ?? [], chips, opts) as Verdict;
  if (st.geo) return geoJudge(st.geo, st.model.traps ?? [], chips, opts) as Verdict;
  if (st.bnd) return boundsJudge(st.bnd, st.model.traps ?? [], chips, opts) as Verdict;
  const o = opts || {};
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const syn = checkSyntax(A);
  if (!syn.ok)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: chipAt(norm.src, syn.index, chips.length),
    };
  const vp = prefixInfo(A, validKeys(st));
  const bad = vp.len < A.length ? chipAt(norm.src, vp.len, chips.length) : -1;
  if (!syn.complete || !syn.hasEq) {
    const tp = !vp.strict && trapPrefix(st, A);
    if (tp)
      return {
        kind: 'trap',
        atoms: A,
        title: 'Piège : ' + lower(tp.title),
        message: tp.why,
        trap: tp,
        badChip: bad,
      };
    return { kind: vp.strict ? 'building' : 'open', atoms: A, complete: false };
  }
  let E: Line;
  try {
    E = parseLine(A);
  } catch {
    return { kind: 'open', atoms: A, complete: false };
  }
  const cmpEq = E.dis.length === 1 && E.dis[0]!.cmp ? E.dis[0]! : null;
  const v = cmpEq ? compareVerdict(st, cmpEq, E, A) : evaluate(st, E, A, bad);
  if (vp.strict && !o.verify) {
    if (v.kind === 'step') return Object.assign(v, { kind: 'ready' as const });
    return { kind: 'building', atoms: A, complete: true };
  }
  // la ligne n'est peut-être pas finie : on attend que l'élève demande la vérification
  if (!o.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

/* ================= comparaisons (moteur v5) ================= */
/** clé d'une comparaison, écrite dans le sens « > » / « ≥ » (« 0 < Δ » ≡ « Δ > 0 ») */
function cmpKey(d: Eq): string {
  const flip = d.cmp === '<' || d.cmp === '≤';
  const rel = d.cmp === '<' ? '>' : d.cmp === '≤' ? '≥' : d.cmp!;
  const [a, b] = flip ? [d.sides[1]!, d.sides[0]!] : [d.sides[0]!, d.sides[1]!];
  return canon(a) + ' ' + rel + ' ' + canon(b);
}
interface CheckEntry {
  spec: CheckSpec;
  line: Line;
  key: string;
}
function checkEntries(st: ReasonerState): CheckEntry[] {
  return (st.model.checks ?? []).flatMap((c) => {
    try {
      const line = parseText(c.line);
      const d = line.dis[0];
      return d && d.cmp ? [{ spec: c, line, key: cmpKey(d) }] : [];
    } catch {
      return [];
    }
  });
}
function checkDone(st: ReasonerState, key: string): boolean {
  return (st.stated.checks ?? []).includes(key);
}
/** la grandeur est donnée, ou une ligne écrite donne déjà sa valeur finie (« Δ = 1 ») */
function writtenValue(st: ReasonerState, n: string): boolean {
  if (st.model.symbols[n]?.given) return true;
  return st.facts.some(
    (f) =>
      isFinalLine(f.line) &&
      f.line.dis.some((d) => d.sides.some((sd) => sd.k === 's' && sd.n === n)),
  );
}
/** comparaison décisive suivante : ses grandeurs sont écrites avec leur valeur, pas encore écrite */
function nextCheck(st: ReasonerState): CheckEntry | null {
  return (
    checkEntries(st).find(
      (c) =>
        !checkDone(st, c.key) &&
        !hasUnknownLine(c.line, st.K) &&
        Object.keys(lineSyms(c.line)).every((n) => isConst(n) || writtenValue(st, n)),
    ) ?? null
  );
}
/* ================= réponses arrondies (moteur v10) ================= */
function roundingTarget(st: ReasonerState): string {
  return st.model.rounding?.symbol ?? st.model.goal[0]!;
}
function roundingWords(p: number): string {
  return p === 0
    ? 'à l’unité'
    : p === 1
      ? 'au dixième'
      : p === 2
        ? 'au centième'
        : p === 3
          ? 'au millième'
          : 'à ' + p + ' chiffres après la virgule';
}
/** valeurs exactes de la grandeur arrondie (celles du modèle) */
function roundingValues(st: ReasonerState): number[] {
  return st.model.symbols[roundingTarget(st)]?.values ?? [];
}
/** reste-t-il à écrire l'arrondi ? (pas si la valeur exacte a déjà assez peu de chiffres) */
function roundingPending(st: ReasonerState): boolean {
  const r = st.model.rounding;
  if (!r) return false;
  const vals = roundingValues(st);
  if (!vals.length || vals.every((v) => decimalPlaces(v) <= r.places)) return false;
  const pre = canon(sym(roundingTarget(st))) + ' ≈ ';
  return !(st.stated.checks ?? []).some((k) => k.startsWith(pre));
}
/**
 * Réponse arrondie : la grandeur est écrite égale à un calcul sans lettre
 * (« h = 200 ÷ 28,26 ») dont la valeur est la bonne — assez pour arrondir,
 * même si ce quotient n'a ni écriture décimale ni fraction simple.
 */
function roundingReady(st: ReasonerState): boolean {
  if (!st.model.rounding) return false;
  const target = roundingTarget(st);
  const vals = roundingValues(st);
  return st.facts.some(
    (f) =>
      f.line.dis.length === 1 &&
      f.line.dis[0]!.sides.length === 2 &&
      !f.line.dis[0]!.cmp &&
      [0, 1].some((si) => {
        const d = f.line.dis[0]!;
        const s0 = d.sides[si]!;
        const o = d.sides[1 - si]!;
        if (s0.k !== 's' || s0.n !== target || !numericIn(o, {})) return false;
        const v = ev(o, {});
        if (!vals.some((w) => close(v, w))) return false;
        // un calcul encore à faire (6² → 36) passe avant l'arrondi
        const did: unknown[] = [];
        evalInner(o, {}, did);
        return did.length === 0;
      }),
  );
}
/** la ligne d'arrondi du corrigé : « AC ≈ 8,4 » */
function roundingLine(st: ReasonerState): Line | null {
  const r = st.model.rounding;
  if (!r || !roundingPending(st)) return null;
  const v = roundingValues(st)[0];
  if (v === undefined) return null;
  return { dis: [{ sides: [sym(roundingTarget(st)), num(roundTo(v, r.places))], cmp: '≈' }] };
}
function approxVerdict(st: ReasonerState, d: Eq, A: string[], a: number, b: number): Verdict {
  const r = st.model.rounding;
  const target = roundingTarget(st);
  const left = d.sides[0]!;
  const isTarget = !!r && left.k === 's' && left.n === target;
  if (!isTarget) {
    if (cmpHolds('≈', a, b))
      return {
        kind: 'useless',
        atoms: A,
        title: 'Juste, mais pas utile ici',
        message: r
          ? 'C’est l’arrondi de ' + target + ' qu’on te demande, une fois sa valeur calculée.'
          : 'L’exercice demande une valeur exacte : écris « = » avec la valeur exacte.',
      };
    return {
      kind: 'false',
      atoms: A,
      title: 'Ce n’est pas une bonne valeur approchée',
      message: 'Regarde les valeurs que tu as calculées.',
    };
  }
  const p = r!.places;
  const words = roundingWords(p);
  const key = cmpKey(d);
  if ((st.stated.checks ?? []).includes(key))
    return {
      kind: 'dup',
      atoms: A,
      title: 'Déjà écrit',
      message: 'Tu as déjà arrondi ' + target + '.',
    };
  if (decimalPlaces(b) > p) {
    if (cmpHolds('≈', a, b))
      return {
        kind: 'useless',
        atoms: A,
        title: 'Garde moins de chiffres',
        message:
          'On demande ' +
          target +
          ' arrondi ' +
          words +
          (p === 0
            ? ' : aucun chiffre après la virgule.'
            : ' : ' + p + ' chiffre' + (p > 1 ? 's' : '') + ' après la virgule.'),
      };
    return {
      kind: 'false',
      atoms: A,
      title: 'Valeur approchée fausse',
      message: target + ' vaut environ ' + formatNumber(roundTo(a, p + 2)) + '.',
    };
  }
  if (close(b, roundTo(a, p))) {
    const hora = r!.hora ?? 2;
    return {
      kind: 'step',
      atoms: A,
      line: { dis: [d] },
      hora,
      final: true,
      rule: {
        id: 'arrondi',
        card: '',
        label: 'Arrondir',
        hora,
        why:
          'On arrondit ' +
          words +
          ' : on regarde le chiffre suivant (5 ou plus : on arrondit au-dessus).',
      },
    };
  }
  if (close(b, truncTo(a, p)))
    return {
      kind: 'false',
      atoms: A,
      title: 'Arrondir, ce n’est pas couper',
      message:
        'Regarde le chiffre juste après : s’il vaut 5 ou plus, le dernier chiffre gardé augmente de 1.',
    };
  return {
    kind: 'false',
    atoms: A,
    title: 'Valeur approchée fausse',
    message: target + ' vaut environ ' + formatNumber(roundTo(a, p + 2)) + '.',
  };
}
function compareVerdict(st: ReasonerState, d: Eq, E: Line, A: string[]): Verdict {
  const K = st.K;
  const unknown = Object.keys(symsOf({ k: 'add', c: d.sides })).filter(
    (n) => K[n] === undefined && !isConst(n),
  );
  if (unknown.length)
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Calcule d’abord ' + unknown.join(', '),
      message:
        'Pour comparer, il faut connaître la valeur de ' +
        unknown.join(', ') +
        ' : calcule-la sur une ligne avant.',
    };
  const a = numVal(d.sides[0]!, K);
  const b = numVal(d.sides[1]!, K);
  if (d.cmp === '≈' && a !== null && b !== null) return approxVerdict(st, d, A, a, b);
  if (a === null || b === null || !cmpHolds(d.cmp!, a, b)) {
    return {
      kind: 'false',
      atoms: A,
      title: 'Cette comparaison est fausse',
      message:
        a !== null && d.sides[0]!.k === 's'
          ? d.sides[0]!.n + ' vaut ' + formatNumber(a) + '.'
          : 'Regarde les valeurs que tu as calculées.',
    };
  }
  const key = cmpKey(d);
  const entry = checkEntries(st).find((c) => c.key === key);
  if (!entry)
    return {
      kind: 'useless',
      atoms: A,
      title: 'Juste, mais à côté',
      message: 'Cette comparaison est vraie, mais elle ne décide rien pour cette question.',
      line: E,
    };
  if (checkDone(st, key))
    return { kind: 'dup', atoms: A, title: 'Déjà écrit', message: 'Tu as déjà cette comparaison.' };
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: entry.spec.hora ?? 2,
    rule: {
      id: 'compare',
      card: '',
      label: entry.spec.label,
      hora: entry.spec.hora ?? 2,
      why: entry.spec.why,
    },
  };
}

function evaluate(st: ReasonerState, E: Line, A: string[], bad: number): Verdict {
  const K = st.K;
  if (E.dis.length === 1 && E.dis[0]!.sides.length > 2 && hasUnknownLine(E, K))
    return chain(st, E, A);
  const J: Line = {
    dis: E.dis.map((d) =>
      d.sides.length > 2 ? { sides: [d.sides[0]!, d.sides[d.sides.length - 1]!] } : d,
    ),
  };
  if (isTrue(E, st)) {
    const just = justify(st, J);
    // moteur v15 : depuis l'énoncé, l'opération s'écrit avant son résultat
    if (just && st.model.stepwise && jumpsFromGiven(st, E, just))
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: 'Écris d’abord l’opération',
        message:
          'Ta valeur est juste, mais montre comment tu l’obtiens : écris d’abord l’opération (par exemple « x = 12 ÷ 3 »), puis son résultat.',
      };
    if (just) return usefulness(st, E, J, A, just);
    if (!hasUnknownLine(E, K)) return calcVerdict(st, E, A);
    const u = unjustified(st, E, A);
    // Calcul littéral (`envs`) : la ligne est une égalité vraie POUR TOUT x.
    // Juste, donc jamais refusée : l'élève peut la garder, sans HORA (elle
    // n'est reliée à aucune de ses lignes ni à aucun outil).
    // (sauf récurrence : la conclusion s'écrit APRÈS l'hérédité, jamais avant)
    if (st.model.envs && !(st.model.preds ?? []).some((p) => p.skill === 'recurrence'))
      return identityKept(E, A, u);
    return u;
  }
  return falseVerdict(st, E, J, A, bad);
}

/**
 * La réponse (« x = 4 ») tirée d'une égalité de l'énoncé (« 3x = 12 ») en faisant
 * l'opération ET le calcul d'un coup.
 */
function jumpsFromGiven(st: ReasonerState, E: Line, just: Justification): boolean {
  const goalLine =
    isAnswerLine(st, E) ||
    (isFinalLine(E) &&
      E.dis.every((d) => d.sides.some((sd) => sd.k === 's' && st.model.goal.includes(sd.n!))));
  // calcul littéral : l'opération sur les deux membres faite ET simplifiée d'un coup
  const literalJump =
    just.cand.kind === 'bothsides' &&
    /^(multiplie \(ou divise\)|passe des termes)/.test(just.cand.desc ?? '');
  if (!goalLine || (just.m.ev === 0 && !literalJump)) return false;
  const src = just.cand.src;
  return src !== undefined && st.facts[src]?.source === 'given';
}
/** Égalité vraie pour tout x mais non justifiée : acceptée, gardée sans HORA. */
function identityKept(E: Line, A: string[], u: Verdict): Verdict {
  const generic = u.title === 'D’où ça vient ?';
  return {
    kind: 'useless',
    atoms: A,
    line: E,
    title: 'Juste, mais sans HORA',
    message:
      'Ta ligne est juste. Je ne sais pas la relier à tes lignes précédentes : garde-la si elle t’aide.' +
      (generic ? '' : ' Conseil : ' + lowerFirstLetter(u.message ?? '')),
  };
}
function lowerFirstLetter(t: string): string {
  return t.charAt(0).toLowerCase() + t.slice(1);
}

/**
 * Calcul littéral : une valeur de x qui contredit la ligne (« pour x = 1,
 * A vaut −8 mais −2x − 3 vaut −5 »). Rien pour « x = 3 » (une valeur isolée
 * d'une variable n'est pas une identité) ni pour une ligne à plusieurs « ou ».
 */
function counterExample(st: ReasonerState, E: Line): string | null {
  const envs = st.model.envs;
  if (!envs?.length || E.dis.length !== 1) return null;
  const d = E.dis[0]!;
  if (d.sides.length !== 2) return null;
  const free = (n: string) => st.model.symbols[n]?.kind === 'free';
  if (d.sides.some((t) => t.k === 's' && free(t.n!))) return null;
  const vars = Object.keys(symsOf({ k: 'add', c: d.sides })).filter(free);
  const score = (env: Env) =>
    vars.reduce(
      (acc, n) =>
        acc +
        Math.abs(env[n] ?? 0) +
        (Number.isInteger(env[n]) ? 0 : 10) +
        (env[n] === 0 ? 0.5 : 0),
      0,
    );
  const bad = envs
    .filter((env) => {
      const vals = d.sides.map((t) => ev(t, env));
      return vals.every((v) => isFinite(v)) && !close(vals[0]!, vals[1]!);
    })
    .sort((a, b) => score(a) - score(b))[0];
  if (!bad) return null;
  const at = vars.length
    ? 'pour ' + vars.map((n) => n + ' = ' + formatNumber(bad[n]!)).join(' et ') + ', '
    : '';
  const [l, r] = d.sides.map((t) => textOf(t) + ' vaut ' + formatNumber(ev(t, bad)));
  return at + l + ' mais ' + r + '.';
}

function falseVerdict(st: ReasonerState, E: Line, J: Line, A: string[], bad: number): Verdict {
  const K = st.K;
  const trap = findTrap(st, J);
  if (trap) {
    return {
      kind: 'trap',
      atoms: A,
      title: 'Piège : ' + lower(trap.title),
      message: trap.why,
      trap: { line: trap.line || J, title: trap.title, why: trap.why || '' },
      badChip: bad,
    };
  }
  // réponse arrondie (moteur v10) : « AC = 8,4 » au lieu de « AC ≈ 8,4 »
  const r = st.model.rounding;
  const d0 = E.dis.length === 1 ? E.dis[0]! : null;
  if (r && d0 && d0.sides.length === 2 && !d0.cmp) {
    const target = roundingTarget(st);
    const si = d0.sides.findIndex((x) => x.k === 's' && x.n === target);
    const other = si >= 0 ? numVal(d0.sides[1 - si]!, K) : null;
    if (other !== null && roundingValues(st).some((v) => cmpHolds('≈', v, other)))
      return {
        kind: 'false',
        atoms: A,
        title: 'C’est une valeur approchée',
        message:
          target +
          ' ne vaut pas exactement ' +
          formatNumber(other) +
          ' : écris « ' +
          target +
          ' ≈ ' +
          formatNumber(other) +
          ' ».',
        badChip: bad,
      };
  }
  if (!hasUnknownLine(E, K)) {
    const d0 = E.dis[0]!;
    const vals = d0.sides.map((s) => numVal(s, K));
    if (vals.some((x) => x === null)) {
      return {
        kind: 'false',
        atoms: A,
        title: 'Calcul impossible',
        message: 'Ce calcul est impossible (division par zéro ou racine d’un nombre négatif).',
      };
    }
    return {
      kind: 'false',
      atoms: A,
      title: 'Calcul faux',
      message:
        'Ce calcul est faux : ' +
        d0.sides.map((s, i) => textOf(s) + ' vaut ' + formatNumber(vals[i]!)).join(', ') +
        '.',
    };
  }
  const lo = calcError(st, J);
  if (lo) {
    return {
      kind: 'false',
      atoms: A,
      title: 'Presque !',
      message:
        'Erreur de calcul : à la place de ' +
        formatNumber(lo.wrote) +
        ', il fallait ' +
        formatNumber(lo.expected) +
        ' (c’est ' +
        textOf(lo.f) +
        ').',
    };
  }
  const u = unjustified(st, E, A);
  // calcul littéral : la ligne est fausse, on le dit avec une valeur qui le
  // montre — sauf si un outil a un message plus précis (« D’où vient ce carré ? »)
  const ce = u.title === 'D’où ça vient ?' ? counterExample(st, E) : null;
  if (ce) {
    return {
      kind: 'false',
      atoms: A,
      title: 'Ce n’est pas égal',
      message: 'Ta ligne est fausse : ' + ce,
      badChip: bad,
    };
  }
  return u;
}

function unjustified(st: ReasonerState, E: Line, A: string[]): Verdict {
  let special: { title: string; message: string } | null = null;
  Object.keys(SKILLS).forEach((id) => {
    const u = SKILLS[id]!.unjustified;
    if (!special && u) special = u(st, E);
  });
  if (special) {
    const sp = special as { title: string; message: string };
    return { kind: 'unjustified', atoms: A, title: sp.title, message: sp.message };
  }
  if (
    st.model.aim === 'factor' &&
    E.dis.length === 1 &&
    E.dis[0]!.sides.length === 2 &&
    E.dis[0]!.sides.some((s) => topFactors(s, st.K).length >= 2)
  ) {
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'D’où vient ce produit ?',
      message:
        'Factoriser de tête, c’est risqué. ' +
        (st.model.factorHint ||
          'Montre d’abord le facteur commun, en écrivant ' +
            st.model.goal[0] +
            ' = (facteur commun)(…).') +
        ' Ensuite, je valide chaque transformation.',
    };
  }
  // un pas sauté : la ligne réécrit un remplacement qu'on n'a pas encore écrit
  if (E.dis.length === 1) {
    const subs = substCandidates(st, definitions(st));
    for (let i = 0; i < subs.length; i++) {
      if (identEq(E.dis[0]!, subs[i]!.line.dis[0]!, st.K, true)) {
        const df = subs[i]!.def!;
        return {
          kind: 'unjustified',
          atoms: A,
          title: 'Tu vas trop vite',
          message:
            'Montre d’abord où tu remplaces ' +
            df.s +
            ' par ' +
            textOf(df.expr) +
            ', sur une ligne à part. Ensuite seulement, développe et réduis.',
        };
      }
    }
  }
  const goal = isolatesGoal(E, st, false);
  return {
    kind: 'unjustified',
    atoms: A,
    title: goal ? 'D’où vient cette valeur ?' : 'D’où ça vient ?',
    message: goal
      ? 'Écris d’abord le raisonnement qui mène à cette valeur : je valide chaque ligne qui découle des précédentes.'
      : 'Je ne trouve aucune de tes lignes, ni aucun outil, qui permette d’écrire ça maintenant. Repars de ce que tu sais déjà.',
  };
}

// a = b = c = d : chaque maillon doit découler du précédent
function chain(st: ReasonerState, E: Line, A: string[]): Verdict {
  const sides = E.dis[0]!.sides;
  const st2 = cloneState(st);
  const links: Line[] = [];
  const steps: Verdict[] = [];
  for (let k = 1; k < sides.length; k++) {
    const Lk = eq2(sides[0]!, sides[k]!);
    const Ak = normalize(lineAtoms(Lk));
    if (!isTrue(Lk, st)) {
      const fv = falseVerdict(st2, Lk, Lk, A, -1);
      if (fv.kind === 'unjustified')
        fv.message =
          'À partir de « ' + textOf(sides[k]!) + ' », ta chaîne ne tient plus. ' + fv.message;
      return fv;
    }
    const just = justify(st2, Lk);
    if (!just) {
      if (!hasUnknownLine(Lk, st2.K)) {
        links.push(Lk);
        addFact(st2, Lk, 'line');
        continue;
      }
      const u = unjustified(st2, Lk, A);
      u.message =
        'Le maillon « ' + lineText(Lk) + ' » ne découle pas de ce qui précède. ' + u.message;
      return u;
    }
    const v = usefulness(st2, Lk, Lk, Ak, just);
    if (v.kind === 'step') steps.push(v);
    links.push(Lk);
    addFact(st2, Lk, 'line');
    if (v.hyp) st2.stated.hyp = true;
  }
  if (!steps.length) {
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      links,
      title: 'Juste, mais ça ne t’avance pas',
      message: 'Chaque maillon est juste, mais tu avais déjà tout ça.',
    };
  }
  const main = steps.reduce((a, b) => (b.rule!.hora > a.rule!.hora ? b : a));
  const labels: string[] = [];
  steps.forEach((s) => {
    if (labels.indexOf(s.rule!.label) < 0) labels.push(s.rule!.label);
  });
  const last = steps[steps.length - 1]!;
  return {
    kind: 'step',
    atoms: A,
    line: E,
    links,
    final: !!last.final,
    hyp: steps.some((s) => s.hyp),
    hora: steps.reduce((s, x) => s + (x.hora || 0), 0),
    rule: Object.assign({}, main.rule!, {
      label: labels.join(' → '),
      why: steps.map((s) => s.rule!.why).join(' '),
    }),
  };
}

/** Enregistre une ligne validée (verdict step / ready / useless gardée) dans l'état. */
export function commit(st: ReasonerState, v: Verdict): void {
  if (st.kern) {
    commitKernelLine(st.kern, v as KernelVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kgeo) {
    kgeoCommit(st.kgeo, v as unknown as KGeoVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.krec) {
    krecCommit(st.krec, v as unknown as KRecVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kprog) {
    kprogCommit(st.kprog, v as unknown as KProgVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kmat) {
    kmatCommit(st.kmat, v as unknown as KMatVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.karith) {
    karithCommit(st.karith, v as unknown as KArithVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.klim) {
    klimCommit(st.klim, v as unknown as KLimVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kint) {
    kintCommit(st.kint, v as unknown as KIntVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kode) {
    kodeCommit(st.kode, v as unknown as KOdeVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kpart) {
    kpartCommit(st.kpart, v as unknown as KPartVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kriem) {
    kriemCommit(st.kriem, v as unknown as KRiemVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.klc) {
    klcCommit(st.klc, v as unknown as KLcVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kclaims) {
    kclaimsCommit(st.kclaims, v as unknown as KClaimsVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kvar) {
    kvarCommit(st.kvar, v as unknown as KVarVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.ktab) {
    ktabCommit(st.ktab, v as unknown as KTabVerdict);
    return;
  }
  if (st.kfor) {
    kforCommit(st.kfor, v as unknown as KForVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.kana) {
    kanaCommit(st.kana, v as unknown as KAnaVerdict);
    if (v.line) st.facts.push({ line: v.line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.cong) {
    const line = v.line || parseLine(v.atoms);
    addCongFact(st.cong, line.dis[0]!, 'line');
    st.facts.push({ line, atoms: normalize(lineAtoms(line)), source: 'line' });
    return;
  }
  if (st.cplx) {
    const line = v.line || parseLine(v.atoms);
    commitComplexLine(st.cplx, line);
    st.facts.push({ line, atoms: normalize(lineAtoms(line)), source: 'line' });
    return;
  }
  if (st.bnd) {
    addBoundsLine(st.bnd, v as Parameters<typeof addBoundsLine>[1]);
    st.facts.push({ line: v.line!, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.geo) {
    const line = v.line!;
    if (v.kind === 'step') addGeoLine(st.geo, line);
    st.facts.push({ line, atoms: v.atoms, source: 'line' });
    return;
  }
  if (st.units) {
    const line = v.line!;
    addUnitsLine(st.units, line);
    st.facts.push({ line, atoms: v.atoms, source: 'line' });
    return;
  }
  const lines = v.links || [v.line || parseLine(v.atoms)];
  // comparaison (moteur v5) : notée à part, jamais un fait d'égalité
  const cmp = lines.length === 1 && lines[0]!.dis.length === 1 ? lines[0]!.dis[0]! : null;
  if (cmp && cmp.cmp) {
    st.stated = { ...st.stated, checks: [...(st.stated.checks ?? []), cmpKey(cmp)] };
    st._cache = null;
    return;
  }
  lines.forEach((l) => addFact(st, l, 'line', { obligation: !!(v.rule && v.rule.obligation) }));
  if (v.hyp) st.stated.hyp = true;
  if (v.verif) st.stated.verif = true;
}

/* ================= solveur : prochain pas utile (coup de pouce, corrigé) ================= */
function isFinalLine(line: Line): boolean {
  return line.dis.every(
    (d) =>
      d.sides.length === 2 &&
      [0, 1].some((si) => d.sides[si]!.k === 's' && exactValue(d.sides[1 - si]!) !== null),
  );
}
function fold(t: Node): Node {
  if (!t.c) return t;
  const lits = t.c.filter((c) => c.k === 'n');
  const others = t.c.filter((c) => c.k !== 'n');
  if (lits.length < 2 && !(t.k === 'mul' && lits.length === 1 && lits[0]!.v === 1 && others.length))
    return t;
  const v = t.k === 'add' ? lits.reduce((s, c) => s + c.v!, 0) : lits.reduce((s, c) => s * c.v!, 1);
  const neutral = t.k === 'add' ? 0 : 1;
  const c2 =
    close(v, neutral) && others.length
      ? others
      : t.k === 'mul'
        ? [num(v)].concat(others)
        : others.concat([num(v)]);
  return nary(t.k, c2);
}
function signSplit(t: Node): { neg: boolean; t: Node } {
  if (t.k === 'n' && t.v! < 0) return { neg: true, t: num(-t.v!) };
  if (t.k === 'neg') return { neg: true, t: t.a! };
  return { neg: false, t };
}
function prodSigned(others: Node[], a: Node, b: Node): Node {
  const sa = signSplit(a);
  const sb = signSplit(b);
  let m: Node;
  if (!others.length && hasUnknown(sa.t, {}) && canon(sa.t) === canon(sb.t))
    m = un('sq', sa.t); // x × x → x²
  else if (
    !others.length &&
    hasUnknown(sa.t, {}) &&
    hasUnknown(sb.t, {}) &&
    (sa.t.k === 'mul' || sb.t.k === 'mul')
  )
    m = { k: 'mul', c: [sa.t, sb.t] }; // 2x × x, écrit tel quel (pas « 2xx »)
  else m = nary('mul', others.concat([sa.t, sb.t]));
  return sa.neg !== sb.neg ? neg(m) : m;
}
// k × terme d'une parenthèse : « 3 × 2x » reste écrit (le calcul 6x vient à la ligne suivante)
function distribTerm(others: Node[], term: Node): Node {
  if (term.k === 'mul' && hasUnknown(term, {})) return { k: 'mul', c: others.concat([term]) };
  // 3 × 4 reste écrit : calculé d'un coup, la ligne serait déjà la réponse (3(x + 4) → 3x + 12)
  if (term.k === 'n' && others.every((o) => o.k === 'n'))
    return { k: 'mul', c: others.concat([term]) };
  // x(x + 3) : x × 3 s'écrit 3x
  if (term.k === 'n' && term.v! > 0 && others.every((o) => o.k === 's'))
    return { k: 'mul', c: [term as Node].concat(others) };
  // facteur négatif : −2 × (−4) reste écrit, sinon la ligne serait déjà la réponse
  if (others.some((o) => o.k === 'n' && o.v! < 0)) return { k: 'mul', c: others.concat([term]) };
  return fold(nary('mul', others.concat([term])));
}
/** dénominateur commun des fractions numériques d'une équation qui contient l'inconnue (x ÷ 3, (2x − 1) ÷ 4) */
function commonDenominator(d: Eq, K: Env): number | null {
  if (d.sides.length !== 2 || !d.sides.some((x) => hasUnknown(x, K))) return null;
  // une fraction seule face à un nombre (0,4 = t ÷ 100) : le pas « × 100 » suffit
  if (!d.sides.some((x) => x.k === 'add')) return null;
  const dens: number[] = [];
  d.sides.forEach((x) =>
    (x.k === 'add' ? x.c! : [x]).forEach((t) => {
      const u = signSplit(t).t;
      if (
        u.k === 'div' &&
        u.b!.k === 'n' &&
        Number.isInteger(u.b!.v) &&
        u.b!.v! >= 2 &&
        hasUnknown(u.a!, K)
      )
        dens.push(u.b!.v!);
    }),
  );
  if (!dens.length) return null;
  const l = dens.reduce((a, b) => (a * b) / gcd(a, b), 1);
  return l <= 1000 ? l : null;
}
function scaleTerm(t: Node, L: number): Node {
  const sg = signSplit(t);
  const u = sg.t;
  let r: Node;
  if (u.k === 'div' && u.b!.k === 'n' && Number.isInteger(u.b!.v) && L % u.b!.v! === 0) {
    const f = L / u.b!.v!;
    const a = u.a!;
    r = f === 1 ? a : fold(nary('mul', [num(f)].concat(a.k === 'mul' ? a.c! : [a])));
  } else r = fold(nary('mul', [num(L)].concat(u.k === 'mul' ? u.c! : [u])));
  return sg.neg ? neg(r) : r;
}
function scaleSide(x: Node, L: number): Node {
  return x.k === 'add'
    ? nary(
        'add',
        x.c!.map((t) => scaleTerm(t, L)),
      )
    : scaleTerm(x, L);
}
function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a;
}
/** un monôme à plusieurs lettres : coefficient et degré de chaque inconnue (3a²b → 3, {a: 2, b: 1}) */
function monoMulti(t: Node, K: Env): { c: number; pw: Record<string, number> } | null {
  if (t.k === 'n') return { c: t.v!, pw: {} };
  if (t.k === 's')
    return K[t.n!] !== undefined ? { c: K[t.n!]!, pw: {} } : { c: 1, pw: { [t.n!]: 1 } };
  if (t.k === 'neg') {
    const m = monoMulti(t.a!, K);
    return m ? { c: -m.c, pw: m.pw } : null;
  }
  if (t.k === 'sq') {
    const m = monoMulti(t.a!, K);
    if (!m) return null;
    const pw: Record<string, number> = {};
    Object.keys(m.pw).forEach((n) => (pw[n] = 2 * m.pw[n]!));
    return { c: m.c * m.c, pw };
  }
  if (t.k === 'mul') {
    let c = 1;
    const pw: Record<string, number> = {};
    for (let i = 0; i < t.c!.length; i++) {
      const m = monoMulti(t.c![i]!, K);
      if (!m) return null;
      c *= m.c;
      Object.keys(m.pw).forEach((n) => (pw[n] = (pw[n] || 0) + m.pw[n]!));
    }
    return { c, pw };
  }
  return null;
}
function monoBuild(c: number, pw: Record<string, number>, order: string[]): Node {
  const parts: Node[] = [];
  const a = Math.abs(c);
  order.forEach((n) => {
    const d = pw[n] || 0;
    if (d === 1) parts.push({ k: 's', n });
    if (d === 2) parts.push(un('sq', { k: 's', n }));
  });
  if (a !== 1 || !parts.length) parts.unshift(num(a));
  const t = parts.length === 1 ? parts[0]! : nary('mul', parts);
  return c < 0 ? neg(t) : t;
}
/**
 * Le plus grand facteur commun « monôme » d'une somme de monômes (coefficients entiers) :
 * 6x + 9 → 3, x² + 3x → x, 4x² − 8x → 4x, 5a − 5b → 5. null s'il vaut 1.
 */
function monomialFactor(s: Node, K: Env): { k: Node; rest: Node[]; shown: Node[] } | null {
  if (s.k !== 'add' || s.c!.length < 2) return null;
  const order = Object.keys(symsOf(s)).filter((n) => K[n] === undefined);
  if (!order.length) return null;
  const ms = s.c!.map((t) => monoMulti(t, K));
  if (ms.some((m) => !m || !Number.isInteger(m.c) || m.c === 0)) return null;
  const mm = ms as { c: number; pw: Record<string, number> }[];
  if (mm.some((m) => Object.values(m.pw).some((d) => d > 2))) return null;
  const g = mm.reduce((acc, m) => gcd(acc, Math.abs(m.c)), 0);
  const kpw: Record<string, number> = {};
  order.forEach((n) => {
    const d = Math.min(...mm.map((m) => m.pw[n] || 0));
    if (d) kpw[n] = d;
  });
  if (g === 1 && !Object.keys(kpw).length) return null;
  const k = monoBuild(g, kpw, order);
  const rest = mm.map((m) => {
    const pw: Record<string, number> = {};
    order.forEach((n) => (pw[n] = (m.pw[n] || 0) - (kpw[n] || 0)));
    return monoBuild(m.c / g, pw, order);
  });
  // chaque terme écrit k × reste (le signe reste devant) : 3 × x + 3 × 4
  const shown = rest.map((r) => {
    const sp = signSplit(r);
    // produit à plat, comme l'élève l'écrit : 3 × 2x est 3 × 2 × x (jamais calculé ici)
    const p: Node = {
      k: 'mul',
      c: (k.k === 'mul' ? k.c! : [k]).concat(sp.t.k === 'mul' ? sp.t.c! : [sp.t]),
    };
    return sp.neg ? neg(p) : p;
  });
  return { k, rest, shown };
}
// monôme c·x^d (x = l'inconnue du membre)
function monomial(t: Node, x: string): { c: number; d: number } | null {
  if (t.k === 'n') return { c: t.v!, d: 0 };
  if (t.k === 's') return t.n === x ? { c: 1, d: 1 } : null;
  if (t.k === 'sq') {
    const m = monomial(t.a!, x);
    return m ? { c: m.c * m.c, d: 2 * m.d } : null;
  }
  if (t.k === 'neg') {
    const m2 = monomial(t.a!, x);
    return m2 ? { c: -m2.c, d: m2.d } : null;
  }
  if (t.k === 'mul') {
    let c = 1;
    let d = 0;
    for (let i = 0; i < t.c!.length; i++) {
      const mi = monomial(t.c![i]!, x);
      if (!mi) return null;
      c *= mi.c;
      d += mi.d;
    }
    return { c, d };
  }
  return null;
}
function monoTree(c: number, d: number, x: string): Node | null {
  if (Math.abs(c) < 1e-12) return num(0);
  if (c < 0) {
    const inner = monoTree(-c, d, x);
    return inner ? neg(inner) : null;
  }
  if (d > 2) return null;
  const xs = d === 0 ? null : d === 1 ? sym(x) : un('sq', sym(x));
  if (!xs) return num(Math.round(c * 1e9) / 1e9);
  return close(c, 1) ? xs : nary('mul', [num(Math.round(c * 1e9) / 1e9), xs]);
}
function soleUnknown(t: Node, K: Env): string | null {
  const u = Object.keys(symsOf(t)).filter((s) => K[s] === undefined);
  return u.length === 1 ? u[0]! : null;
}
// −(x + 4) → −x − 4
function unparen(t: Node, did: unknown[]): Node {
  const t2 = mapKids(t, (c) => unparen(c, did));
  if (t2.k !== 'add') return t2;
  let changed = false;
  const c: Node[] = [];
  t2.c!.forEach((x) => {
    if (x.k === 'neg' && x.a!.k === 'add') {
      changed = true;
      x.a!.c!.forEach((y) => c.push(neg(y)));
    } else c.push(x);
  });
  if (changed) did.push(1);
  return changed ? nary('add', c) : t2;
}
// 2x × x → 2x², 3 × 4 → 12 (chaque produit d'une somme)
function monoNorm(t: Node, did: unknown[], K?: Env): Node {
  const x = soleUnknown(t, K || {});
  if (!x) return t;
  function norm(term: Node): Node {
    const m = monomial(term, x!);
    if (!m) return term;
    const nt = monoTree(m.c, m.d, x!);
    if (!nt || canon(nt) === canon(term)) return term;
    did.push(1);
    return nt;
  }
  function walk(n: Node): Node {
    const n2 = mapKids(n, walk);
    return n2.k === 'add' ? nary('add', n2.c!.map(norm)) : n2;
  }
  const r = walk(t);
  return r.k === 'mul' || r.k === 'neg' ? norm(r) : r;
}
// 2x² − 8x + 3x − 12 → 2x² − 5x − 12
function collectDeep(t: Node, did: unknown[], K?: Env): Node {
  const t2 = mapKids(t, (c) => collectDeep(c, did, K));
  if (t2.k !== 'add') return t2;
  const x = soleUnknown(t2, K || {});
  if (!x) return t2;
  const byDeg: Record<number, number> = {};
  for (let i = 0; i < t2.c!.length; i++) {
    const m = monomial(t2.c![i]!, x);
    if (!m || m.d > 2) return t2;
    byDeg[m.d] = (byDeg[m.d] || 0) + m.c;
  }
  const degs = Object.keys(byDeg)
    .map(Number)
    .filter((d) => Math.abs(byDeg[d]!) > 1e-12)
    .sort((a, b) => b - a);
  if (degs.length >= t2.c!.length) return t2;
  did.push(1);
  if (!degs.length) return num(0);
  return nary(
    'add',
    degs.map((d) => monoTree(byDeg[d]!, d, x)!),
  );
}
// un pas de développement : 2(2ⁿ − 1) → 2 × 2ⁿ − 2 ; (2x + 3)(x − 4) → les 4 produits
function expand(t: Node): Node {
  // (a + b)² = a² + 2ab + b² ; (a − b)² = a² − 2ab + b²
  if (t.k === 'sq' && t.a!.k === 'add' && t.a!.c!.length === 2) {
    const [p, q] = t.a!.c! as [Node, Node];
    const sp = signSplit(p);
    const sq = signSplit(q);
    // double produit : les nombres regroupés (2 × x × 1 → 2x)
    const mid = fold(nary('mul', [num(2), sp.t, sq.t]));
    return nary('add', [un('sq', sp.t), sp.neg !== sq.neg ? neg(mid) : mid, un('sq', sq.t)]);
  }
  if (t.k === 'mul') {
    const adds: number[] = [];
    t.c!.forEach((c, j) => {
      if (c.k === 'add') adds.push(j);
    });
    if (adds.length >= 2) {
      const rest = t.c!.filter((_, j) => j !== adds[0] && j !== adds[1]);
      const terms: Node[] = [];
      t.c![adds[0]!]!.c!.forEach((a) =>
        t.c![adds[1]!]!.c!.forEach((b) => terms.push(prodSigned(rest, a, b))),
      );
      return nary('add', terms);
    }
    let i = -1;
    t.c!.forEach((c, j) => {
      if (i < 0 && c.k === 'add') i = j;
    });
    if (i >= 0) {
      let others = t.c!.filter((_, j) => j !== i);
      // facteur négatif : on écrit son signe sur chaque terme (−4(3 − 2x) → −4 × 3 + 4 × 2x)
      let flip = false;
      const s0 = signSplit(others[0]!);
      if (s0.neg) {
        flip = true;
        others = [s0.t].concat(others.slice(1));
      }
      return nary(
        'add',
        t.c![i]!.c!.map((term) => {
          if (!flip) {
            if (term.k === 'neg') return neg(distribTerm(others, term.a!));
            return distribTerm(others, term);
          }
          const st = signSplit(term);
          const prod = distribTerm(others, st.t);
          return st.neg ? prod : neg(prod);
        }),
      );
    }
  }
  // −(a − b + c) = −a + b − c
  if (t.k === 'neg' && t.a!.k === 'add')
    return nary(
      'add',
      t.a!.c!.map((term) => {
        const st = signSplit(term);
        return st.neg ? st.t : neg(st.t);
      }),
    );
  return t;
}
function expandOnce(t: Node, did: { done?: boolean; square?: 'plus' | 'minus' }, depth = 0): Node {
  if (did.done) return t;
  // −(a − b) dans une somme : c'est le pas « enlever les parenthèses » (unparen) qui s'en charge
  const e = t.k === 'neg' && depth > 0 ? t : expand(t);
  if (e !== t) {
    did.done = true;
    if (t.k === 'sq')
      did.square = signSplit(t.a!.c![1]!).neg !== signSplit(t.a!.c![0]!).neg ? 'minus' : 'plus';
    return e;
  }
  return mapKids(t, (c) => expandOnce(c, did, depth + 1));
}
// 2 × 2ⁿ → 2ⁿ⁺¹
function powerCombine(t: Node, did: unknown[]): Node {
  const t2 = mapKids(t, (c) => powerCombine(c, did));
  if (t2.k !== 'mul') return t2;
  const c = t2.c!;
  for (let i = 0; i < c.length; i++) {
    if (c[i]!.k !== 'n') continue;
    for (let j = 0; j < c.length; j++) {
      if (c[j]!.k === 'pow' && c[j]!.a!.k === 'n' && c[j]!.a!.v === c[i]!.v) {
        did.push(1);
        const rest = c.filter((_, q) => q !== i && q !== j);
        const p = pw(c[j]!.a!, fold(nary('add', [c[j]!.b!, num(1)])));
        return rest.length ? nary('mul', rest.concat([p])) : p;
      }
    }
  }
  return t2;
}
/** p/q exact d'un nombre décimal ou d'une fraction d'entiers (moteur v2), sinon null. */
function rationalOf(t: Node): [number, number] | null {
  if (t.k === 'n') {
    const v = t.v!;
    for (let q = 1; q <= 10000; q *= 10) {
      const p = Math.round(v * q);
      if (Math.abs(p / q - v) < 1e-12) return [p, q];
    }
    return null;
  }
  if (t.k === 'div' && t.a!.k === 'n' && t.b!.k === 'n') {
    const a = rationalOf(t.a!);
    const b = rationalOf(t.b!);
    if (!a || !b || b[0] === 0) return null;
    return [a[0] * b[1], a[1] * b[0]];
  }
  if (t.k === 'neg') {
    const a = rationalOf(t.a!);
    return a ? [-a[0], a[1]] : null;
  }
  return null;
}
/** p/q irréductible, dénominateur positif : un nombre si la valeur est décimale exacte. */
function rationalNode(p: number, q: number): Node {
  const g = gcd(Math.abs(p), Math.abs(q)) * (q < 0 ? -1 : 1);
  const [a, b] = [p / g, q / g];
  return b === 1 || isExactDecimal(a / b) ? num(a / b) : dv(num(a), num(b));
}
/**
 * Somme ou produit de fractions (moteur v2) : 1/2 − 1/3 = 1/6, calculé exactement
 * (sinon « 0,5 − 1/3 » resterait bloqué, faute de valeur décimale exacte).
 */
function rationalSum(t: Node, allowNegFrac = false): Node | null {
  if (
    (t.k !== 'add' && t.k !== 'mul') ||
    !t.c!.some((c) => c.k === 'div' || (allowNegFrac && isNegFrac(c)))
  )
    return null;
  const rs = t.c!.map(rationalOf);
  if (rs.some((r) => r === null || Math.abs(r[1]) > 1e6)) return null;
  let p = t.k === 'add' ? 0 : 1;
  let q = 1;
  (rs as [number, number][]).forEach(([a, b]) => {
    if (t.k === 'add') {
      p = p * b + a * q;
      q = q * b;
    } else {
      p *= a;
      q *= b;
    }
  });
  return q === 0 ? null : rationalNode(p, q);
}
function evalInner(t: Node, K: Env, did: unknown[]): Node {
  if (t.k === 'n' || t.k === 's') return t;
  // 0 ÷ (3 × √2) = 0
  if (t.k === 'div' && t.a!.k === 'n' && t.a!.v === 0 && numericIn(t.b!, K) && ev(t.b!, K) !== 0) {
    did.push(t);
    return num(0);
  }
  // produit de racines qui tombe juste : √2 × √2 = 2
  if (
    t.k === 'mul' &&
    t.c!.some((c) => c.k === 'sqrt') &&
    t.c!.every((c) => c.k === 'sqrt' || c.k === 'n') &&
    numericIn(t, K) &&
    isExactDecimal(ev(t, K))
  ) {
    did.push(t);
    return num(Math.round(ev(t, K) * 1e6) / 1e6);
  }
  // puissance d'une racine, ou exposant fractionnaire, qui tombe juste : (√2)⁸ = 16, 125^(1/3) = 5
  // (moteur v9) aussi d'un produit avec des racines : (2√5)² = 20
  if (
    (t.k === 'pow' || t.k === 'sq') &&
    (t.a!.k === 'sqrt' ||
      (t.k === 'pow' && t.b!.k === 'div' && t.a!.k === 'n') ||
      (t.a!.k === 'mul' &&
        t.a!.c!.some((c) => c.k === 'sqrt') &&
        t.a!.c!.every((c) => c.k === 'sqrt' || c.k === 'n'))) &&
    numericIn(t, K)
  ) {
    const v = ev(t, K);
    if (isExactDecimal(v)) {
      did.push(t);
      return num(Math.round(v * 1e6) / 1e6);
    }
  }
  // un monôme divisé par un nombre : (0,3y) ÷ 0,2 → 1,5y (coefficient décimal exact)
  if (t.k === 'div' && t.b!.k === 'n' && t.b!.v !== 0 && hasUnknown(t.a!, K)) {
    const fs = t.a!.k === 'mul' ? t.a!.c! : [t.a!];
    const lits = fs.filter((c) => c.k === 'n');
    const rest = fs.filter((c) => c.k !== 'n');
    // seulement avec un coefficient au numérateur : « t ÷ 100 » reste écrit tel quel
    if (lits.length === 1 && rest.length && rest.every((c) => c.k === 's')) {
      const c = lits[0]!.v! / t.b!.v!;
      if (isExactDecimal(c)) {
        did.push(t);
        return close(c, 1) ? nary('mul', rest) : nary('mul', [num(c)].concat(rest));
      }
    }
  }
  // une fonction de valeur exacte se calcule d'un coup (moteur v2) : cos(120°) = −0,5, ln(1) = 0
  if (isFuncNode(t) && numericIn(t, K)) {
    const v = ev(t, K);
    if (isExactDecimal(v)) {
      did.push(t);
      return num(v);
    }
  }
  const ch = kids(t);
  if (numericIn(t, K) && ch.every((c) => c.k === 'n')) {
    const v = ev(t, K);
    // fraction d'entiers sans écriture décimale exacte : on la rend irréductible
    // (−10 ÷ (−70) → 1 ÷ 7), sinon on la laisse telle quelle
    if (t.k === 'div' && !isExactDecimal(v)) {
      const a = t.a!.v!;
      const b = t.b!.v!;
      if (Number.isInteger(a) && Number.isInteger(b) && b !== 0) {
        const g = gcd(Math.abs(a), Math.abs(b)) * (b < 0 ? -1 : 1);
        if (g !== 1) {
          did.push(t);
          return dv(num(a / g), num(b / g));
        }
      } else if (b !== 0) {
        // décimaux : 2 ÷ 0,15 = 40/3
        const fr = asFraction(v);
        if (fr) {
          did.push(t);
          return fr[0] < 0 ? neg(dv(num(-fr[0]), num(fr[1]))) : dv(num(fr[0]), num(fr[1]));
        }
      }
      return t;
    }
    // ln(5), √2 : pas de valeur décimale exacte, l'expression reste écrite
    // (moteur v9) sauf une puissance entière négative : 3^(−2) = 1/9
    if (!isExactDecimal(v)) {
      const r = rationalBin(t);
      if (r && !(r.k === t.k && textOf(r) === textOf(t))) {
        did.push(t);
        return r;
      }
      return t;
    }
    did.push(t);
    return num(v);
  }
  if (t.c) {
    const before = did.length;
    let mapped = t.c.map((c) => evalInner(c, K, did));
    const exact = rationalSum({ k: t.k, c: mapped });
    if (exact && numericIn(t, K)) {
      did.push(t);
      return exact;
    }
    const lits = mapped.filter((c) => c.k === 'n');
    const others = mapped.filter((c) => c.k !== 'n');
    if (lits.length >= 2 && others.length && isExactDecimal(combine(t.k, lits, K)!)) {
      did.push(nary(t.k, lits));
      const v = combine(t.k, lits, K)!;
      mapped = close(v, t.k === 'add' ? 0 : 1)
        ? others
        : t.k === 'mul'
          ? [num(v)].concat(others)
          : others.concat([num(v)]);
    } else if (numericIn(t, K) && did.length === before) {
      // (moteur v9) rien d'autre à calculer ici : 1 − 1/6 = 5/6
      const ex2 = rationalSum({ k: t.k, c: mapped }, true);
      if (ex2) {
        did.push(t);
        return ex2;
      }
    }
    return nary(t.k, mapped);
  }
  const mk = mapKids(t, (c) => evalInner(c, K, did));
  // (moteur v9) quotient ou puissance entière de fractions, calculé exactement :
  // 6 ÷ (2/3) = 9, 3^(−2) = 1/9, (1/9) ÷ (1/243) = 27
  const exact = rationalBin(mk);
  if (exact && numericIn(t, K)) {
    did.push(t);
    return exact;
  }
  return mk;
}
/** quotient ou puissance entière dont les termes sont des fractions (moteur v9), sinon null */
function rationalBin(t: Node): Node | null {
  if (t.k === 'div') {
    // seulement si un des termes est déjà une fraction (sinon la règle ordinaire s'applique)
    if (t.a!.k !== 'div' && t.b!.k !== 'div' && !isNegFrac(t.a!) && !isNegFrac(t.b!)) return null;
    const a = rationalOf(t.a!);
    const b = rationalOf(t.b!);
    if (!a || !b || b[0] === 0) return null;
    // seulement si une fraction n'a pas d'écriture décimale (sinon le calcul ordinaire suffit)
    if (isExactDecimal(a[0] / a[1]) && isExactDecimal(b[0] / b[1])) return null;
    const [p, q] = [a[0] * b[1], a[1] * b[0]];
    if (Math.abs(p) > 1e9 || Math.abs(q) > 1e9) return null;
    return rationalNode(p, q);
  }
  if (t.k === 'pow' || t.k === 'sq') {
    const base = rationalOf(t.a!);
    const e: [number, number] | null = t.k === 'sq' ? [2, 1] : rationalOf(t.b!);
    if (!base || !e || e[1] !== 1 || !Number.isInteger(e[0]) || Math.abs(e[0]) > 30) return null;
    // seulement un exposant négatif (une puissance positive d'une fraction reste écrite :
    // (5/6)³ est une forme exacte finale)
    if (e[0] >= 0) return null;
    const n = Math.abs(e[0]);
    let [p, q] = [base[0] ** n, base[1] ** n];
    if (e[0] < 0) [p, q] = [q, p];
    // seulement si le résultat n'a pas d'écriture décimale exacte : 3^(−2) = 1/9
    if (q !== 0 && isExactDecimal(p / q)) return null;
    if (q === 0 || Math.abs(p) > 1e9 || Math.abs(q) > 1e9) return null;
    return rationalNode(p, q);
  }
  return null;
}
function isNegFrac(t: Node): boolean {
  return t.k === 'neg' && t.a!.k === 'div';
}
function sameEq(a: Eq, b: Eq): boolean {
  return lineText({ dis: [a] }) === lineText({ dis: [b] });
}
interface Improvement {
  eq?: Eq;
  line?: Line;
  kind: string;
  label: string;
  text: string;
  why: string;
}
function improveEq(
  d: Eq,
  st: ReasonerState,
  defs: Definition[],
  inDisjunction = false,
): Improvement | null {
  const K = st.K;
  if (d.sides.length !== 2) return null;
  const lone = (s: Node): boolean => s.k === 's';
  // 1. remplacer les grandeurs connues par leur valeur (forme exacte si besoin : AB = √32)
  const names: string[] = [];
  const closed = closedForms(st);
  // « C(20,18) = C(20,2) », « N(0) = C » : deux grandeurs seules ; on remplace celle
  // qui est connue quand l'autre ne l'est pas encore
  const bothLone = d.sides.every(lone);
  const s1 = d.sides.map((s, si) => {
    if (lone(s)) {
      const other = d.sides[1 - si]!;
      const goal = st.model.goal;
      const replaceable =
        bothLone &&
        ((K[s.n!] !== undefined && K[other.n!] === undefined) ||
          (goal.includes(other.n!) && !goal.includes(s.n!)));
      if (!replaceable) return s;
    }
    const r = subst(s, K, false, closed);
    Object.keys(symsOf(s)).forEach((n) => {
      if (K[n] !== undefined && names.indexOf(n) < 0 && !symsOf(r)[n]) names.push(n);
    });
    return r;
  });
  if (names.length && !sameEq({ sides: s1 }, d))
    return {
      eq: { sides: s1 },
      kind: 'subst',
      label: 'Remplacer',
      text:
        'Remplace ' +
        names.join(' et ') +
        ' par ' +
        (names.length > 1 ? 'leurs valeurs.' : 'sa valeur.'),
      why:
        'On remplace ' +
        names.join(' et ') +
        ' par ' +
        (names.length > 1 ? 'leurs valeurs.' : 'sa valeur.'),
    };
  // 2. remplacer une grandeur par son expression (hypothèse de récurrence…)
  // la définition la plus récente d'abord (x = 1,5y plutôt que x = 0,3y/0,2)
  for (let i = (defs || []).length - 1; i >= 0; i--) {
    const df = defs[i]!;
    let hit = false;
    let s2 = d.sides.map((s) => {
      if (lone(s) || !symsOf(s)[df.s]) return s;
      hit = true;
      return replaceSym(s, df.s, df.expr);
    });
    // y = 2x − 1 et y = −x + 5 : les deux expressions de y sont égales
    if (!hit) {
      const li = d.sides.findIndex((s) => lone(s) && s.n === df.s);
      const other = li >= 0 ? d.sides[1 - li]! : null;
      if (other && hasUnknown(other, K) && !symsOf(other)[df.s]) {
        hit = true;
        s2 = li === 0 ? [df.expr, other] : [other, df.expr];
      }
    }
    // remplacer x dans l'équation d'où l'on a tiré x donne une identité (0,2 × (1,5y) = 0,3y)
    const U = Object.keys(symsOf({ k: 'add', c: s2 })).filter((x) => K[x] === undefined);
    const identity =
      hit &&
      U.length > 0 &&
      [1.37, 2.9, 0.53].every((v, i) => {
        const env: Env = Object.assign({}, K);
        U.forEach((u, j) => (env[u] = v + j * 0.71 + i * 0.13));
        const a = ev(s2[0]!, env);
        const b = ev(s2[1]!, env);
        return isFinite(a) && Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a));
      });
    if (hit && !identity) {
      return {
        eq: { sides: s2 },
        kind: 'subst',
        label: df.source === 'hyp' ? 'Utiliser l’hypothèse' : 'Remplacer',
        text:
          df.source === 'hyp'
            ? 'Utilise l’hypothèse de récurrence : remplace ' +
              df.s +
              ' par ' +
              textOf(df.expr) +
              '.'
            : 'Remplace ' + df.s + ' par ' + textOf(df.expr) + '.',
        why:
          'On remplace ' +
          df.s +
          ' par ' +
          textOf(df.expr) +
          (df.source === 'hyp' ? ' : c’est l’hypothèse de récurrence.' : '.'),
      };
    }
  }
  // 3. supprimer les parenthèses précédées d'un « − »
  const ud: unknown[] = [];
  const su = d.sides.map((s) => unparen(s, ud));
  // une réécriture qui s'écrit à l'identique (−(3x) → (−3) × x) n'est pas un pas : étape suivante
  if (ud.length && !sameEq({ sides: su }, d))
    return {
      eq: { sides: su },
      kind: 'unparen',
      label: 'Supprimer les parenthèses',
      text: 'Supprime les parenthèses : un « − » devant change le signe de chaque terme.',
      why: 'Un « − » devant une parenthèse change le signe de chaque terme.',
    };
  // 4 bis. un produit égal à zéro : le produit nul d'abord ((2x − 1 − 3)(2x − 1 + 3) = 0 :
  // on réduira chaque facteur dans son cas, sans réécrire le produit)
  const pz0 = productZero(d, K);
  if (pz0 && pz0.length > 1) {
    return {
      line: { dis: pz0.map((fc) => ({ sides: [fc, num(0)] })) },
      kind: 'rule',
      label: 'Produit nul',
      text: 'Un produit est nul quand l’un de ses facteurs est nul : écris « facteur = 0 ou facteur = 0 ».',
      why: 'Un produit est nul si et seulement si l’un de ses facteurs est nul.',
    };
  }
  // 4. calculer chaque produit
  const md: unknown[] = [];
  const sm = d.sides.map((s) => monoNorm(s, md, K));
  if (md.length && !sameEq({ sides: sm }, d))
    return {
      eq: { sides: sm },
      kind: 'mono',
      label: 'Calculer',
      text: 'Calcule chaque produit.',
      why: 'On calcule chaque produit.',
    };
  // 5. regrouper les termes semblables
  const cd: unknown[] = [];
  const sc = d.sides.map((s) => collectDeep(s, cd, K));
  if (cd.length && !sameEq({ sides: sc }, d))
    return {
      eq: { sides: sc },
      kind: 'collect',
      label: 'Réduire',
      text: 'Regroupe les termes semblables : les x² ensemble, les x ensemble, les nombres ensemble.',
      why: 'On regroupe les termes semblables.',
    };
  // 6. calculer, réduire
  const did: Node[] = [];
  const pdid: unknown[] = [];
  let symbolic = false;
  const s3 = d.sides.map((s) => {
    const n0 = did.length + pdid.length;
    const r = powerCombine(evalInner(s, K, did), pdid);
    if (did.length + pdid.length > n0 && hasUnknown(s, K)) symbolic = true;
    return r;
  });
  if ((did.length || pdid.length) && !sameEq({ sides: s3 }, d)) {
    return {
      eq: { sides: s3 },
      kind: 'calc',
      label: pdid.length || symbolic ? 'Réduire' : 'Calculer',
      text:
        pdid.length || symbolic
          ? 'Réduis : regroupe les nombres et les puissances de même base.'
          : 'Calcule ' + did.slice(0, 2).map(textOf).join(' et ') + '.',
      why: pdid.length
        ? 'On regroupe les nombres, et 2 × 2ⁿ = 2ⁿ⁺¹ : multiplier par 2 ajoute 1 à l’exposant.'
        : symbolic
          ? 'On regroupe les nombres.'
          : 'On calcule ' + did.slice(0, 2).map(textOf).join(' et ') + '.',
    };
  }
  // 7. produit nul
  const pr = productZero(d, K);
  if (pr) {
    return {
      line: { dis: pr.map((fc) => ({ sides: [fc, num(0)] })) },
      kind: 'rule',
      label: pr.length === 1 ? 'Carré nul' : 'Produit nul',
      text: 'Un produit est nul quand l’un de ses facteurs est nul : écris « facteur = 0 ou facteur = 0 ».',
      why: 'Un produit est nul si et seulement si l’un de ses facteurs est nul.',
    };
  }
  // 9 bis. x² = a (a > 0, x pas une longueur) : deux solutions, √a et −√a
  // (le corrigé garde le chemin choisi par l'auteur : factoriser puis produit nul)
  const factorRoute = (st.model.preds ?? []).some(
    (p) => p.skill === 'differenceCarres' || p.skill === 'produitNul' || p.skill === 'secondDegre',
  );
  const r2 = factorRoute ? [] : racine2(d, st);
  if (r2.length) {
    const x0 = r2[0]!.dis[0]!.sides[0]!;
    const xn = x0.k === 's' ? x0.n + '²' : 'ce carré';
    return {
      line: r2[0]!,
      kind: 'racine',
      label: 'Racine carrée',
      text:
        'Tu connais ' + xn + '. Écris ses deux valeurs possibles : la racine carrée et son opposé.',
      why: 'Un carré égal à un nombre positif : ce qui est au carré vaut sa racine carrée ou son opposé.',
    };
  }
  // équation à résoudre en factorisant (x² = 5x, (x + 1)² = 4(x + 1)) : on passe tout
  // du même côté, puis on factorise — surtout pas développer
  const factorTool = (st.model.preds ?? []).some(
    (p) =>
      p.skill === 'facteurCommun' || p.skill === 'differenceCarres' || p.skill === 'carreParfait',
  );
  const isZero = (x: Node) => x.k === 'n' && x.v === 0;
  // une équation en x, pas une définition « A = 4x² − 8x »
  const solving =
    factorTool &&
    !st.model.aim &&
    !d.sides.some((x, i) => x.k === 's' && !symsOf(d.sides[1 - i]!)[x.n!]);
  if (
    solving &&
    d.sides.length === 2 &&
    !d.sides.some(isZero) &&
    d.sides.every((x) => hasUnknown(x, K))
  ) {
    const z = transpose(d, K).find(
      (t) => t.kind === 'transpo' && t.eq.sides.length === 2 && isZero(t.eq.sides[1]!),
    );
    if (z)
      return {
        eq: { sides: z.eq.sides.map(trivSimp) },
        // une réécriture : la ligne est équivalente, mais c'est elle qui permet de factoriser
        kind: 'isolate',
        label: 'Tout passer du même côté',
        text: 'Pour factoriser, passe tous les termes du même côté : « … = 0 ».',
        why: 'On se ramène à « … = 0 » pour pouvoir factoriser puis utiliser le produit nul.',
      };
  }
  // 8 bis. (moteur v9) un carré seul avec l'inconnue : on l'isole au lieu de développer
  // ((x − 2)² − 9 = 0 → (x − 2)² = 9 ; 2(x − 3)² = 8 → (x − 3)² = 4), la racine carrée suit
  if (!st.model.aim && !solving && !factorRoute) {
    const iso = isolateSquare(d, K);
    if (iso)
      return {
        eq: iso,
        kind: 'isolate',
        label: 'Même opération des deux côtés',
        text: 'Isole le carré : passe les nombres de l’autre côté.',
        why: 'On isole le carré pour pouvoir prendre la racine carrée.',
      };
  }
  // 8. développer (sauf si l'on cherche une forme factorisée, ou si on factorise pour résoudre)
  if (st.model.aim !== 'factor' && !solving) {
    const dd: { done?: boolean; square?: 'plus' | 'minus' } = {};
    const s4 = d.sides.map((s) => expandOnce(s, dd));
    if (dd.done && dd.square) {
      const form = dd.square === 'minus' ? '(a − b)² = a² − 2ab + b²' : '(a + b)² = a² + 2ab + b²';
      return {
        eq: { sides: s4 },
        kind: 'expand',
        label: dd.square === 'minus' ? 'Identité (a − b)²' : 'Identité (a + b)²',
        text: 'Développe le carré avec l’identité ' + form + '.',
        why: 'On développe avec l’identité remarquable ' + form + '.',
      };
    }
    if (dd.done)
      return {
        eq: { sides: s4 },
        kind: 'expand',
        label: 'Développer',
        text: 'Développe : chaque terme d’une parenthèse multiplie chaque terme de l’autre.',
        why: 'On développe : chaque terme d’une parenthèse multiplie chaque terme de l’autre.',
      };
  }
  // 9. racine
  const r = racine(d, st);
  if (r.length)
    return {
      eq: r[0]!,
      kind: 'racine',
      label: 'Racine carrée',
      text: 'Tu connais ' + r[0]!.sides[0]!.n + '². Prends la racine carrée.',
      why: r[0]!.sides[0]!.n + ' est une longueur, donc positive : on prend la racine carrée.',
    };
  // 9 ter. des fractions (x ÷ 3 + 2 = x ÷ 2) : on multiplie les deux membres par le
  // dénominateur commun (2x + 12 = 3x)
  const lcd = commonDenominator(d, K);
  if (
    lcd &&
    // (moteur v9) pas dans un « ou » : x ÷ 2 − 3 = 0 → x ÷ 2 = 3 (le juge ne suit pas
    // une multiplication faite dans une seule branche)
    !inDisjunction &&
    !st.model.aim &&
    !d.sides.some((x, i) => x.k === 's' && !symsOf(d.sides[1 - i]!)[x.n!])
  ) {
    const scaled = { sides: d.sides.map((x) => scaleSide(x, lcd)) };
    if (!sameEq(scaled, d))
      return {
        eq: scaled,
        kind: 'isolate',
        label: 'Chasser les dénominateurs',
        text: 'Multiplie chaque terme des deux membres par ' + lcd + ', le dénominateur commun.',
        why: 'On multiplie les deux membres par ' + lcd + ' : les fractions disparaissent.',
      };
  }
  // 10. isoler l'inconnue
  if (!isFinalLine({ dis: [d] })) {
    let best: { eq: Eq; m: number; desc: string; moved?: boolean } | null = null;
    transpose(d, K)
      .filter((t) => t.kind === 'transpo')
      .forEach((t) => {
        let s = { sides: t.eq.sides.map(trivSimp) };
        let desc = t.desc;
        // terme en x déplacé : on écrit directement la ligne regroupée (2x − 1 = x + 4 → x − 1 = 4)
        if (t.unknownMove) {
          s = { sides: s.sides.map((x) => trivSimp(collectDeep(x, [], K))) };
          desc = desc.replace(/\.$/, ', puis regroupe les x.');
        }
        let m = eqM(s, K);
        // x passé de l'autre côté : on juge la ligne avec le pas suivant (0 = 0,2ℓ − 4 → 4 = 0,2ℓ)
        if (t.unknownMove)
          transpose(s, K)
            .filter((t2) => t2.kind === 'transpo' && !t2.unknownMove)
            .forEach((t2) => (m = Math.min(m, eqM({ sides: t2.eq.sides.map(trivSimp) }, K) + 1)));
        if (!best || m < best.m) best = { eq: s, m, desc, moved: t.unknownMove };
      });
    let b = best as { eq: Eq; m: number; desc: string } | null;
    // deux inconnues (0,2x = 0,3y) : isoler l'une donne une expression à substituer (x = 1,5y)
    const unkOf = (e: Eq) =>
      Object.keys(symsOf({ k: 'add', c: e.sides })).filter((x) => K[x] === undefined);
    if ((!b || b.m >= eqM(d, K)) && unkOf(d).length === 2 && !d.sides.some(lone)) {
      const iso = transpose(d, K)
        .filter((t) => t.kind === 'transpo')
        .map((t) => ({ sides: t.eq.sides.map(trivSimp) }))
        .filter((e) => e.sides.some((x) => lone(x) && K[x.n!] === undefined));
      if (iso.length) b = { eq: iso[0]!, m: eqM(iso[0]!, K), desc: 'Isole une inconnue.' };
      if (b && iso.length)
        return {
          eq: b.eq,
          kind: 'isolate',
          label: 'Même opération des deux côtés',
          text: 'Isole ' + b.eq.sides.find(lone)!.n + ' pour pouvoir le remplacer.',
          why: 'On isole une inconnue : on pourra la remplacer par son expression dans l’autre équation.',
        };
    }
    if (b && b.m < eqM(d, K))
      return {
        eq: b.eq,
        // x passé de l'autre côté : avance même si la ligne paraît aussi longue (−0,2ℓ = −4)
        kind: (b as { moved?: boolean }).moved ? 'isolate' : 'transpo',
        label: 'Même opération des deux côtés',
        text: b.desc,
        why: onDit(b.desc),
      };
  }
  return null;
}
/** le carré (ou c × carré) d'une expression qui contient l'inconnue */
function unknownSquare(t: Node, K: Env): boolean {
  const sq = (x: Node) =>
    (x.k === 'sq' || (x.k === 'pow' && x.b!.k === 'n' && x.b!.v === 2)) &&
    hasUnknown(x.a!, K) &&
    // le carré d'une lettre seule (BC², t²) suit déjà la règle ordinaire
    x.a!.k !== 's';
  if (sq(t)) return true;
  return (
    t.k === 'mul' &&
    t.c!.filter((c) => sq(c)).length === 1 &&
    t.c!.every((c) => sq(c) || c.k === 'n')
  );
}
/** a·(…)² + b = c → a·(…)² = c − b, puis (…)² = (c − b) ÷ a (moteur v9) */
function isolateSquare(d: Eq, K: Env): Eq | null {
  if (d.sides.length !== 2) return null;
  for (const si of [0, 1]) {
    const side = d.sides[si]!;
    const other = d.sides[1 - si]!;
    if (!numericIn(other, K)) continue;
    const ov = ev(other, K);
    if (!isFinite(ov)) continue;
    // a·(…)² + b (b numérique) : on passe b
    if (side.k === 'add') {
      const unk = side.c!.filter((c) => hasUnknown(c, K));
      if (unk.length !== 1 || !unknownSquare(unk[0]!, K)) continue;
      const rest = side.c!.filter((c) => !hasUnknown(c, K));
      const v = ov - rest.reduce((acc, c) => acc + ev(c, K), 0);
      if (!isExactDecimal(v)) continue;
      return si === 0 ? { sides: [unk[0]!, num(v)] } : { sides: [num(v), unk[0]!] };
    }
    // a·(…)² = c : on divise par a
    if (side.k === 'mul' && unknownSquare(side, K)) {
      const a = side.c!.filter((c) => c.k === 'n').reduce((acc, c) => acc * c.v!, 1);
      const sqn = side.c!.find((c) => c.k !== 'n')!;
      if (a === 0 || !isExactDecimal(ov / a)) continue;
      return si === 0 ? { sides: [sqn, num(ov / a)] } : { sides: [num(ov / a), sqn] };
    }
  }
  return null;
}
interface ImprovedLine {
  line: Line;
  kind: string;
  label: string;
  text: string;
  why: string;
}
function improve(line: Line, st: ReasonerState): ImprovedLine | null {
  if (line.dis.length === 1) {
    const m = improveEq(line.dis[0]!, st, []);
    if (!m) return null;
    if (m.eq && sameEq(m.eq, line.dis[0]!)) return null;
    return {
      line: m.line || { dis: [m.eq!] },
      kind: m.kind,
      label: m.label,
      text: m.text,
      why: m.why,
    };
  }
  let changed = false;
  const texts: string[] = [];
  const dis = line.dis.map((d) => {
    const mm = improveEq(d, st, [], true);
    if (mm && mm.eq) {
      changed = true;
      if (texts.indexOf(mm.text) < 0) texts.push(mm.text);
      return mm.eq;
    }
    return d;
  });
  if (!changed) return null;
  return {
    line: { dis },
    kind: 'calc',
    label: 'Même opération des deux côtés',
    text: texts.length > 1 ? 'Dans chaque cas, isole x.' : texts[0] + ' (dans chaque cas)',
    why: 'Dans chaque cas, on isole x en faisant la même opération des deux côtés.',
  };
}
function improveFact(f: Fact, st: ReasonerState, defs: Definition[]): ImprovedLine | null {
  const fi = st.facts.indexOf(f);
  const line = f.line;
  if (line.dis.length === 1) {
    const m = improveEq(
      line.dis[0]!,
      st,
      defs.filter((df) => df.gi !== fi),
    );
    if (!m) return null;
    if (m.eq && sameEq(m.eq, line.dis[0]!)) return null;
    return {
      line: m.line || { dis: [m.eq!] },
      kind: m.kind,
      label: m.label,
      text: m.text,
      why: m.why,
    };
  }
  return improve(line, st);
}
const REWRITE: Record<string, 1> = { expand: 1, unparen: 1, mono: 1, collect: 1, isolate: 1 };
// en remontant depuis le but : les grandeurs qu'il faudra connaître pour l'atteindre
function neededSymbols(st: ReasonerState, rcs: RuleConclusion[]): Record<string, 1> {
  const K = st.K;
  const needed: Record<string, 1> = {};
  st.model.goal.forEach((g) => {
    needed[g] = 1;
  });
  let changed = true;
  while (changed) {
    changed = false;
    rcs.forEach((rc) => {
      const unk = Object.keys(lineSyms(rc.line)).filter((s) => K[s] === undefined);
      if (!unk.some((u) => needed[u])) return;
      unk.forEach((u) => {
        if (!needed[u]) {
          needed[u] = 1;
          changed = true;
        }
      });
    });
  }
  return needed;
}
function nextMove(st: ReasonerState, prefer?: string | null): Move | null {
  if (goalReached(st)) return null;
  // réponse arrondie (moteur v10) : la valeur exacte est écrite, on arrondit
  if (exactGoalReached(st) || roundingReady(st)) {
    const rl = roundingLine(st);
    if (rl)
      return {
        line: rl,
        kind: 'check',
        label: 'Arrondir',
        text:
          'Arrondis ' + roundingTarget(st) + ' ' + roundingWords(st.model.rounding!.places) + '.',
      };
  }
  const K = st.K;
  const rcs = ruleConclusions(st);
  // 1. les obligations (initialisation…)
  const ob = rcs.filter((rc) => rc.obligation && !established(st, rc.line))[0];
  if (ob) return { line: ob.line, kind: 'rule', label: ob.label, text: ob.hint, rc: ob };
  // 1 bis. une comparaison décisive dont les grandeurs sont calculées (« Δ > 0 »)
  const ck = nextCheck(st);
  if (ck) return { line: ck.line, kind: 'check', label: ck.spec.label, text: ck.spec.why };
  // 2. ce que les outils demandent ensuite (hypothèse de récurrence…)
  const keys = Object.keys(SKILLS);
  for (let k = 0; k < keys.length; k++) {
    const nxf = SKILLS[keys[k]!]!.next;
    if (!nxf) continue;
    const nx = nxf(st);
    if (nx) return nx;
  }
  // 3. faire avancer une ligne déjà écrite (la plus récente d'abord)
  const facts = st.facts.slice().reverse();
  const defs = definitions(st);
  for (let i = 0; i < facts.length; i++) {
    const f = facts[i]!;
    // « x = 10 ÷ 70 » donne la valeur, mais pas encore sous forme finale : on simplifie
    const unfinished = isolatesGoalByFraction(f.line, st);
    if (f.obligation || f.source === 'hyp' || (isFinalLine(f.line) && !unfinished)) continue;
    const m = improveFact(f, st, defs);
    if (!m) continue;
    // une substitution qui ne laisse plus aucune lettre (5 × 0,2 = 1, 0,8 + 0,2 = 1)
    // ne fait que vérifier : ça n'avance pas vers le but
    if (m.kind === 'subst' && !Object.keys(lineSyms(m.line)).length) continue;
    if (REWRITE[m.kind]) {
      if (st.facts.some((g) => lineText(g.line) === lineText(m.line))) continue;
      return m;
    }
    if (m.line.dis.every((d) => covered(st, d))) {
      const simplified =
        unfinished &&
        isolatesGoal(m.line, st, true) &&
        !st.facts.some((g) => lineText(g.line) === lineText(m.line));
      if (!simplified) continue;
    }
    return m;
  }
  // 4. un outil pas encore utilisé, qui mène vers ce que la partie demande
  const needed = neededSymbols(st, ruleConclusions(st, true));
  const open = rcs.filter((rc) => {
    if (rc.obligation || rc.needsProof) return false;
    if (rc.transform) return !st.facts.some((g) => matchLine(rc.line, g.line, K));
    if (rc.line.dis.every((d) => covered(st, d))) return false;
    const unk = Object.keys(lineSyms(rc.line)).filter((s) => K[s] === undefined);
    return unk.length === 1 && !!needed[unk[0]!];
  });
  // système (x = 4y et x + y = 1) : faute d'outil à une inconnue, un outil à deux inconnues
  // déjà toutes nécessaires ; la substitution fera le reste
  if (!open.length)
    rcs.forEach((rc) => {
      if (rc.obligation || rc.needsProof || rc.transform) return;
      if (rc.line.dis.length !== 1 || rc.line.dis.every((d) => covered(st, d))) return;
      const unk = Object.keys(lineSyms(rc.line)).filter((s) => K[s] === undefined);
      if (unk.length === 2 && unk.every((u) => !!needed[u])) open.push(rc);
    });
  if (prefer) open.sort((a, b) => (b.label === prefer ? 1 : 0) - (a.label === prefer ? 1 : 0));
  if (open.length)
    return {
      line: open[0]!.line,
      kind: 'rule',
      label: open[0]!.label,
      text: open[0]!.hint,
      rc: open[0]!,
    };
  // 5. système sans inconnue isolable : combiner les deux équations (moteur v12)
  return combinationMove(st);
}
/**
 * Σ aᵢ·xᵢ + c = 0 lu sur une équation écrite (null si elle n'est pas affine
 * en les inconnues `names`) : [a₁, …, aₙ, c].
 */
function affineN(eq: Eq, names: string[], K: Env): number[] | null {
  const f = eqResidual(eq);
  if (!f) return null;
  const at = (vals: number[]) => {
    const env: Env = { ...K };
    names.forEach((n, i) => (env[n] = vals[i]!));
    return f(env);
  };
  const zero = names.map(() => 0);
  const c = at(zero);
  const co = names.map((_, i) => at(zero.map((_, j) => (i === j ? 1 : 0))) - c);
  if (![c, ...co].every(isFinite)) return null;
  const pts = [
    [2.3, -1.7, 0.6, 1.9],
    [-3.1, 0.9, 4.2, -0.4],
    [0.6, 4.2, -2.3, 2.7],
  ];
  for (const p of pts) {
    const v = names.map((_, i) => p[i % p.length]! + i * 0.13);
    const got = at(v);
    const want = co.reduce((acc, a, i) => acc + a * v[i]!, c);
    if (Math.abs(got - want) > 1e-7 * Math.max(1, Math.abs(got))) return null;
  }
  return [...co, c];
}
function gcdInt(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}
/** combinaison qui élimine une inconnue : « 8x = 8 », « y + z = 1 » (coefficients entiers) */
function combinationMove(st: ReasonerState): Move | null {
  const K = st.K;
  const eqs: Eq[] = [];
  st.facts.forEach((fa) => {
    if (fa.line.dis.length !== 1 || fa.source === 'hyp') return;
    const d = fa.line.dis[0]!;
    const unk = Object.keys(lineSyms(fa.line)).filter((x) => K[x] === undefined);
    if (unk.length >= 2 && eqResidual(d)) eqs.push(d);
  });
  const written = new Set(st.facts.map((f) => lineText(f.line)));
  const best: { line: Line | null; cost: number } = { line: null, cost: Infinity };
  for (let i = 0; i < eqs.length; i++) {
    for (let j = i + 1; j < eqs.length; j++) {
      const names = Object.keys(lineSyms({ dis: [eqs[i]!, eqs[j]!] })).filter(
        (x) => K[x] === undefined,
      );
      if (names.length < 2 || names.length > 4) continue;
      const p = affineN(eqs[i]!, names, K);
      const q = affineN(eqs[j]!, names, K);
      if (!p || !q || !p.concat(q).every(Number.isInteger)) continue;
      names.forEach((_, v) => {
        if (!p[v] || !q[v]) return;
        // λ·E₁ + μ·E₂ sans la v-ième inconnue
        const g = gcdInt(p[v]!, q[v]!);
        let lam = q[v]! / g;
        let mu = -p[v]! / g;
        if (lam < 0) [lam, mu] = [-lam, -mu];
        let co = p.map((a, k) => lam * a + mu * q[k]!);
        // « −y − 2z = −2 » s'écrit « y + 2z = 2 » : premier coefficient positif
        const lead = co.slice(0, names.length).find((a) => a !== 0);
        if (lead !== undefined && lead < 0) co = co.map((a) => -a);
        const k0 = -co[names.length]!;
        const terms = names.map((n, k) => [n, co[k]!] as const).filter(([, a]) => a !== 0);
        if (!terms.length) return;
        const text =
          terms
            .map(([n, a], k) => {
              const sg = a < 0 ? (k ? ' − ' : '−') : k ? ' + ' : '';
              const m = Math.abs(a);
              return sg + (m === 1 ? '' : formatNumber(m) + ' × ') + n;
            })
            .join('') +
          ' = ' +
          formatNumber(k0);
        const line = parseText(text);
        if (written.has(lineText(line))) return;
        // le moins d'inconnues restantes, les multiplicateurs les plus simples, puis
        // la première inconnue éliminée d'abord (pivot de Gauss)
        const order = names.slice().sort();
        const cost =
          terms.length * 1000 + (Math.abs(lam) + Math.abs(mu)) * 10 + order.indexOf(names[v]!);
        if (cost < best.cost) {
          best.line = line;
          best.cost = cost;
        }
      });
    }
  }
  if (!best.line) return null;
  return {
    line: best.line,
    kind: 'comb',
    label: 'Combinaison des équations',
    text: 'Combine les deux équations pour éliminer une inconnue.',
    why: 'On combine les deux équations : une inconnue disparaît.',
  };
}

export interface SolutionStep {
  line: Line;
  text: string;
  atoms: string[];
  label: string;
  card: string | null;
  why: string;
}
/** Le corrigé ligne à ligne que trouve le moteur (coup de pouce, porte de validation). */
export function solve(
  model: ScratchModel,
  prefer?: string | null,
): { path: SolutionStep[]; reached: boolean } {
  const st = createState(model);
  const path: SolutionStep[] = [];
  if (st.kern) {
    const sol = kernelSolvePath(model);
    sol.lines.forEach(({ line, text, v }) =>
      path.push({
        line,
        text,
        atoms: lineAtoms(line),
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kfor) {
    const sol = kforSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kana) {
    const sol = kanaSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kint) {
    const sol = kintSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kode) {
    const sol = kodeSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kpart) {
    const sol = kpartSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kriem) {
    const sol = kriemSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.klc) {
    const sol = klcSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.klim) {
    const sol = klimSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.karith) {
    const sol = karithSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kvar) {
    const sol = kvarSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.ktab) {
    const sol = ktabSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kclaims) {
    const sol = kclaimsSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kmat) {
    const sol = kmatSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kprog) {
    const sol = kprogSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.krec) {
    const sol = krecSolvePath(model);
    sol.lines.forEach(({ line, text, atoms, v }) =>
      path.push({
        line,
        text,
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.kgeo) {
    const sol = kgeoSolvePath(model);
    sol.lines.forEach(({ line, text, v }) =>
      path.push({
        line,
        text,
        atoms: [text],
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      }),
    );
    return { path, reached: sol.reached };
  }
  if (st.units || st.geo || st.bnd) {
    (st.units
      ? unitsPathLines(model.units!)
      : st.bnd
        ? boundsPathLines(model.bounds!)
        : geoPathLines(model.geometry!)
    ).forEach((line) => {
      const text = lineText(line);
      const v = judge(st, [{ atoms: [text] }], { verify: true });
      path.push({
        line,
        text,
        atoms: [text],
        label: v.rule ? v.rule.label : '',
        card: null,
        why: v.rule ? v.rule.why : v.message || '',
      });
      if (v.kind === 'step') commit(st, v);
    });
    return { path, reached: goalReached(st) };
  }
  if (st.cong || st.cplx) {
    (st.cong ? congPathLines(model.congruence!) : complexPathLines(model.complex!)).forEach(
      (line) => {
        const atoms = lineAtoms(line);
        const v = judge(st, [{ atoms: normalize(atoms) }], { verify: true });
        path.push({
          line,
          text: lineText(line),
          atoms,
          label: v.rule ? v.rule.label : '',
          card: null,
          why: v.rule ? v.rule.why : '',
        });
        if (v.kind === 'step') commit(st, v);
      },
    );
    return { path, reached: goalReached(st) };
  }
  // moteur v16 : corrigé écrit à la main, rejoué par le juge
  if (model.path?.length) {
    model.path.forEach((text) => {
      const line = parseText(text);
      const atoms = normalize(lineAtoms(line));
      const v = judge(st, [{ atoms }], { verify: true });
      path.push({
        line,
        text: lineText(line),
        atoms,
        label: v.rule ? v.rule.label : '',
        card: v.rule ? v.rule.card || null : null,
        why: v.rule ? v.rule.why : v.message || '',
      });
      if (v.kind === 'step') commit(st, v);
    });
    return { path, reached: goalReached(st) };
  }
  // plafond : les questions en plusieurs temps (budget → côté → surface → gain) dépassent 16 lignes
  for (let i = 0; i < 40; i++) {
    const mv = nextMove(st, i === 0 ? prefer : null);
    if (!mv) break;
    // la ligne telle que l'élève l'écrirait : relue depuis ses atomes (−2 × x → −2x)
    let line = mv.line;
    try {
      line = parseLine(normalize(lineAtoms(mv.line)));
    } catch {
      /* ligne du solveur non relisible : on garde l'arbre */
    }
    mv.line = line;
    path.push({
      line: mv.line,
      text: lineText(mv.line),
      atoms: lineAtoms(mv.line),
      label: mv.label,
      card: mv.rc ? mv.rc.card || null : null,
      why: mv.rc ? mv.rc.why : mv.why || mv.text || '',
    });
    if (mv.kind === 'check') {
      const d = mv.line.dis[0]!;
      st.stated = { ...st.stated, checks: [...(st.stated.checks ?? []), cmpKey(d)] };
      st._cache = null;
      continue;
    }
    addFact(st, mv.line, 'line', { obligation: !!(mv.rc && mv.rc.obligation) });
    if (mv.kind === 'hyp') st.stated.hyp = true;
  }
  return { path, reached: goalReached(st) };
}
/** l'élève a-t-il cette ligne (ou une ligne équivalente) dans son brouillon ? */
export function hasLine(st: ReasonerState, line: Line): boolean {
  if (st.kern) return kernelHasLine(st.kern, line);
  if (st.kgeo) return kgeoEstablished(st.kgeo, lineText(line));
  if (st.krec) return krecEstablished(st.krec, lineText(line));
  if (st.kprog) return kprogEstablished(st.kprog, lineText(line));
  if (st.kmat) return kmatEstablished(st.kmat, lineText(line));
  if (st.karith) return karithEstablished(st.karith, lineText(line));
  if (st.klim) return klimEstablished(st.klim, lineText(line));
  if (st.klc) return klcEstablished(st.klc, lineText(line));
  if (st.kint) return kintEstablished(st.kint, lineText(line));
  if (st.kode) return kodeEstablished(st.kode, lineText(line));
  if (st.kpart) return kpartEstablished(st.kpart, lineText(line));
  if (st.kriem) return kriemEstablished(st.kriem, lineText(line));
  if (st.kclaims) return kclaimsEstablished(st.kclaims, lineText(line));
  if (st.kvar) return kvarEstablished(st.kvar, lineText(line));
  if (st.ktab) return ktabEstablished(st.ktab, lineText(line));
  if (st.kana) return kanaEstablished(st.kana, lineText(line));
  if (st.kfor) return kforEstablished(st.kfor, lineText(line));
  if (st.cong) return congEstablished(st.cong, line);
  if (st.cplx) return complexEstablished(st.cplx, line);
  if (st.units) return unitsEstablished(st.units, line);
  if (st.geo) return geoEstablished(st.geo, line);
  if (st.bnd) return boundsEstablished(st.bnd, line);
  const K = st.K;
  return st.facts.some((f) => {
    if (f.source !== 'line') return false;
    return !!(matchLine(f.line, line, K) || matchLine(line, f.line, K));
  });
}

/* ================= coup de pouce ================= */
export interface Hint {
  text: string;
  line: Line;
  lineText: string;
  /** les atomes qu'il reste à écrire si la ligne en cours est un bon début, sinon null */
  rest: string[] | null;
  kind: string;
  label: string;
}
export function hint(st: ReasonerState, chips: Chip[]): Hint | null {
  if (st.kgeo) return kgeoHint(st.kgeo, chips);
  if (st.krec) return krecHint(st.krec, chips);
  if (st.kprog) return kprogHint(st.kprog, chips);
  if (st.kmat) return kmatHint(st.kmat, chips);
  if (st.karith) return karithHint(st.karith, chips);
  if (st.klim) return klimHint(st.klim, chips);
  if (st.klc) return klcHint(st.klc, chips);
  if (st.kint) return kintHint(st.kint, chips);
  if (st.kode) return kodeHint(st.kode, chips);
  if (st.kpart) return kpartHint(st.kpart, chips);
  if (st.kriem) return kriemHint(st.kriem, chips);
  if (st.kclaims) return kclaimsHint(st.kclaims, chips);
  if (st.kvar) return kvarHint(st.kvar, chips);
  if (st.ktab) return ktabHint(st.ktab, chips);
  if (st.kana) return kanaHint(st.kana, chips);
  if (st.kfor) return kforHint(st.kfor, chips);
  const mv = st.kern
    ? kernelMove(st.kern)
    : st.cong || st.cplx || st.units || st.geo || st.bnd || st.model.path?.length
      ? congNextMove(st)
      : nextMove(st);
  if (!mv) return null;
  // moteur v42 : un tableau de signes se dresse dans son éditeur, pas bout par bout
  if (mv.table)
    return {
      text: mv.text || '',
      line: mv.line,
      lineText: mv.display ?? 'Tableau de signes',
      rest: null,
      kind: mv.kind,
      label: mv.label,
    };
  const atoms = normalize(lineAtoms(mv.line));
  const A = normalizeChips(chips).atoms;
  let rest: string[] | null = null;
  if (!A.length) rest = atoms;
  else {
    formVariants(atoms).forEach((v) => {
      if (rest) return;
      if (v.length > A.length && A.every((a, i) => v[i] === a)) rest = v.slice(A.length);
    });
  }
  const r = rest as string[] | null;
  let finalRest = r;
  if (
    r &&
    A.length &&
    r[0] === '×' &&
    (r[1] === '(' || isVar(r[1] || '')) &&
    isNum(A[A.length - 1])
  )
    finalRest = r.slice(1);
  return {
    text: mv.text || '',
    line: mv.line,
    lineText: mv.display ?? lineText(mv.line),
    rest: finalRest,
    kind: mv.kind,
    label: mv.label,
  };
}

/** récurrence (moteur v26) : la prochaine ligne du corrigé, bouts de la palette compris */
function krecHint(ks: KRecState, chips: Chip[]): Hint | null {
  const m = krecNextMove(ks);
  if (!m) return null;
  const A = normalizeChips(chips).atoms;
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la ligne suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** trace Python (moteur v28) : la prochaine ligne du corrigé qui fait avancer la trace */
function kprogHint(ks: KProgState, chips: Chip[]): Hint | null {
  const m = kprogNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris l’état suivant') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** feuille à formules (moteur v32) : la prochaine ligne du corrigé */
function kforHint(ks: KForState, chips: Chip[]): Hint | null {
  const m = kforNextMove(ks);
  if (!m) return null;
  const A = normalizeChips(chips).atoms;
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la ligne suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** dérivées et primitives (moteur v31) : la prochaine ligne du corrigé */
function kanaHint(ks: KAnaState, chips: Chip[]): Hint | null {
  const m = kanaNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la ligne suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** équations différentielles (moteur v62) : la ligne du corrigé qui suit la dernière écrite */
function kodeHint(ks: KOdeState, chips: Chip[]): Hint | null {
  const m = kodeNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris l’étape suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** dérivées partielles (moteur v64) : la ligne du corrigé qui suit la dernière écrite */
function kpartHint(ks: KPartState, chips: Chip[]): Hint | null {
  const m = kpartNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris l’étape suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** intégrales (moteur v55) : la ligne du corrigé qui suit la dernière écrite */
function kintHint(ks: KIntState, chips: Chip[]): Hint | null {
  const m = kintNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris l’étape suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** sommes de Riemann (moteur v59) : la ligne du corrigé qui suit la dernière écrite */
function kriemHint(ks: KRiemState, chips: Chip[]): Hint | null {
  const m = kriemNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris l’étape suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** limites affirmées (moteur v57) : la prochaine limite à établir */
function klcHint(ks: KLcState, chips: Chip[]): Hint | null {
  const m = klcNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la limite suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** limites (moteur v54) : la prochaine ligne du corrigé */
function klimHint(ks: KLimState, chips: Chip[]): Hint | null {
  const m = klimNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la limite suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

function karithHint(ks: KArithState, chips: Chip[]): Hint | null {
  const m = karithNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris le calcul suivant') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** étude de fonction (lot 6.20) : dériver (ligne suivante du corrigé), puis le tableau */
function kvarHint(ks: KVarState, chips: Chip[]): Hint | null {
  const m = kvarNextMove(ks);
  if (!m) return null;
  if (m.table)
    return {
      text: `Prochain pas : dresse le tableau de variations de ${ks.f} (bouton « Tableau » de la palette).`,
      line: m.line,
      lineText: 'Tableau de variations',
      rest: null,
      kind: 'kernel',
      label: m.label,
    };
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la ligne suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** tableau à remplir (lot 6.21) : le tableau, puis la grandeur suivante à lire */
function ktabHint(ks: KTabState, chips: Chip[]): Hint | null {
  const m = ktabNextMove(ks);
  if (!m) return null;
  if (m.table)
    return {
      text: 'Prochain pas : remplis le tableau (bouton « Tableau » de la palette).',
      line: m.line,
      lineText: m.label,
      rest: null,
      kind: 'kernel',
      label: m.label,
    };
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : lis le tableau.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** affirmations à trancher (moteur v36) : la prochaine ligne du corrigé */
function kclaimsHint(ks: KClaimsState, chips: Chip[]): Hint | null {
  const m = kclaimsNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris le calcul suivant') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** calcul matriciel (moteur v30) : la prochaine ligne du corrigé */
function kmatHint(ks: KMatState, chips: Chip[]): Hint | null {
  const m = kmatNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris le calcul suivant') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** géométrie (moteur v25) : la prochaine ligne du corrigé, bouts de la palette compris */
function kgeoHint(ks: KGeoState, chips: Chip[]): Hint | null {
  const m = kgeoNextMove(ks);
  if (!m) return null;
  const A = chips.flatMap((c) => c.atoms);
  const rest = !A.length
    ? m.atoms
    : m.atoms.length > A.length && A.every((a, i) => m.atoms[i] === a)
      ? m.atoms.slice(A.length)
      : null;
  return {
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'écris la propriété suivante') + '.',
    line: m.line,
    lineText: m.text,
    rest,
    kind: 'kernel',
    label: m.label,
  };
}

/** noyau (moteur v18) : la ligne suivante du corrigé, sinon le 1er pas de la recherche */
function kernelMove(ks: KernelSheetState): Move | null {
  const m = kernelNextMove(ks);
  if (!m) return null;
  if (m.table)
    return {
      line: m.line,
      kind: 'kernel',
      label: m.label,
      table: true,
      display: 'Tableau de signes',
      text: 'Prochain pas : dresse le tableau de signes de ta ligne (bouton « Tableau de signes » de la palette).',
    };
  return {
    line: m.line,
    kind: 'kernel',
    label: m.label,
    display: m.text,
    text: 'Prochain pas : ' + (m.label ? lower(m.label) : 'transforme ta dernière ligne') + '.',
  };
}

/** congruences : la prochaine ligne du corrigé pas encore établie */
function congNextMove(st: ReasonerState): Move | null {
  if (goalReached(st)) return null;
  const path = solve(st.model).path;
  for (const p of path) {
    if (hasLine(st, p.line)) continue;
    return {
      line: p.line,
      kind: 'cong',
      label: p.label,
      text: 'Prochain pas : ' + (p.label || 'calcule, puis réduis') + '.',
    };
  }
  return null;
}

/* ================= pour le panneau « moteur » ================= */
export function snapshot(st: ReasonerState): {
  facts: number;
  hyps: number;
  known: string[];
  oneStep: number;
  tools: string[];
} {
  if (st.kfor)
    return {
      facts: st.kfor.lines.length,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kana)
    return {
      facts: st.kana.lines.length,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kint)
    return {
      facts: st.kint.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kpart)
    return {
      facts: st.kpart.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kode)
    return {
      facts: st.kode.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kriem)
    return {
      facts: st.kriem.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.klim)
    return {
      facts: st.klim.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.klc)
    return {
      facts: st.klc.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.karith)
    return {
      facts: st.karith.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kvar)
    return {
      facts: st.kvar.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.ktab)
    return {
      facts: st.ktab.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kclaims)
    return {
      facts: st.kclaims.lines,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kmat)
    return {
      facts: st.kmat.lines.length,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kprog)
    return {
      facts: st.kprog.lines.length,
      hyps: 0,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.krec)
    return {
      facts: st.krec.lines.length,
      hyps: st.krec.facts.length,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kgeo)
    return {
      facts: st.kgeo.lines.length,
      hyps: st.kgeo.givens.length,
      known: [],
      oneStep: 0,
      tools: [],
    };
  if (st.kern)
    return {
      facts: st.kern.lines.length,
      hyps: st.kern.hyps.length,
      known: [],
      oneStep: 0,
      tools: [],
    };
  const known = Object.keys(st.K)
    .filter((n) => st.model.symbols[n] && !st.model.symbols[n]!.given)
    .map((n) => n + ' = ' + formatNumber(st.K[n]!));
  return {
    facts: st.facts.filter((f) => f.source === 'line').length,
    hyps: st.facts.filter((f) => f.source !== 'line').length,
    known,
    oneStep: candidates(st, false).length,
    tools: ruleConclusions(st)
      .map((r) => r.card!)
      .filter((c, i, a) => a.indexOf(c) === i),
  };
}

/** La ligne (texte) est-elle une des réponses attendues ? */
export function isAnswer(st: ReasonerState, text: string): boolean {
  if (st.kern) return kernelGoalReached(st.kern) && kernelEquivalentToAnswer(st.kern, text, true);
  if (st.kgeo) return kgeoIsAnswer(st.kgeo, text);
  if (st.krec) return krecIsAnswer(st.krec, text);
  if (st.kprog) return kprogIsAnswer(st.kprog, text);
  if (st.kmat) return kmatGoalReached(st.kmat) && kmatIsAnswer(st.kmat, text);
  if (st.karith) return karithGoalReached(st.karith) && karithIsAnswer(st.karith, text);
  if (st.klim) return klimGoalReached(st.klim) && klimIsAnswer(st.klim, text);
  if (st.kint) return kintGoalReached(st.kint) && kintIsAnswer(st.kint, text);
  if (st.kode)
    return (
      kodeGoalReached(st.kode) && kodeIsAnswer(st.kode, text) && kodeAnswerWritten(st.kode, text)
    );
  if (st.kriem) return kriemGoalReached(st.kriem) && kriemIsAnswer(st.kriem, text);
  if (st.kpart) return kpartGoalReached(st.kpart) && kpartIsAnswer(st.kpart, text);
  // affirmations (moteur v36) : les choix se lisent dans `claimDecisions`
  if (st.kclaims || st.kvar || st.ktab || st.klc) return false;
  if (st.kana) return kanaGoalReached(st.kana) && kanaIsAnswer(st.kana, text);
  if (st.kfor) return kforIsAnswer(st.kfor, text);
  if (st.cong) return congIsTrue(st.cong, parseText(text));
  if (st.cplx) return complexIsTrue(st.cplx, parseText(text));
  if (st.units) return unitsIsAnswer(st.units, text);
  if (st.geo) return geoIsGoal(st.geo, text) && geoGoalReached(st.geo);
  if (st.bnd) return boundsIsGoal(st.bnd, text) && boundsGoalReached(st.bnd);
  return isAnswerLine(st, parseText(text));
}
/**
 * QCM à plusieurs bonnes réponses (lot 6.7) : le choix « x = v » est-il UNE des solutions
 * établies par la feuille (réponse atteinte) ? Feuilles du noyau en algèbre seulement.
 */
export function answerContains(st: ReasonerState, text: string): boolean {
  if (!st.kern) return false;
  return kernelGoalReached(st.kern) && kernelAnswerContains(st.kern, text);
}

/**
 * Feuille d'affirmations (moteur v36) : la décision de chaque affirmation, dans l'ordre des
 * choix du QCM (true : vraie, false : fausse, null : pas encore tranchée) ; null hors de ces
 * feuilles.
 */
export function claimDecisions(st: ReasonerState): (boolean | null)[] | null {
  if (st.kvar) return kvarDecisions(st.kvar);
  if (st.ktab) return ktabDecisions(st.ktab);
  if (st.klc) return klcDecisions(st.klc);
  return st.kclaims ? kclaimsDecisions(st.kclaims) : null;
}
/** Résultat d'une question précédente, admis au début de la suivante (« on admet que BC = 10 »). */
export function admit(st: ReasonerState, text: string): void {
  addFact(st, parseText(text), 'given', { admitted: true });
}
/** Vérifie qu'une identité de la forme « a = b » tient (sert à la porte de validation). */
export function factorOk(from: Node, to: Node, K: Env): boolean {
  return factorOK(from, to, K);
}
export { addM };
